import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { 
    ChevronRight, 
    ChevronLeft, 
    SkipForward, 
    Mic, 
    Square, 
    Upload, 
    Trash2, 
    RefreshCw, 
    Play, 
    Pause, 
    Check,
    Download,
    EyeOff,
    CheckCircle,
    Sun,
    Moon,
    AlertCircle,
    Instagram,
    X
} from 'react-feather';
import api from './api';
import { getCurrencySymbol } from './utils/currency';
import { isRawVideoTooLong, readVideoFileDurationSec, RAW_VIDEO_TOO_LONG_MESSAGE } from './utils/rawVideoLimits';
import { trackEvent } from './utils/amplitude';
import { autoplayVoiceoverUrl } from './utils/autoplayVoiceover';
import { celebrateVideoDownload } from './utils/downloadCelebration';
import TextareaAutosize from 'react-textarea-autosize';
import { useDarkMode } from './DarkModeContext';
import VideoGenerationSteps from './components/VideoGenerationSteps';

const VOICE_CLONE_LIMIT_MESSAGE = "You've reached the limit of voice-cloning for free accounts. Please upgrade your subscription.";

function isVoiceCloneLimitError(error) {
    const data = error?.response?.data;
    if (data?.code === 'voice_clone_limit_reached') return true;
    const detail = data?.details?.detail || data?.detail;
    return detail?.status === 'voice_add_edit_limit_reached';
}

// Custom Sparkles icon component old
//const Sparkles = ({ size = 16, ...props }) => (
//    <svg
//        width={size}
//        height={size}
//        viewBox="0 0 24 24"
//        fill="none"
//        stroke="currentColor"
//        strokeWidth="1.5"
//        strokeLinecap="round"
//        strokeLinejoin="round"
//        {...props}
//    >*/
        {/* Main star */}
//        <path d="M12 2l2.5 7.5L22 12l-7.5 2.5L12 22l-2.5-7.5L2 12l7.5-2.5L12 2z"/>
        {/* Small sparkles around */}
//        <path d="M6 6l1.5 1.5"/>
//        <path d="M18 6l-1.5 1.5"/>
//        <path d="M6 18l1.5-1.5"/>
//        <path d="M18 18l-1.5-1.5"/>
//        {/* Additional sparkle dots */}
//        <circle cx="4" cy="8" r="0.5" fill="currentColor"/>
//        <circle cx="20" cy="8" r="0.5" fill="currentColor"/>
//        <circle cx="4" cy="16" r="0.5" fill="currentColor"/>
//        <circle cx="20" cy="16" r="0.5" fill="currentColor"/>
//        <circle cx="8" cy="4" r="0.5" fill="currentColor"/>
//        <circle cx="16" cy="4" r="0.5" fill="currentColor"/>
//        <circle cx="8" cy="20" r="0.5" fill="currentColor"/>
//        <circle cx="16" cy="20" r="0.5" fill="currentColor"/>
//    </svg>
//);*/

const Sparkles = ({ size = 16, ...props }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 56 56"
        fill="currentColor"
        {...props}
    >
        <path d="M 26.6875 12.6602 C 26.9687 12.6602 27.1094 12.4961 27.1797 12.2383 C 27.9062 8.3242 27.8594 8.2305 31.9375 7.4570 C 32.2187 7.4102 32.3828 7.2461 32.3828 6.9648 C 32.3828 6.6836 32.2187 6.5195 31.9375 6.4726 C 27.8828 5.6524 28.0000 5.5586 27.1797 1.6914 C 27.1094 1.4336 26.9687 1.2695 26.6875 1.2695 C 26.4062 1.2695 26.2656 1.4336 26.1953 1.6914 C 25.3750 5.5586 25.5156 5.6524 21.4375 6.4726 C 21.1797 6.5195 20.9922 6.6836 20.9922 6.9648 C 20.9922 7.2461 21.1797 7.4102 21.4375 7.4570 C 25.5156 8.2774 25.4687 8.3242 26.1953 12.2383 C 26.2656 12.4961 26.4062 12.6602 26.6875 12.6602 Z M 15.3438 28.7852 C 15.7891 28.7852 16.0938 28.5039 16.1406 28.0821 C 16.9844 21.8242 17.1953 21.8242 23.6641 20.5821 C 24.0860 20.5117 24.3906 20.2305 24.3906 19.7852 C 24.3906 19.3633 24.0860 19.0586 23.6641 18.9883 C 17.1953 18.0977 16.9609 17.8867 16.1406 11.5117 C 16.0938 11.0899 15.7891 10.7852 15.3438 10.7852 C 14.9219 10.7852 14.6172 11.0899 14.5703 11.5352 C 13.7969 17.8164 13.4687 17.7930 7.0469 18.9883 C 6.6250 19.0821 6.3203 19.3633 6.3203 19.7852 C 6.3203 20.2539 6.6250 20.5117 7.1406 20.5821 C 13.5156 21.6133 13.7969 21.7774 14.5703 28.0352 C 14.6172 28.5039 14.9219 28.7852 15.3438 28.7852 Z M 31.2344 54.7305 C 31.8438 54.7305 32.2891 54.2852 32.4062 53.6524 C 34.0703 40.8086 35.8750 38.8633 48.5781 37.4570 C 49.2344 37.3867 49.6797 36.8945 49.6797 36.2852 C 49.6797 35.6758 49.2344 35.2070 48.5781 35.1133 C 35.8750 33.7070 34.0703 31.7617 32.4062 18.9180 C 32.2891 18.2852 31.8438 17.8633 31.2344 17.8633 C 30.6250 17.8633 30.1797 18.2852 30.0860 18.9180 C 28.4219 31.7617 26.5938 33.7070 13.9140 35.1133 C 13.2344 35.2070 12.7891 35.6758 12.7891 36.2852 C 12.7891 36.8945 13.2344 37.3867 13.9140 37.4570 C 26.5703 39.1211 28.3281 40.8321 30.0860 53.6524 C 30.1797 54.2852 30.6250 54.7305 31.2344 54.7305 Z"/>
    </svg>
);

// Default voices for onboarding
const DEFAULT_VOICES = [
    {
        id: 'nPczCjzI2devNBz1zQrb',
        name: 'Brian',
    },
    // { 
    //     id: '6aDn1KB0hjpdcocrUkmq', 
    //     name: 'Tiffany Natural',
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_tiffany+-+realistic+conversations.mp3'
    // },
    // { 
    //     id: 'nXIYu9FT5meibkBbZFT7', 
    //     name: 'James Sales Pro',
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_james+-+sales+professional+.mp3'
    // },
    // { 
    //     id: '6OzrBCQf8cjERkYgzSg8', 
    //     name: 'Young Jamal',
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_young+jamal.mp3'
    // },
    // { 
    //     id: 'BZgkqPqms7Kj9ulSkVzn', 
    //     name: "Eve Energy",
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_eve.mp3'
    // },
    // { 
    //     id: 'UkO7OCLgMp3WYf4UPjE5', 
    //     name: 'Tiago Reassuring (PT)',
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_tiago+-+serious+and+reassuring.mp3'
    // },
];

// SmoothScaleCarousel component
const SmoothScaleCarousel = ({ items = [] }) => {
  const N = items.length;
  
  // Detect mobile device
  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 768px)').matches;
  });

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleChange = (e) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  
  // Create infinite loop by repeating items multiple times
  // Reduce copies on mobile for better performance
  const copies = isMobile ? 2 : 5;
  const extendedItems = React.useMemo(() => {
    const result = [];
    for (let i = 0; i < copies; i++) {
      result.push(...items);
    }
    return result;
  }, [items, copies]);
  
  // Start index in middle - adjust based on number of copies
  const initialIndex = React.useMemo(() => {
    return isMobile ? N : N * 2; // For 2 copies start at N, for 5 copies start at N*2
  }, [N, isMobile]);
  
  const [index, setIndex] = React.useState(initialIndex);
  const [resting, setResting] = React.useState(true);
  const dwell = 1200;     // pause when centered
  const duration = 900;   // movement duration

  // Adjust index when mobile state changes
  React.useEffect(() => {
    setIndex(initialIndex);
  }, [initialIndex]);

  React.useEffect(() => {
    if (!resting) return;
    const id = setTimeout(() => {
      setResting(false);
      setIndex((i) => {
        return i + 1;
      });
    }, dwell);
    return () => clearTimeout(id);
  }, [resting]);

  const onTransitionEnd = () => {
    setResting(true);
    // When we reach the end, snap back appropriately
    if (isMobile) {
      // For 2 copies on mobile, reset when reaching end of 2nd copy
      if (index >= N * 2) {
        setIndex(N); // Reset to start of second copy
      }
    } else {
      // When we reach the end of the 4th copy, snap back to start of 2nd copy
      if (index >= N * 4) {
        setIndex(N * 2); // Reset to start of middle copy
      }
    }
  };

  const VISIBLE = 3;
  const STEP = 100 / VISIBLE;
  const translate = STEP - index * STEP;
  const trackWidth = (extendedItems.length / VISIBLE) * 100;

  // Early return after all hooks
  if (N === 0) return null;

  return (
    <div
      style={{
        overflow: "hidden",
        maxWidth: 800,
        margin: "0 auto",
        position: "relative",
        height: 440,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        top: -100,
      }}
    >
      <style>{`
        @keyframes carousel-green-scan {
          0% { top: -48px; }
          28.57% { top: 100%; }
          57.14% { top: -48px; }
          85.71% { top: 100%; }
          100% { top: 100%; }
        }
        .carousel-scan-overlay {
          position: absolute;
          inset: 0;
          border-radius: inherit;
          overflow: hidden;
          pointer-events: none;
        }
        .carousel-scan-band {
          position: absolute;
          left: 0;
          right: 0;
          height: 48px;
          background: linear-gradient(
            to bottom,
            rgba(0, 255, 136, 0),
            rgba(0, 255, 160, 0.35),
            rgba(100, 255, 180, 0.25),
            rgba(0, 255, 136, 0)
          );
          animation: carousel-green-scan 3.5s ease-in-out infinite;
        }
      `}</style>
      <div
        onTransitionEnd={onTransitionEnd}
        style={{
          display: "flex",
          transition: `transform ${duration}ms cubic-bezier(0.4,0,0.2,1)`,
          transform: `translateX(${translate}%)`,
          width: `${trackWidth}%`,
          alignItems: "center",
        }}
      >
        {extendedItems.map((cut, i) => {
          const isCenter = i === index;
          const scale = isCenter ? 1.15 : 0.85;
          const opacity = isCenter ? 1 : 0.7;
          return (
            <div
              key={`${i}-${cut._id}`}
              style={{
                flex: `0 0 ${STEP}%`,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                transition: "transform 600ms ease, opacity 600ms ease",
                transform: `scale(${scale})`,
                opacity,
              }}
            >
              <div
                style={{
                  position: 'relative',
                  width: '55%',
                  aspectRatio: '9 / 16',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: isCenter
                    ? "0 20px 40px rgba(0,0,0,0.35)"
                    : "0 10px 20px rgba(0,0,0,0.2)",
                }}
              >
                <video
                  src={cut.url}
                  playsInline
                  muted
                  autoPlay={!isMobile}
                  loop={!isMobile}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                  onLoadedMetadata={(e) => {
                    try {
                      e.currentTarget.currentTime = e.currentTarget.duration / 2;
                    } catch {}
                  }}
                />
                <div className="carousel-scan-overlay" aria-hidden="true">
                  <div className="carousel-scan-band" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const OnboardingFlow = ({
    step,
    userName,
    onboardingProfile,
    setOnboardingProfile,
    onboardingRecordings,
    setOnboardingRecordings,
    isRecordingOnboarding,
    setIsRecordingOnboarding,
    recordingDurationOnboarding,
    setRecordingDurationOnboarding,
    recordingIntervalOnboarding,
    setRecordingIntervalOnboarding,
    isSavingRecordingOnboarding,
    setIsSavingRecordingOnboarding,
    onboardingVoiceId,
    setOnboardingVoiceId,
    isGeneratingVoiceOnboarding,
    setIsGeneratingVoiceOnboarding,
    onboardingVideos,
    setOnboardingVideos,
    onboardingCuts,
    setOnboardingCuts,
    onboardingUploadProgress,
    setOnboardingUploadProgress,
    onboardingScript,
    setOnboardingScript,
    onboardingVoiceoverUrl,
    setOnboardingVoiceoverUrl,
    voiceoverDuration,
    setVoiceoverDuration,
    onboardingVideoUrl,
    setOnboardingVideoUrl,
    isGeneratingScriptOnboarding,
    setIsGeneratingScriptOnboarding,
    isGeneratingVoiceoverOnboarding,
    setIsGeneratingVoiceoverOnboarding,
    isGeneratingVideoOnboarding,
    setIsGeneratingVideoOnboarding,
    isDownloadingVideoOnboarding,
    setIsDownloadingVideoOnboarding,
    isSavingVideoOnboarding,
    setIsSavingVideoOnboarding,
    onNext,
    onPrevious,
    onSkip,
    onComplete,
    onAISuggestion,
    onProfileSave,
    currentWorkspace,
    aiSuggestionLoading,
    triggerCompletionGlow,
    playCompletionSound
}) => {
    // Use shared dark mode context
    const { darkMode, toggleDarkMode } = useDarkMode();
    const currency = getCurrencySymbol();
    const [showVoiceClonePaywall, setShowVoiceClonePaywall] = useState(false);
    
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [showExamples, setShowExamples] = useState(false);
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
    const [currentExample, setCurrentExample] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [audioImportRef] = useState(useRef(null));
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [mediaStream, setMediaStream] = useState(null);
    const [audioRef] = useState(useRef(null));
    const voiceoverAudioRef = useRef(null);
    const requestVoiceoverAutoplayRef = useRef(false);
    const scriptTextareaRef = useRef(null);
    const [isPlayingVoice, setIsPlayingVoice] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const confettiCanvasRef = useRef(null);
    const [playingVoiceId, setPlayingVoiceId] = useState(null);
    const [mousePositions, setMousePositions] = useState([
        { x: 20, y: 20, targetX: 20, targetY: 20, color: '#0066cc', label: 'hook designer' },
        { x: 80, y: 60, targetX: 80, targetY: 60, color: '#ffc107', label: 'researcher' },
        { x: 140, y: 100, targetX: 140, targetY: 100, color: '#dc3545', label: 'strategist' },
        { x: 200, y: 40, targetX: 200, targetY: 40, color: '#28a745', label: 'content writer' }
    ]);
    const [scriptGenerationDuration, setScriptGenerationDuration] = useState(null);
    const [currentAudio, setCurrentAudio] = useState(null);
    const [generatingVoiceId, setGeneratingVoiceId] = useState(null);
    const [scriptModified, setScriptModified] = useState(false); // Track if script content has changed (for saving)
    const [isEditingScript, setIsEditingScript] = useState(false); // Track if we're in edit mode (showing textarea)
    const [currentVoiceoverVoiceId, setCurrentVoiceoverVoiceId] = useState(null);
    const [currentVoiceoverVoiceType, setCurrentVoiceoverVoiceType] = useState(null); // 'default' or 'cloned'
    const [voiceoverRegenerationCount, setVoiceoverRegenerationCount] = useState(0); // Track number of regenerations
    const [uploadProgress, setUploadProgress] = useState({});
    const [hoveredUploadFile, setHoveredUploadFile] = useState(null);
    const [uploadStats, setUploadStats] = useState({
        totalFiles: 0,
        currentFileIndex: 0,
        completedFiles: 0,
        failedFiles: 0,
        currentFileName: ''
    });
    const [onboardingVideoProgress, setOnboardingVideoProgress] = useState(0);
    const [showInsufficientCutsModal, setShowInsufficientCutsModal] = useState(false);
    const [cancelledUploads, setCancelledUploads] = useState(new Set());
    const [activeUploads, setActiveUploads] = useState(new Map()); // Track active XMLHttpRequests
    const [testAudioUrl, setTestAudioUrl] = useState('');
    const [isTestingVoice, setIsTestingVoice] = useState(false);
    const onboardingFileInputRef = useRef(null);
    const [showMobilePickerNotice, setShowMobilePickerNotice] = useState(false);
    const [importingInstagram, setImportingInstagram] = useState(false);
    const [instagramImportProgress, setInstagramImportProgress] = useState({ progress: 0, imported: 0, total: 0 });
    const [instagramImportSuccess, setInstagramImportSuccess] = useState(null);
    const [instagramHandle, setInstagramHandle] = useState('');
    const [onboardingErrors, setOnboardingErrors] = useState([]);
    const [mobilePickerNoticeDismissed, setMobilePickerNoticeDismissed] = useState(() => {
        try {
            return localStorage.getItem('onboarding_mobile_picker_notice_dismissed') === '1';
        } catch {
            return false;
        }
    });

    const openOnboardingFilePicker = (e) => {
        if (e?.preventDefault) e.preventDefault();
        if (screenWidth <= 768 && !mobilePickerNoticeDismissed) {
            setShowMobilePickerNotice(true);
            return;
        }
        onboardingFileInputRef.current?.click();
    };
    const [voiceCloningError, setVoiceCloningError] = useState(null);
    const [playingRecordingId, setPlayingRecordingId] = useState(null);
    const [isImportingAudio, setIsImportingAudio] = useState(false);
    const recordingAudioRefs = useRef({});
    const currentGenerationIdRef = useRef(0); // Track the latest generation request
    const [recordingLength, setRecordingLength] = useState({});
    const [selectedVoiceId, setSelectedVoiceId] = useState('');
    const [useClonedVoice, setUseClonedVoice] = useState(false);
    const [selectedDefaultVoice, setSelectedDefaultVoice] = useState('');
    const [selectedMusicId, setSelectedMusicId] = useState('');
    const [selectedFontId, setSelectedFontId] = useState('');
    const [studioHook, setStudioHook] = useState('');
    const [voiceGenerationMode, setVoiceGenerationMode] = useState(() => {
        const saved = localStorage.getItem('voiceover_voice_generation_mode');
        return saved || 'stable'; // default to stable
    });
    const [showVoiceGenerationModeInfo, setShowVoiceGenerationModeInfo] = useState(false);
    const [showVoiceDropdown, setShowVoiceDropdown] = useState(false);
    const [availableHooks, setAvailableHooks] = useState([]);
    const [isGeneratingScript, setIsGeneratingScript] = useState(false);
    const [isGeneratingVoiceover, setIsGeneratingVoiceover] = useState(false);
    const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
    const [scriptId, setScriptId] = useState('');
    const [voiceoverUrl, setVoiceoverUrl] = useState('');
    const [videoUrl, setVideoUrl] = useState('');
    const [progress, setProgress] = useState(0);
    const [progressMessage, setProgressMessage] = useState('');
    const [translationLanguage, setTranslationLanguage] = useState('es');
    const [showTranslationLanguageDropdown, setShowTranslationLanguageDropdown] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);
    const [isPolling, setIsPolling] = useState(false);
    const [isPollingCuts, setIsPollingCuts] = useState(false); // Track if we're actively polling for cuts
    const [lastCutsCount, setLastCutsCount] = useState(0); // Track cuts count to detect new cuts
    const [cutsGenerationActive, setCutsGenerationActive] = useState(false); // Track if cuts are actively being generated
    const [referralSource, setReferralSource] = useState('');
    
    const referralOptions = [
        'TikTok',
        'Instagram',
        'X (Twitter)',
        'Reddit',
        'Linkedin',
        'Youtube',
        'Discord',
        'Google',
        'Product Hunt',
        'AI',
        'Friend or Colleague',
        'Other'
    ];
    
    const languageOptions = [
        { code: 'en', name: 'English' },
        { code: 'es', name: 'Spanish' },
        { code: 'fr', name: 'French' },
        { code: 'de', name: 'German' },
        { code: 'it', name: 'Italian' },
        { code: 'pt', name: 'Portuguese' },
        { code: 'ru', name: 'Russian' },
        { code: 'ja', name: 'Japanese' },
        { code: 'ko', name: 'Korean' },
        { code: 'zh', name: 'Chinese' },
        { code: 'ar', name: 'Arabic' },
        { code: 'hi', name: 'Hindi' },
        { code: 'nl', name: 'Dutch' },
        { code: 'sv', name: 'Swedish' },
        { code: 'no', name: 'Norwegian' },
        { code: 'da', name: 'Danish' },
        { code: 'fi', name: 'Finnish' },
        { code: 'pl', name: 'Polish' },
        { code: 'tr', name: 'Turkish' }
    ];
    const [pollingInterval, setPollingInterval] = useState(null);
    const [showCuts, setShowCuts] = useState(false);
    const [hiddenCuts, setHiddenCuts] = useState(new Set());

    // Handle ESC key and body scroll when video modal is open
    useEffect(() => {
        if (isVideoModalOpen) {
            // Prevent body scroll
            document.body.style.overflow = 'hidden';
            
            // Handle ESC key
            const handleEsc = (e) => {
                if (e.key === 'Escape') {
                    setIsVideoModalOpen(false);
                }
            };
            
            window.addEventListener('keydown', handleEsc);
            
            return () => {
                document.body.style.overflow = '';
                window.removeEventListener('keydown', handleEsc);
            };
        }
    }, [isVideoModalOpen]);

    // Recording examples
    const RECORDING_EXAMPLES = [
        "Hi, I noticed your impressive work at [Company] and wanted to reach out.",
        "We help companies like yours improve productivity by 40%.",
        "The marketing landscape is rapidly changing, especially regarding automation.",
        "Many teams face challenges with content creation and distribution.",
        "Our solution simplifies complex processes through intelligent automation."
    ];

    // Default voices
    // const DEFAULT_VOICES = [
    //     { id: 'voice1', name: 'Professional Male', previewUrl: '' },
    //     { id: 'voice2', name: 'Friendly Female', previewUrl: '' },
    //     { id: 'voice3', name: 'Authoritative Male', previewUrl: '' }
    // ];

    // Default music
    // const DEFAULT_MUSIC = [
    //     { id: 'music1', name: 'Upbeat Corporate', url: '' },
    //     { id: 'music2', name: 'Modern Tech', url: '' },
    //     { id: 'music3', name: 'Professional Background', url: '' }
    // ];

    // Default fonts
    // const DEFAULT_FONTS = [
    //     { id: 'font1', name: 'Modern Sans', url: '' },
    //     { id: 'font2', name: 'Clean Serif', url: '' },
    //     { id: 'font3', name: 'Bold Display', url: '' }
    // ];

    useEffect(() => {
        const handleResize = () => setScreenWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Auto-focus textarea when entering edit mode
    useEffect(() => {
        if (isEditingScript && scriptTextareaRef.current) {
            // Use setTimeout to ensure the textarea is rendered before focusing
            setTimeout(() => {
                scriptTextareaRef.current?.focus();
                // Move cursor to end of text
                if (scriptTextareaRef.current) {
                    const length = scriptTextareaRef.current.value.length;
                    scriptTextareaRef.current.setSelectionRange(length, length);
                }
            }, 0);
        }
    }, [isEditingScript]);

    // Close voice dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showVoiceDropdown && !event.target.closest('[data-voice-dropdown]')) {
                setShowVoiceDropdown(false);
            }
        };
        if (showVoiceDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showVoiceDropdown]);

    useEffect(() => {
        if (!showVoiceDropdown && currentAudio) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
            currentAudio.onended = null;
            currentAudio.onerror = null;
            currentAudio.src = '';
            currentAudio.load();
            setCurrentAudio(null);
            setPlayingVoiceId(null);
        }
    }, [showVoiceDropdown, currentAudio]);

    useEffect(() => {
        if (showExamples) {
            const interval = setInterval(() => {
                setCurrentExample(prev => (prev + 1) % RECORDING_EXAMPLES.length);
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [showExamples]);

    // Cleanup microphone on unmount
    useEffect(() => {
        return () => {
            if (mediaStream) {
                mediaStream.getTracks().forEach(track => track.stop());
            }
        };
    }, [mediaStream]);

    const generateScript = useCallback(async () => {
        console.log('generateScript called with profile:', onboardingProfile);
        if (!onboardingProfile.companyPurpose) {
            console.log('No company purpose, returning early');
            return;
        }
        
        // Prevent multiple simultaneous generations
        if (isGeneratingScriptOnboarding) {
            console.log('Already generating script, skipping');
            return;
        }
        
        console.log('Setting isGeneratingScriptOnboarding to true');
        const generationStartTime = Date.now();
        setIsGeneratingScriptOnboarding(true);
        setProgress(0);
        setProgressMessage('Starting script generation...');
        
        // Start simulated progress with lock to prevent regression
        const lockDuration = 10000; // 10 seconds
        const progressLockUntil = Date.now() + lockDuration;
        const simulatedTimersRef = { current: [] };
        
        const simulatedSteps = [
            { percent: 10, message: 'Coming up with a big idea...', delay: 1000 },
            { percent: 20, message: 'Analyzing your business...', delay: 5000 },
            { percent: 25, message: 'Finding a hook...', delay: 7500 },
            { percent: 40, message: 'Writing your script...', delay: 9000 },
            { percent: 70, message: 'Refining the content...', delay: 12000 },
            { percent: 90, message: 'Finalizing your script...', delay: 15000 },
        ];
        
        const simulatedTimers = simulatedSteps.map((step) => {
            return setTimeout(() => {
                setProgress(prev => {
                    // Ensure monotonic progress
                    if (step.percent > prev) {
                        return step.percent;
                    }
                    return prev;
                });
                setProgressMessage(step.message);
            }, step.delay);
        });
        simulatedTimersRef.current = simulatedTimers;
        
        try {
            console.log('Calling API to generate script...');
            
            // Randomly select one of the predefined hook templates
            const hookTemplates = [
                "Something most people don't realize about [TOPIC] is [HIDDEN_TRUTH].",
                "What if the key to [TARGET_GOAL] is actually [UNEXPECTED_SOLUTION]?",
                "Want to know the biggest shortcut to [TARGET_GOAL] that nobody talks about?",
                "The truth about [TARGET_GOAL]: myth vs reality."
            ];
            
            const selectedHook = hookTemplates[Math.floor(Math.random() * hookTemplates.length)];
            
            // Use workspace script generation with randomly selected hook
            // For onboarding, generate a 15-second script
            const response = await api.generateWorkspaceScriptsAsync(currentWorkspace._id, {
                hooks: [selectedHook],
                includePublishedContext: false,
                creativityLevel: 3,
                duration: 20 // 20 seconds for onboarding
            });
            console.log('Workspace script generation response:', response.data);
            
            // Poll for script completion
            const pollForScript = async () => {
                try {
                    const statusResponse = await api.getWorkspaceScriptsStatus(currentWorkspace._id);
                    console.log('Script status:', statusResponse.data);
                    
                    // Update progress only if lock has expired and it's monotonic
                    if (statusResponse.data.progress !== undefined && Date.now() >= progressLockUntil) {
                        setProgress(prev => {
                            const backendPercent = statusResponse.data.progress;
                            // Only update if backend progress is greater than current
                            if (backendPercent > prev) {
                                return backendPercent;
                            }
                            return prev;
                        });
                        // setProgressMessage(statusResponse.data.message || 'Generating script...');
                    }
                    
                    if (statusResponse.data.scripts && statusResponse.data.scripts.length > 0) {
                        // Clear simulated timers
                        simulatedTimersRef.current.forEach(timer => clearTimeout(timer));
                        
                        const script = statusResponse.data.scripts[0]; // Get the first script
                        console.log('Script generated:', script);
                        const generationDuration = Math.round((Date.now() - generationStartTime) / 1000);
                        setScriptGenerationDuration(generationDuration);
                        setOnboardingScript(script);
                        setScriptModified(false); // Fresh script, not modified yet
                        setIsEditingScript(false); // Show highlighted version
                        trackEvent('onboarding_script_generated');
                        setIsGeneratingScriptOnboarding(false);
                        setProgress(100);
                        setProgressMessage('Script generated successfully!');
                        if (typeof playCompletionSound === 'function') playCompletionSound('script_merra');
                        if (typeof triggerCompletionGlow === 'function') triggerCompletionGlow();
                    } else if (statusResponse.data.status === 'processing' || statusResponse.data.status === 'generating') {
                        // Still processing, poll again
                        setTimeout(pollForScript, 2000);
                        return;
                    } else {
                        // Clear simulated timers on error
                        simulatedTimersRef.current.forEach(timer => clearTimeout(timer));
                        
                        // Error or no scripts
                        console.log('Script generation failed or no scripts found');
                        setIsGeneratingScriptOnboarding(false);
                        setProgress(0);
                        setProgressMessage('Script generation failed');
                    }
                } catch (error) {
                    console.error('Error polling for script:', error);
                    // Clear simulated timers on error
                    simulatedTimersRef.current.forEach(timer => clearTimeout(timer));
                    setIsGeneratingScriptOnboarding(false);
                    setProgress(0);
                    setProgressMessage('Error generating script');
                }
            };
            
            // Start polling after a short delay
            setTimeout(pollForScript, 2000);
            
        } catch (error) {
            console.error('Error generating script:', error);
            // Clear simulated timers on error
            simulatedTimersRef.current.forEach(timer => clearTimeout(timer));
            setIsGeneratingScriptOnboarding(false);
            setProgress(0);
            setProgressMessage('Error generating script');
        }
    }, [onboardingProfile.companyPurpose, onboardingProfile.targetAudience, onboardingProfile.targetChallenge, onboardingProfile.competitors, currentWorkspace, isGeneratingScriptOnboarding]);

    const translateScript = async () => {
        if (!translationLanguage) {
            console.error('Please select a translation language.');
            return;
        }

        if (!onboardingScript || !onboardingScript._id) {
            console.error('No script to translate.');
            return;
        }

        try {
            setIsTranslating(true);
            console.log('Starting translation for language:', translationLanguage);
            
            // Start async translation
            const response = await api.translateWorkspaceScript(currentWorkspace._id, onboardingScript._id, {
                language: translationLanguage,
                content: onboardingScript.content,
                contentAlt: onboardingScript.contentAlt || ''
            });

            console.log('Translation API response:', response.data);

            if (response.data.success) {
                const translationId = response.data.translationId;
                console.log('Translation started with ID:', translationId);
                
                let pollCount = 0;
                const maxPolls = 30; // Maximum 30 polls (60 seconds)
                
                // Poll for translation completion
                const pollForTranslation = async () => {
                    try {
                        pollCount++;
                        console.log(`Polling for translation status... (attempt ${pollCount}/${maxPolls})`);
                        
                        if (pollCount > maxPolls) {
                            console.error('Translation polling timeout reached');
                            setIsTranslating(false);
                            return;
                        }
                        
                        const statusResponse = await api.getWorkspaceTranslationStatus(currentWorkspace._id, translationId);
                        console.log('Translation status response:', statusResponse.data);
                        
                        const status = statusResponse.data;
                        
                        if (status.status === 'completed') {
                            console.log('Translation completed, updating script');
                            // Update the script with translated content
                            setOnboardingScript(prev => ({
                                ...prev,
                                content: status.result.content,
                                contentAlt: status.result.contentAlt
                            }));
                            setScriptModified(false); // Fresh translation, not modified yet
                            setIsEditingScript(false); // Show highlighted version
                            setIsTranslating(false);
                            trackEvent('onboarding_script_translated', { 
                                language: translationLanguage,
                                workspaceId: currentWorkspace._id
                            });
                            return;
                        } else if (status.status === 'error') {
                            console.error('Translation failed with error status');
                            setIsTranslating(false);
                            return;
                        }
                        
                        // Still processing, poll again
                        console.log('Translation still processing, polling again in 2 seconds...');
                        setTimeout(pollForTranslation, 2000);
                    } catch (error) {
                        console.error('Error polling for translation:', error);
                        setIsTranslating(false);
                    }
                };
                
                // Start polling after a short delay
                setTimeout(pollForTranslation, 2000);
            } else {
                console.error('Translation failed:', response.data.error);
                setIsTranslating(false);
            }
        } catch (error) {
            console.error('Error translating script:', error);
            setIsTranslating(false);
        }
    };

    // Step 0 (referral source) is re-introduced - onboarding starts at step 0

    // Auto-generate script when arriving at step 2
    useEffect(() => {
        console.log('Script generation useEffect triggered:', {
            step,
            hasScript: !!onboardingScript,
            isGenerating: isGeneratingScriptOnboarding,
            hasCompanyPurpose: !!onboardingProfile.companyPurpose
        });
        
        if (step === 3 && !onboardingScript && !isGeneratingScriptOnboarding && onboardingProfile.companyPurpose && currentWorkspace) {
            console.log('Starting script generation...');
            generateScript();
        }
    }, [step, onboardingScript, isGeneratingScriptOnboarding, onboardingProfile.companyPurpose, currentWorkspace, generateScript]);

    // Refresh recordings and voice data when entering voiceover step or when workspace changes
    useEffect(() => {
        const refreshVoiceData = async () => {
            if (step === 4 && currentWorkspace) { // Voiceover generation step
                try {
                    // Refresh workspace data to get latest recordings and voiceId
                    const workspaceResponse = await api.getWorkspace(currentWorkspace._id);
                    const workspace = workspaceResponse.data;
                    
                    // Always update recordings from workspace (even if empty array)
                    setOnboardingRecordings(workspace.recordings || []);
                    
                    // Update voiceId from workspace
                    if (workspace.voiceId) {
                        setOnboardingVoiceId(workspace.voiceId);
                    } else {
                        // Clear voiceId if no recordings exist (voice can't exist without recordings)
                        setOnboardingVoiceId('');
                    }
                    
                    // Clear any previous errors
                    setVoiceCloningError(null);
                } catch (error) {
                    console.error('Error refreshing workspace voice data:', error);
                }
            }
        };
        
        refreshVoiceData();
    }, [step, currentWorkspace?._id]); // Use _id instead of entire object to avoid unnecessary refreshes

    // Set workspace voice as default selected in case 3 if it exists
    useEffect(() => {
        if (step === 3 && onboardingVoiceId && !selectedDefaultVoice) {
            // Set workspace voice as default selected if no voice is currently selected
            setSelectedDefaultVoice(onboardingVoiceId);
        }
    }, [step, onboardingVoiceId, selectedDefaultVoice]);

    // Scroll to top when step changes so user always sees the top of the new screen (e.g. cuts screen)
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [step]);

    useEffect(() => {
        if (!onboardingVoiceoverUrl || !requestVoiceoverAutoplayRef.current) return;
        requestVoiceoverAutoplayRef.current = false;
        const t = setTimeout(() => {
            autoplayVoiceoverUrl(onboardingVoiceoverUrl, voiceoverAudioRef.current);
        }, 50);
        return () => clearTimeout(t);
    }, [onboardingVoiceoverUrl]);

    // Restore voice selection state when navigating back to voiceover step
    useEffect(() => {
        if (step === 4 && onboardingVoiceoverUrl) { // Voiceover generation step
            // If we have a voiceover and it was generated with a cloned voice, restore the state
            if (currentVoiceoverVoiceType === 'cloned' && onboardingVoiceId) {
                setUseClonedVoice(true);
                setSelectedDefaultVoice('');
            } else if (currentVoiceoverVoiceId) {
                // If it was generated with a default voice, restore that selection
                setUseClonedVoice(false);
                setSelectedDefaultVoice(currentVoiceoverVoiceId);
            }
        }
    }, [step, onboardingVoiceoverUrl, currentVoiceoverVoiceType, currentVoiceoverVoiceId, onboardingVoiceId]);

    // Load cuts when arriving at step 6 (Filter Cuts) or step 7 (video generation), or step 3 (voiceover)
    useEffect(() => {
        const loadCuts = async () => {
            // Load cuts when arriving at step 3 (voiceover), step 6 (filter cuts), or step 7 (video generation)
            if ((step === 3 || step === 6 || step === 7) && currentWorkspace && onboardingVideos.length > 0) {
                try {
                    console.log(`Loading cuts for step ${step}...`);
                    const cutsResponse = await api.getWorkspaceCuts(currentWorkspace._id);
                    const allCuts = cutsResponse.data || [];
                    
                    // Filter cuts for uploaded videos
                    const videoCuts = allCuts.filter(cut => 
                        onboardingVideos.some(video => video._id === cut.videoId)
                    );
                    
                    if (videoCuts.length > 0) {
                        setOnboardingCuts(videoCuts);
                        setShowCuts(true);
                        trackEvent('onboarding_cuts_loaded', { 
                            count: videoCuts.length,
                            step: step,
                            workspaceId: currentWorkspace._id
                        });
                        
                        // If we're on step 3 and not all cuts are ready, start polling
                        if (step === 3) {
                            // Check if we should start polling (e.g., if cuts count is less than expected)
                            // or if cuts generation might still be in progress
                            const expectedCutsPerVideo = 10; // Approximate expected cuts per video
                            const expectedTotalCuts = onboardingVideos.length * expectedCutsPerVideo;
                            
                            if (videoCuts.length < expectedTotalCuts) {
                                console.log(`Only ${videoCuts.length} cuts found, expected more. Starting polling...`);
                                startCutsPolling(onboardingVideos);
                            }
                        }
                    } else {
                        console.log('No cuts found for uploaded videos, starting polling...');
                        // Start polling for cuts if none found (for step 3, 6, or 7 - e.g. after Instagram import)
                        startCutsPolling(onboardingVideos);
                    }
                } catch (error) {
                    console.error(`Error loading cuts for step ${step}:`, error);
                    // Start polling even if there's an error so cuts appear when ready (e.g. after Instagram import)
                    if (step === 3 || step === 6 || step === 7) {
                        startCutsPolling(onboardingVideos);
                    }
                }
            }
        };

        loadCuts();
    }, [step, currentWorkspace, onboardingVideos]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const cancelUpload = async (fileName) => {
        try {
            console.log(`[CANCEL UPLOAD] Cancelling upload for file: ${fileName}`);
            
            // Abort the active XMLHttpRequest if it exists
            const activeUpload = activeUploads.get(fileName);
            if (activeUpload) {
                console.log(`[CANCEL UPLOAD] Aborting XMLHttpRequest for: ${fileName}`);
                activeUpload.abort();
                setActiveUploads(prev => {
                    const newMap = new Map(prev);
                    newMap.delete(fileName);
                    return newMap;
                });
            }
            
            // Add to cancelled uploads set
            setCancelledUploads(prev => new Set([...prev, fileName]));
            
            // Remove from upload progress
            setUploadProgress(prev => {
                const newProgress = { ...prev };
                delete newProgress[fileName];
                return newProgress;
            });
            
            // Update upload stats
            setUploadStats(prev => {
                const newStats = {
                    ...prev,
                    failedFiles: prev.failedFiles + 1,
                    currentFileIndex: Math.max(0, prev.currentFileIndex - 1)
                };
                
                // If all files are cancelled or completed, clear the upload stats
                const totalProcessed = newStats.completedFiles + newStats.failedFiles;
                if (totalProcessed >= prev.totalFiles) {
                    console.log('[CANCEL UPLOAD] All files processed, clearing upload stats');
                    // Clear stats after a short delay to show final state
                    setTimeout(() => {
                        setUploadStats({
                            totalFiles: 0,
                            currentFileIndex: 0,
                            completedFiles: 0,
                            failedFiles: 0,
                            currentFileName: ''
                        });
                    }, 2000);
                }
                
                return newStats;
            });
            
            // Call backend cancel API to clean up S3 and database
            if (currentWorkspace) {
                try {
                    await api.cancelUpload(currentWorkspace._id, fileName);
                    console.log(`[CANCEL UPLOAD] Backend notified of cancellation for: ${fileName}`);
                } catch (error) {
                    console.warn('Failed to notify backend of upload cancellation:', error);
                    // Don't show error to user since frontend cancellation worked
                }
            }
            
            trackEvent('onboarding_upload_cancelled', { 
                fileName,
                workspaceId: currentWorkspace?._id
            });
            
        } catch (error) {
            console.error('Error cancelling upload:', error);
        }
    };

    const initializeRecorder = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            setMediaStream(stream); // Store the stream for cleanup
            const recorder = new MediaRecorder(stream);
            setMediaRecorder(recorder);
            return recorder;
        } catch (error) {
            console.error('Error accessing microphone:', error);
            return null;
        }
    };

    const startRecording = async () => {
        const recorder = await initializeRecorder();
        if (!recorder) return;

        const chunks = [];
        recorder.ondataavailable = (event) => chunks.push(event.data);
        
        recorder.onstop = async () => {
            const blob = new Blob(chunks, { type: 'audio/webm' });
            const formData = new FormData();
            formData.append('audio', blob, 'recording.webm');
            formData.append('duration', recordingDurationOnboarding);
            
            setIsSavingRecordingOnboarding(true);
            try {
                if (currentWorkspace) {
                    // Use workspace recording API
                    const response = await api.saveWorkspaceRecording(currentWorkspace._id, formData);
                if (response.data.success) {
                    // Only allow one recording at a time - replace existing if any
                    const recording = response.data.recording;
                    console.log('Recording saved, structure:', recording);
                    if (!recording._id) {
                        console.error('Recording missing _id:', recording);
                    }
                    const newRecordings = [recording];
                    setOnboardingRecordings(newRecordings);
                    trackEvent('onboarding_recording_saved', { 
                        duration: recordingDurationOnboarding,
                        workspaceId: currentWorkspace._id
                    });
                    // Automatically start voice cloning with updated recordings
                    setTimeout(() => {
                        handleVoiceCloningWithRecordings(newRecordings);
                    }, 1000);
                }
                } else {
                    // Fallback to user recording API
                    const response = await api.saveRecording(formData);
                    // Only allow one recording at a time - replace existing if any
                    const newRecordings = [response.data.recording];
                    setOnboardingRecordings(newRecordings);
                    trackEvent('onboarding_recording_saved', { duration: recordingDurationOnboarding });
                    // Automatically start voice cloning with updated recordings
                    setTimeout(() => {
                        handleVoiceCloningWithRecordings(newRecordings);
                    }, 1000);
                }
            } catch (error) {
                console.error('Error saving recording:', error);
            } finally {
                setIsSavingRecordingOnboarding(false);
            }
        };

        recorder.start();
        setIsRecordingOnboarding(true);
        setRecordingDurationOnboarding(0);
        
        const interval = setInterval(() => {
            setRecordingDurationOnboarding(prev => prev + 1);
        }, 1000);
        setRecordingIntervalOnboarding(interval);
    };

    const stopRecording = () => {
        if (mediaRecorder && mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
            setIsRecordingOnboarding(false);
            if (recordingIntervalOnboarding) {
                clearInterval(recordingIntervalOnboarding);
                setRecordingIntervalOnboarding(null);
            }
            
            // Stop all tracks to release microphone
            if (mediaStream) {
                mediaStream.getTracks().forEach(track => track.stop());
                setMediaStream(null);
            }
        }
    };

    const handleAudioImport = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const isAudio = file.type.startsWith('audio/');
        const isVideo = file.type.startsWith('video/');
        if (!isAudio && !isVideo) {
            setVoiceCloningError('Please select an audio or video file');
            if (audioImportRef.current) audioImportRef.current.value = '';
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            setVoiceCloningError('Each file must be 10MB or less (ElevenLabs limit)');
            if (audioImportRef.current) audioImportRef.current.value = '';
            return;
        }

        const formData = new FormData();
        formData.append('audio', file);
        
        setVoiceCloningError(null); // Clear any previous errors
        setIsImportingAudio(true);
        
        try {
            if (currentWorkspace) {
                // Use workspace audio import API
                const response = await api.uploadWorkspaceAudioForVoiceCloning(currentWorkspace._id, formData);
                if (response.data.success) {
                    // Only allow one recording at a time - replace existing if any
                    const recording = response.data.recording;
                    console.log('Audio imported, recording structure:', recording);
                    if (!recording._id) {
                        console.error('Recording missing _id:', recording);
                    }
                    const newRecordings = [recording];
                    setOnboardingRecordings(newRecordings);
                    trackEvent('onboarding_audio_imported', { 
                        fileName: file.name,
                        workspaceId: currentWorkspace._id
                    });
                    // Automatically start voice cloning with updated recordings
                    setTimeout(() => {
                        handleVoiceCloningWithRecordings(newRecordings);
                    }, 1000);
                } else {
                    setVoiceCloningError('Failed to upload voice file. Please try again.');
                }
            } else {
                // Fallback to user audio import API
                const response = await api.uploadAudioForVoiceCloning(formData);
                // Only allow one recording at a time - replace existing if any
                const newRecordings = [response.data.recording];
                setOnboardingRecordings(newRecordings);
                trackEvent('onboarding_audio_imported', { fileName: file.name });
                // Automatically start voice cloning with updated recordings
                setTimeout(() => {
                    handleVoiceCloningWithRecordings(newRecordings);
                }, 1000);
            }
        } catch (error) {
            console.error('Error importing audio:', error);
            setVoiceCloningError(error.response?.data?.error || error.message || 'Failed to import voice file. Please try again.');
        } finally {
            setIsImportingAudio(false);
            // Reset file input so the same file can be selected again
            if (audioImportRef.current) {
                audioImportRef.current.value = '';
            }
        }
    };

    const handleDeleteRecording = async (recordingId) => {
        // Store previous state for optimistic update revert
        const previousRecordings = [...onboardingRecordings];
        const previousVoiceId = onboardingVoiceId;
        const previousVoiceCloningError = voiceCloningError;
        
            // Stop playing if this recording is currently playing
            if (playingRecordingId === recordingId) {
                const audio = recordingAudioRefs.current[recordingId];
                if (audio) {
                    audio.pause();
                    audio.currentTime = 0;
                }
                setPlayingRecordingId(null);
                delete recordingAudioRefs.current[recordingId];
            }

        // Optimistically update UI
        const updatedRecordings = onboardingRecordings.filter(r => r._id !== recordingId);
        setOnboardingRecordings(updatedRecordings);
        
        // Clear voiceId if no recordings remain (voice can't exist without recordings)
        if (updatedRecordings.length === 0) {
            setOnboardingVoiceId('');
            setVoiceCloningError(null);
        }

        try {
            if (currentWorkspace) {
                // Use workspace recording delete API
                await api.deleteWorkspaceRecording(currentWorkspace._id, recordingId);
                trackEvent('onboarding_recording_deleted', { 
                    workspaceId: currentWorkspace._id
                });
            } else {
                // Fallback to user recording delete API
                await api.deleteRecording(recordingId);
                trackEvent('onboarding_recording_deleted');
            }
        } catch (error) {
            console.error('Error deleting recording:', error);
            // Revert optimistic update
            setOnboardingRecordings(previousRecordings);
            setOnboardingVoiceId(previousVoiceId);
            setVoiceCloningError(previousVoiceCloningError);
        }
    };

    const handlePlayRecording = (recording) => {
        // Stop any currently playing recording
        if (playingRecordingId && recordingAudioRefs.current[playingRecordingId]) {
            const currentAudio = recordingAudioRefs.current[playingRecordingId];
            if (currentAudio) {
                currentAudio.pause();
                currentAudio.currentTime = 0;
            }
        }

        // If clicking the same recording, just stop it
        if (playingRecordingId === recording._id) {
            setPlayingRecordingId(null);
            return;
        }

        // Get or create audio element
        let audio = recordingAudioRefs.current[recording._id];
        if (!audio) {
            audio = new Audio(recording.url);
            audio.addEventListener('ended', () => {
                setPlayingRecordingId(null);
            });
            audio.addEventListener('error', () => {
                console.error('Error playing recording:', recording._id);
                setPlayingRecordingId(null);
            });
            recordingAudioRefs.current[recording._id] = audio;
        }

        // Play the recording
        audio.play().catch(error => {
            console.error('Error playing audio:', error);
            setPlayingRecordingId(null);
        });
        setPlayingRecordingId(recording._id);
    };

    const handleVoiceCloningWithRecordings = async (recordings) => {
        if (!recordings || recordings.length < 1) {
            console.error('No recordings provided to handleVoiceCloningWithRecordings');
            return;
        }
        
        // Ensure recordings have _id property
        const recordingIds = recordings
            .map(r => {
                // Handle both _id and id properties, and convert to string
                const id = r._id || r.id;
                if (!id) {
                    console.error('Recording missing _id:', r);
                    return null;
                }
                return id.toString();
            })
            .filter(id => id !== null);
        
        if (recordingIds.length === 0) {
            console.error('No valid recording IDs found:', recordings);
            setVoiceCloningError('No valid recordings found. Please record or import audio again.');
            return;
        }
        
        console.log('Cloning voice with recording IDs:', recordingIds);
        
        setIsGeneratingVoiceOnboarding(true);
        setVoiceCloningError(null);
        setShowVoiceClonePaywall(false);
        try {
            if (currentWorkspace) {
                // Use workspace voice cloning API
                const response = await api.cloneWorkspaceVoice(currentWorkspace._id, {
                    recordings: recordingIds
                });
                if (response.data && response.data.voiceId) {
                    setOnboardingVoiceId(response.data.voiceId);
                    trackEvent('onboarding_voice_cloned', { 
                        recordingsCount: recordings.length,
                        workspaceId: currentWorkspace._id
                    });
                } else {
                    throw new Error('No voiceId returned from API');
                }
            } else {
                // Fallback to user voice cloning API
                const response = await api.cloneVoice({
                    recordings: recordingIds
                });
                if (response.data && response.data.voiceId) {
                    setOnboardingVoiceId(response.data.voiceId);
                    trackEvent('onboarding_voice_cloned', { recordingsCount: recordings.length });
                } else {
                    throw new Error('No voiceId returned from API');
                }
            }
        } catch (error) {
            console.error('Error cloning voice:', error);
            setVoiceCloningError(
                isVoiceCloneLimitError(error)
                    ? VOICE_CLONE_LIMIT_MESSAGE
                    : (error.response?.data?.error || 'Failed to clone voice. Please try again.')
            );
        } finally {
            setIsGeneratingVoiceOnboarding(false);
        }
    };

    const startVoiceCloneCheckout = async (plan) => {
        if (!currentWorkspace?._id) return;
        try {
            const response = await api.createCheckoutSession(plan, currentWorkspace._id);
            window.location.href = response.data.url;
        } catch (e) {
            setVoiceCloningError('Failed to start checkout. Please try again.');
        }
    };

    const voiceCloneUpgradeActions = voiceCloningError === VOICE_CLONE_LIMIT_MESSAGE && currentWorkspace ? (
        showVoiceClonePaywall ? (
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '12px' }}>
                <button type="button" onClick={() => startVoiceCloneCheckout('creator')} style={{ padding: '8px 16px', backgroundColor: '#e1f679', color: '#111', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                    Subscribe: Merra AI Creator — {currency}25/month
                </button>
                <button type="button" onClick={() => startVoiceCloneCheckout('pro')} style={{ padding: '8px 16px', backgroundColor: '#e1f679', color: '#111', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                    Subscribe: Merra AI Pro — {currency}99/month
                </button>
            </div>
        ) : (
            <button type="button" onClick={() => setShowVoiceClonePaywall(true)} style={{ marginTop: '12px', padding: '8px 16px', backgroundColor: '#e1f679', color: '#111', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                Upgrade
            </button>
        )
    ) : null;

    const handleVoiceCloning = async () => {
        if (onboardingRecordings.length < 1) {
            console.error('No recordings available for voice cloning');
            setVoiceCloningError('No recordings available. Please record or import audio first.');
            return;
        }
        
        console.log('handleVoiceCloning called with recordings:', onboardingRecordings);
        await handleVoiceCloningWithRecordings(onboardingRecordings);
    };

    const testClonedVoice = async () => {
        if (!onboardingVoiceId) return;
        
        setIsTestingVoice(true);
        try {
            if (currentWorkspace) {
                // Use workspace voice testing API
                const response = await api.testWorkspaceClonedVoice(
                    currentWorkspace._id, 
                    "hey, this is me, the cloned voice, do I sound like you ?",
                    onboardingVoiceId
                );
                setTestAudioUrl(response.data.audioUrl);
                trackEvent('onboarding_voice_tested', { 
                    workspaceId: currentWorkspace._id
                });
            } else {
                // Fallback to user voice testing API
                const response = await api.testClonedVoice(
                    "hey, this is me, the cloned voice, do I sound like you ?",
                    onboardingVoiceId
                );
                setTestAudioUrl(response.data.audioUrl);
                trackEvent('onboarding_voice_tested');
            }
        } catch (error) {
            console.error('Error testing voice:', error);
        } finally {
            setIsTestingVoice(false);
        }
    };

    // Mac Photos (and some other apps) drop videos with type image/jpeg or application/octet-stream; use filename extension to detect video
    const getEffectiveFileType = (file) => {
        const name = (file.name || '').toLowerCase();
        const videoExts = ['.mov', '.mp4', '.m4v', '.avi', '.webm', '.mkv', '.3gp'];
        const isVideoExt = videoExts.some(ext => name.endsWith(ext));
        const ambiguousType = !file.type || file.type.startsWith('image/') || file.type === 'application/octet-stream';
        if (isVideoExt && ambiguousType) {
            if (name.endsWith('.mov')) return 'video/quicktime';
            if (name.endsWith('.mp4') || name.endsWith('.m4v')) return 'video/mp4';
            if (name.endsWith('.webm')) return 'video/webm';
            if (name.endsWith('.avi')) return 'video/x-msvideo';
            if (name.endsWith('.mkv')) return 'video/x-matroska';
            if (name.endsWith('.3gp')) return 'video/3gpp';
            return 'video/mp4';
        }
        return file.type;
    };

    const isVideoFile = (file) => getEffectiveFileType(file).startsWith('video/');

    const handleFileUpload = async (files) => {
        // Only videos: use getEffectiveFileType so Mac Photos (image/* or octet-stream) is accepted when extension is video
        const fileArray = Array.from(files).filter(isVideoFile);
        if (fileArray.length === 0) {
            if (files.length > 0) {
                const fileList = Array.from(files);
                const hasImages = fileList.some(f => (f.type || '').startsWith('image/'));
                if (hasImages) {
                    window.alert(
                        "Looks like you dragged from macOS Photos. It provides a JPEG preview instead of the original video.\n\n" +
                        "Fix: export the video to Finder first (Photos > File > Export > Export Unmodified Original), " +
                        "then drag the .MOV/.MP4 here."
                    );
                } else {
                    setOnboardingErrors(prev => [...prev, 'Only video files are accepted. Please drop or select video files (.mov, .mp4, etc.).']);
                }
            }
            return;
        }
        const uploadErrors = [];
        const failedFiles = new Set();

        // Calculate total size and estimate time
        const totalSize = fileArray.reduce((sum, file) => sum + file.size, 0);
        const totalSizeMB = totalSize / (1024 * 1024);
        const isLargeUpload = totalSizeMB > 1000 || fileArray.length > 20;
        
        // Track video upload start
        trackEvent('onboarding_video_upload_started', {
            fileCount: fileArray.length,
            totalSizeMB: Math.round(totalSizeMB * 100) / 100,
            isLargeUpload,
            workspaceId: currentWorkspace?._id
        });

        // Initialize upload stats
        setUploadStats({
            totalFiles: fileArray.length,
            currentFileIndex: 0,
            completedFiles: 0,
            failedFiles: 0,
            currentFileName: fileArray[0]?.name || ''
        });

        try {
            if (currentWorkspace) {
                // Use workspace video upload flow
                const uploadPromises = fileArray.map(async (file, index) => {
                    // Check file size
                    if (file.size > 2000 * 1024 * 1024) { // 2000MB
                        uploadErrors.push(`File ${file.name} is too large. Maximum size is 2GB`);
                        setUploadStats(prev => ({
                            ...prev,
                            failedFiles: prev.failedFiles + 1
                        }));
                        return;
                    }

                    const durationSec = await readVideoFileDurationSec(file);
                    if (isRawVideoTooLong(durationSec)) {
                        uploadErrors.push(`${file.name} is longer than 5 minutes. ${RAW_VIDEO_TOO_LONG_MESSAGE}`);
                        setUploadStats(prev => ({
                            ...prev,
                            failedFiles: prev.failedFiles + 1
                        }));
                        return;
                    }

                    try {
                        const effectiveType = getEffectiveFileType(file);
                        trackEvent('onboarding_file_upload_started', {
                            fileName: file.name,
                            fileSize: file.size,
                            fileType: effectiveType,
                            fileIndex: index + 1,
                            totalFiles: fileArray.length,
                            workspaceId: currentWorkspace._id
                        });

                        // Step 1: Get presigned URL for direct S3 upload (or multipart upload ID)
                        const uploadUrlResponse = await api.getWorkspaceUploadUrl(
                            currentWorkspace._id,
                            file.name,
                            effectiveType,
                            file.size
                        );

                        const { uploadUrl, key, videoUrl: initialVideoUrl, multipart, uploadId } = uploadUrlResponse.data;
                        let videoUrl = initialVideoUrl;

                        // Step 2: Upload directly to S3 (multipart for large files, single-part for smaller)
                        let uploadPromise;
                        if (multipart && uploadId) {
                            // Multipart upload for files >500MB
                            uploadPromise = new Promise(async (resolve, reject) => {
                                try {
                                    const PART_SIZE = 10 * 1024 * 1024; // 10MB per part
                                    const totalParts = Math.ceil(file.size / PART_SIZE);
                                    const parts = [];
                                    let uploadedBytes = 0;

                                    // Upload each part
                                    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
                                        const start = (partNumber - 1) * PART_SIZE;
                                        const end = Math.min(start + PART_SIZE, file.size);
                                        const chunk = file.slice(start, end);

                                        // Get presigned URL for this part
                                        const partUrlResponse = await api.getWorkspaceMultipartUrl(
                                            currentWorkspace._id,
                                            key,
                                            uploadId,
                                            partNumber
                                        );

                                        // Upload the part
                                        const partETag = await new Promise((partResolve, partReject) => {
                                            const xhr = new XMLHttpRequest();

                                            xhr.upload.addEventListener('progress', (event) => {
                                                if (event.lengthComputable) {
                                                    uploadedBytes = start + event.loaded;
                                                    const progress = Math.round((uploadedBytes * 100) / file.size);
                                                    setUploadProgress(prev => ({
                                                        ...prev,
                                                        [file.name]: progress
                                                    }));
                                                }
                                            });

                                            xhr.addEventListener('load', () => {
                                                if (xhr.status >= 200 && xhr.status < 300) {
                                                    const etag = xhr.getResponseHeader('ETag')?.replace(/"/g, '');
                                                    if (!etag) {
                                                        partReject(new Error('No ETag in response'));
                                                        return;
                                                    }
                                                    partResolve(etag);
                                                } else {
                                                    partReject(new Error(`Part ${partNumber} upload failed: ${xhr.statusText}`));
                                                }
                                            });

                                            xhr.addEventListener('error', () => partReject(new Error(`Part ${partNumber} upload failed: Network error`)));
                                            
                                            const fileSizeMB = file.size / 1024 / 1024;
                                            const timeoutMinutes = Math.max(60, Math.ceil(fileSizeMB / 100) * 5 + 60);
                                            xhr.addEventListener('timeout', () => {
                                                partReject(new Error(`Part ${partNumber} upload timeout after ${timeoutMinutes} minutes`));
                                            });

                                            xhr.open('PUT', partUrlResponse.data.uploadUrl);
                                            xhr.timeout = timeoutMinutes * 60 * 1000;
                                            xhr.send(chunk);
                                        });

                                        parts.push({ ETag: partETag, PartNumber: partNumber });
                                    }

                                    // Complete multipart upload
                                    const completeResponse = await api.completeWorkspaceMultipart(
                                        currentWorkspace._id,
                                        key,
                                        uploadId,
                                        parts
                                    );

                                    videoUrl = completeResponse.data.videoUrl || videoUrl;
                                    
                                    // Ensure progress shows 100% when complete
                                    setUploadProgress(prev => ({
                                        ...prev,
                                        [file.name]: 100
                                    }));
                                    
                                    resolve({ status: 200 }); // Resolve with success
                                } catch (error) {
                                    // Abort multipart upload on error
                                    try {
                                        await api.abortWorkspaceMultipart(currentWorkspace._id, key, uploadId);
                                    } catch (abortError) {
                                        console.error('Failed to abort multipart upload:', abortError);
                                    }
                                    reject(error);
                                }
                            });
                        } else {
                            // Single-part upload for smaller files
                            uploadPromise = new Promise((resolve, reject) => {
                                const xhr = new XMLHttpRequest();
                                
                                // Store the XMLHttpRequest for potential cancellation
                                setActiveUploads(prev => new Map(prev).set(file.name, xhr));
                                
                                // Track upload progress
                                xhr.upload.addEventListener('progress', (event) => {
                                    if (event.lengthComputable) {
                                        const progress = Math.round((event.loaded * 100) / event.total);
                                        setUploadProgress(prev => ({
                                            ...prev,
                                            [file.name]: progress
                                        }));
                                    }
                                });
                                
                                // Handle successful upload
                                xhr.addEventListener('load', () => {
                                    // Clean up XMLHttpRequest from active uploads
                                    setActiveUploads(prev => {
                                        const newMap = new Map(prev);
                                        newMap.delete(file.name);
                                        return newMap;
                                    });
                                    
                                    if (xhr.status >= 200 && xhr.status < 300) {
                                        resolve(xhr);
                                    } else {
                                        reject(new Error(`S3 upload failed: ${xhr.statusText}`));
                                    }
                                });
                                
                                // Handle upload errors
                                xhr.addEventListener('error', () => {
                                    // Clean up XMLHttpRequest from active uploads
                                    setActiveUploads(prev => {
                                        const newMap = new Map(prev);
                                        newMap.delete(file.name);
                                        return newMap;
                                    });
                                    reject(new Error('S3 upload failed: Network error'));
                                });
                                
                                // Handle upload timeout
                                xhr.addEventListener('timeout', () => {
                                    // Clean up XMLHttpRequest from active uploads
                                    setActiveUploads(prev => {
                                        const newMap = new Map(prev);
                                        newMap.delete(file.name);
                                        return newMap;
                                    });
                                    reject(new Error('S3 upload failed: Timeout'));
                                });
                                
                                // Configure and start upload (use effectiveType so Mac Photos videos saved as image/jpeg upload as video)
                                xhr.open('PUT', uploadUrl);
                                xhr.setRequestHeader('Content-Type', effectiveType);
                                xhr.timeout = 1800000; // 30 minutes timeout for very large files
                                xhr.send(file);
                            });
                        }
                        
                        await uploadPromise;

                        // Step 3: Confirm upload
                        const confirmResponse = await api.confirmWorkspaceUpload(
                            currentWorkspace._id,
                            key,
                            videoUrl,
                            file.name,
                            '',
                            JSON.stringify([]),
                            { durationSec: durationSec ?? undefined }
                        );

                        trackEvent('onboarding_file_upload_success', {
                            fileName: file.name,
                            fileSize: file.size,
                            fileType: effectiveType,
                            fileIndex: index + 1,
                            totalFiles: fileArray.length,
                            workspaceId: currentWorkspace._id
                        });

                        // Update onboarding videos
                        setOnboardingVideos(prev => {
                            const uploaded = confirmResponse.data.video || {};
                            const updatedVideo = {
                                _id: uploaded._id,
                                name: uploaded.title,
                                url: uploaded.url,
                                status: uploaded.status,
                                tags: uploaded.tags || [],
                                description: uploaded.description || '',
                                cuts: uploaded.cuts || [],
                                createdAt: uploaded.createdAt
                            };
                            const exists = prev.some(v => v._id === uploaded._id);
                            if (exists) {
                                return prev.map(v => v._id === uploaded._id ? { ...v, ...updatedVideo } : v);
                            }
                            return [updatedVideo, ...prev];
                        });

                        // Keep progress at 100% to show completed circles
                        // Don't remove from uploadProgress so circles remain visible
                        setUploadProgress(prev => ({
                            ...prev,
                            [file.name]: 100
                        }));

                        // Update completed files count
                        setUploadStats(prev => {
                            const newStats = {
                                ...prev,
                                completedFiles: prev.completedFiles + 1
                            };
                            
                            // If all files are completed or failed, clear the upload stats
                            const totalProcessed = newStats.completedFiles + newStats.failedFiles;
                            if (totalProcessed >= prev.totalFiles) {
                                console.log('[UPLOAD COMPLETE] All files processed, clearing upload stats');
                                // Clear stats after a short delay to show final state
                                setTimeout(() => {
                                    setUploadStats({
                                        totalFiles: 0,
                                        currentFileIndex: 0,
                                        completedFiles: 0,
                                        failedFiles: 0,
                                        currentFileName: ''
                                    });
                                }, 2000);
                            }
                            
                            return newStats;
                        });

                        return confirmResponse.data.video;
                    } catch (error) {
                        trackEvent('onboarding_file_upload_error', {
                            fileName: file.name,
                            error: error.message,
                            fileIndex: index + 1,
                            totalFiles: fileArray.length,
                            workspaceId: currentWorkspace._id
                        });
                        
                        // Handle upload errors
                        uploadErrors.push(`Failed to upload ${file.name}: ${error.response?.data?.error || error.message}`);
                        failedFiles.add(file.name);
                        
                        // Clear progress for failed upload
                        setUploadProgress(prev => {
                            const newProgress = { ...prev };
                            delete newProgress[file.name];
                            return newProgress;
                        });

                        // Update failed files count
                        setUploadStats(prev => {
                            const newStats = {
                                ...prev,
                                failedFiles: prev.failedFiles + 1
                            };
                            
                            // If all files are completed or failed, clear the upload stats
                            const totalProcessed = newStats.completedFiles + newStats.failedFiles;
                            if (totalProcessed >= prev.totalFiles) {
                                console.log('[UPLOAD FAILED] All files processed, clearing upload stats');
                                // Clear stats after a short delay to show final state
                                setTimeout(() => {
                                    setUploadStats({
                                        totalFiles: 0,
                                        currentFileIndex: 0,
                                        completedFiles: 0,
                                        failedFiles: 0,
                                        currentFileName: ''
                                    });
                                }, 2000);
                            }
                            
                            return newStats;
                        });
                    }
                });

                // Wait for all uploads to complete
                const results = await Promise.allSettled(uploadPromises);
                const uploadedVideos = results
                    .filter(result => result.status === 'fulfilled' && result.value)
                    .map(result => result.value);

                trackEvent('onboarding_videos_uploaded', { 
                    count: uploadedVideos.length,
                    workspaceId: currentWorkspace._id
                });
                
                // Start polling for cuts if any uploads were successful
                if (uploadedVideos.length > 0) {
                    startCutsPolling(uploadedVideos);
                }
                if (uploadErrors.length > 0) {
                    setOnboardingErrors(prev => [...prev, ...uploadErrors]);
                }
            } else {
                // Fallback to user video upload
                const formData = new FormData();
                fileArray.forEach((file, index) => {
                    formData.append('files', file);
                });

                const response = await api.uploadVideo(formData);
                setOnboardingVideos(response.data.videos);
                trackEvent('onboarding_videos_uploaded', { count: fileArray.length });
                
                // Start polling for cuts
                startCutsPolling(response.data.videos);
            }

            // Clear upload stats after all files are processed
            setTimeout(() => {
                setUploadStats({
                    totalFiles: 0,
                    currentFileIndex: 0,
                    completedFiles: 0,
                    failedFiles: 0,
                    currentFileName: ''
                });
            }, 3000);

        } catch (error) {
            console.error('Error uploading files:', error);
        }
    };

    // Poll for cuts generation status for uploaded videos
    useEffect(() => {
        if (step !== 6 || !currentWorkspace || !onboardingVideos || onboardingVideos.length === 0) {
            return;
        }

        // Initialize which videos are generating
        const checkInitialStatus = async () => {
            let hasGenerating = false;
            for (const video of onboardingVideos) {
                try {
                    const statusResp = await api.getVideoCutsGenerationStatus(currentWorkspace._id, video._id);
                    if (statusResp.data.status === 'generating' || statusResp.data.status === 'pending') {
                        hasGenerating = true;
                        break;
                    }
                } catch (error) {
                    console.error(`Error checking status for video ${video._id}:`, error);
                }
            }
            setCutsGenerationActive(hasGenerating);
        };

        checkInitialStatus();

        // Poll every 3 seconds for status updates
        const statusIntervalRef = { current: null };
        
        const pollStatus = async () => {
            if (!currentWorkspace || !onboardingVideos || onboardingVideos.length === 0) {
                if (statusIntervalRef.current) {
                    clearInterval(statusIntervalRef.current);
                    statusIntervalRef.current = null;
                }
                return;
            }

            // Check all videos in parallel instead of sequentially
            try {
                const statusPromises = onboardingVideos.map(video => 
                    api.getVideoCutsGenerationStatus(currentWorkspace._id, video._id)
                        .then(resp => ({ videoId: video._id, status: resp?.data?.status, success: true }))
                        .catch(error => {
                            console.error(`Error checking status for video ${video._id}:`, error);
                            return { videoId: video._id, status: null, success: false };
                        })
                );

                const statusResults = await Promise.all(statusPromises);
                
                let hasGenerating = false;
                let allCompleted = true;
                
                for (const result of statusResults) {
                    if (!result.success) {
                        // If we can't check status, assume it might still be generating
                        allCompleted = false;
                        continue;
                    }
                    
                    const status = result.status;
                    if (status === 'generating' || status === 'pending') {
                        hasGenerating = true;
                        allCompleted = false;
                    } else if (status !== 'completed' && status !== 'error') {
                        // Unknown status, assume not completed to be safe
                        allCompleted = false;
                    }
                }
                
                setCutsGenerationActive(hasGenerating);
                
                // Stop polling if all videos are completed or errored (and none are generating)
                if (!hasGenerating && allCompleted && statusIntervalRef.current) {
                    console.log('All videos completed, stopping cuts status polling');
                    setCutsGenerationActive(false); // Explicitly set to false when stopping
                    clearInterval(statusIntervalRef.current);
                    statusIntervalRef.current = null;
                }
            } catch (error) {
                console.error('Error in parallel status polling:', error);
            }
        };
        
        statusIntervalRef.current = setInterval(pollStatus, 3000);

        return () => {
            if (statusIntervalRef.current) {
                clearInterval(statusIntervalRef.current);
                statusIntervalRef.current = null;
            }
        };
    }, [step, currentWorkspace, onboardingVideos]);

    const startCutsPolling = (videos) => {
        const list = Array.isArray(videos) ? videos : [];
        if (list.length === 0) return;
        console.log('Starting cuts polling for videos:', list.map(v => v._id));
        setIsPollingCuts(true); // Set polling state to true
        // Don't set cutsGenerationActive here - let the status polling handle it
        setLastCutsCount(onboardingCuts.length); // Initialize with current cuts count
        let pollCount = 0;
        const maxPolls = 20; // 40 seconds total (20 * 2 seconds) - increased for more cuts
        let noNewCutsCount = 0; // Track consecutive polls with no new cuts
        const maxNoNewCuts = 3; // Stop if no new cuts for 3 consecutive polls (6 seconds)
        
        const interval = setInterval(async () => {
            try {
                pollCount++;
                console.log(`Polling cuts... (attempt ${pollCount}/${maxPolls})`);
                
                if (pollCount > maxPolls) {
                    console.log('Cuts polling timeout reached');
                    setIsPollingCuts(false);
                    setCutsGenerationActive(false);
                    clearInterval(interval);
                    return;
                }
                
                if (currentWorkspace) {
                    // First check if all videos have completed cuts generation (in parallel)
                    try {
                        const statusPromises = list.map(video => 
                            api.getVideoCutsGenerationStatus(currentWorkspace._id, video._id)
                                .then(resp => ({ videoId: video._id, status: resp?.data?.status, success: true }))
                                .catch(error => {
                                    console.error(`Error checking status for video ${video._id}:`, error);
                                    return { videoId: video._id, status: null, success: false };
                                })
                        );

                        const statusResults = await Promise.all(statusPromises);
                        
                        let allCompleted = true;
                        let hasGenerating = false;
                        
                        for (const result of statusResults) {
                            if (!result.success) {
                                allCompleted = false;
                                continue;
                            }
                            
                            const status = result.status;
                            if (status === 'generating' || status === 'pending') {
                                hasGenerating = true;
                                allCompleted = false;
                            } else if (status !== 'completed' && status !== 'error') {
                                allCompleted = false;
                            }
                        }
                        
                        // If all videos are completed, stop polling
                        if (allCompleted && !hasGenerating) {
                            console.log('All videos completed, stopping cuts polling');
                            setCutsGenerationActive(false);
                            setIsPollingCuts(false);
                            clearInterval(interval);
                            return;
                        }
                    } catch (error) {
                        console.error('Error in parallel status check:', error);
                    }
                    
                    const cutsResponse = await api.getWorkspaceCuts(currentWorkspace._id);
                    const allCuts = cutsResponse.data || [];
                    const videoCuts = allCuts.filter(cut => 
                        list.some(video => video._id === cut.videoId)
                    );
                    
                    console.log(`Found ${videoCuts.length} cuts for uploaded videos (previous: ${lastCutsCount})`);
                    
                    // Check if we have new cuts
                    if (videoCuts.length > lastCutsCount) {
                        console.log(`New cuts detected! ${lastCutsCount} -> ${videoCuts.length}`);
                        noNewCutsCount = 0; // Reset counter
                        // Don't set cutsGenerationActive here - let status polling handle it
                        
                        setOnboardingCuts(prevCuts => {
                            // Merge with existing cuts, avoiding duplicates
                            const existingIds = new Set(prevCuts.map(c => c._id));
                            const newCuts = videoCuts.filter(cut => !existingIds.has(cut._id));
                            const mergedCuts = [...prevCuts, ...newCuts];
                            
                            console.log(`Updated cuts: ${prevCuts.length} -> ${mergedCuts.length}`);
                            return mergedCuts;
                        });
                        setShowCuts(true);
                        setLastCutsCount(videoCuts.length);
                        
                        // Track event for new cuts
                        trackEvent('onboarding_cuts_generated', { 
                            count: videoCuts.length,
                            totalCuts: videoCuts.length,
                            workspaceId: currentWorkspace._id
                        });
                    } else {
                        // No new cuts this poll
                        noNewCutsCount++;
                        console.log(`No new cuts this poll (${noNewCutsCount}/${maxNoNewCuts})`);
                        
                        // If no new cuts for several consecutive polls, assume generation is complete
                        if (noNewCutsCount >= maxNoNewCuts) {
                            console.log('No new cuts detected for several polls, assuming generation complete');
                            setCutsGenerationActive(false);
                            setIsPollingCuts(false);
                            clearInterval(interval);
                            return;
                        }
                    }
                } else {
                    console.log('No workspace available, skipping cuts polling');
                    setIsPollingCuts(false);
                    setCutsGenerationActive(false);
                    clearInterval(interval);
                }
            } catch (error) {
                console.error('Error polling cuts:', error);
            }
        }, 2000);

        // Clear interval after timeout
        setTimeout(() => {
            console.log('Cuts polling completed after timeout');
            setIsPollingCuts(false);
            setCutsGenerationActive(false);
            clearInterval(interval);
        }, 40000); // Increased timeout to 40 seconds
    };

    const handleHideCut = async (cutId) => {
        try {
            if (!currentWorkspace?._id) {
                console.error('No workspace available');
                return;
            }
            await api.toggleWorkspaceCutVisibility(currentWorkspace._id, cutId);
            setHiddenCuts(prev => new Set([...prev, cutId]));
            trackEvent('onboarding_cut_hidden');
        } catch (error) {
            console.error('Error hiding cut:', error);
        }
    };

    const stopAllAudio = useCallback(() => {
        // Stop voice preview audio
        if (currentAudio) {
            try {
                currentAudio.pause();
                currentAudio.currentTime = 0;
                // Remove event listeners to prevent memory leaks
                currentAudio.onended = null;
                currentAudio.onerror = null;
                // Properly dispose of audio object
                currentAudio.src = '';
                currentAudio.load();
            } catch (e) {
                // Ignore errors
            }
            setCurrentAudio(null);
        }
        setPlayingVoiceId(null);
        
        // Stop voiceover audio
        if (voiceoverAudioRef.current) {
            try {
                voiceoverAudioRef.current.pause();
                voiceoverAudioRef.current.currentTime = 0;
            } catch (e) {
                // Ignore errors
            }
        }
    }, [currentAudio]);

    const handleVoicePreview = (voiceId, previewUrl) => {
        if (playingVoiceId === voiceId) {
            // Stop current preview and properly dispose
            if (currentAudio) {
                currentAudio.pause();
                currentAudio.currentTime = 0;
                // Remove event listeners to prevent memory leaks
                currentAudio.onended = null;
                currentAudio.onerror = null;
                // Properly dispose of audio object
                currentAudio.src = '';
                currentAudio.load();
                setCurrentAudio(null);
            }
            setPlayingVoiceId(null);
            return;
        }

        // Stop voiceover if playing
        if (voiceoverAudioRef.current && !voiceoverAudioRef.current.paused) {
            try {
                voiceoverAudioRef.current.pause();
                voiceoverAudioRef.current.currentTime = 0;
            } catch (e) {
                // Ignore errors
            }
        }

        // Stop any currently playing preview and properly dispose
        if (currentAudio) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
            // Remove event listeners to prevent memory leaks
            currentAudio.onended = null;
            currentAudio.onerror = null;
            // Properly dispose of audio object
            currentAudio.src = '';
            currentAudio.load();
            setCurrentAudio(null);
        }
        setPlayingVoiceId(null);
        
        // Start new preview
        setTimeout(() => {
            setPlayingVoiceId(voiceId);
            const audio = new Audio(previewUrl);
            setCurrentAudio(audio);
            
            audio.play().catch(error => {
                console.error('Error playing voice preview:', error);
                setPlayingVoiceId(null);
                setCurrentAudio(null);
            });
            
            audio.onended = () => {
                setPlayingVoiceId(null);
                setCurrentAudio(null);
            };
            
            audio.onerror = () => {
                console.error('Error playing voice preview:', previewUrl);
                setPlayingVoiceId(null);
                setCurrentAudio(null);
            };
        }, 100);
    };

    // Helper function for case 3 to generate voiceover with a specific voiceId
    const generateVoiceover = async (voiceId) => {
        if (!onboardingScript || !voiceId) return;
        
        const isWorkspaceVoice = onboardingVoiceId && voiceId === onboardingVoiceId;
        
        setIsGeneratingVoiceoverOnboarding(true);
        if (onboardingVoiceoverUrl) requestVoiceoverAutoplayRef.current = true;
        try {
            // If script was modified, save the changes to database first
            if (scriptModified) {
                console.log('Saving script changes before voiceover generation...');
                await api.updateWorkspaceScriptContent(
                    currentWorkspace._id, 
                    onboardingScript._id, 
                    'main', 
                    onboardingScript.content
                );
            }
            
            const response = await api.studioGenerateVoiceover(onboardingScript._id, null, voiceId);
            setOnboardingVoiceoverUrl(response.data.voiceoverUrl);
            setCurrentVoiceoverVoiceId(voiceId);
            setCurrentVoiceoverVoiceType(isWorkspaceVoice ? 'cloned' : 'default');
            setSelectedDefaultVoice(voiceId); // Update selected voice
            if (typeof playCompletionSound === 'function') playCompletionSound('voice_merra');
            if (typeof triggerCompletionGlow === 'function') triggerCompletionGlow(['#667eea', '#764ba2']);
            
            // Get voiceover duration for warning message
            const audio = new Audio(response.data.voiceoverUrl);
            audio.addEventListener('loadedmetadata', () => {
                setVoiceoverDuration(audio.duration);
            });
            
            trackEvent('onboarding_voiceover_generated', { 
                voiceType: isWorkspaceVoice ? 'cloned' : 'default',
                voiceId: voiceId
            });
        } catch (error) {
            console.error('Error generating voiceover:', error);
        } finally {
            setIsGeneratingVoiceoverOnboarding(false);
            setScriptModified(false); // Reset after voiceover generation
            setIsEditingScript(false); // Return to highlighted view
        }
    };

    const generateVideo = async (forceFillWithTextScreens = false, forceProceedWithRepeatedCuts = false) => {
        console.log('generateVideo called with:', {
            hasScript: !!onboardingScript,
            hasWorkspace: !!currentWorkspace,
            scriptId: onboardingScript?._id,
            workspaceId: currentWorkspace?._id
        });
        
        if (!onboardingScript || !currentWorkspace) {
            console.log('generateVideo: Missing script or workspace, returning');
            return;
        }
        
        const availableCutsCount = onboardingCuts.filter(cut => !hiddenCuts.has(cut._id)).length;
        // Onboarding: assume 2s cuts, regular pace only → need ceil(duration/2) cuts
        const requiredCuts = voiceoverDuration > 0 ? Math.ceil(voiceoverDuration / 2) : 0;
        if (!forceFillWithTextScreens && !forceProceedWithRepeatedCuts && requiredCuts > 0 && availableCutsCount < requiredCuts) {
            setShowInsufficientCutsModal(true);
            return;
        }
        
        console.log('generateVideo: Starting video generation...');
        setIsGeneratingVideoOnboarding(true);
        setOnboardingVideoProgress(0);
        
        let generatePayload = null;
        try {
            // Determine music selection - use random music if none selected
            let musicIdToUse = selectedMusicId || '';
            if (!musicIdToUse) {
                try {
                    const musicResponse = await api.getWorkspaceMusic(currentWorkspace._id);
                    const availableMusic = musicResponse.data || [];
                    if (availableMusic.length > 0) {
                        // Pick a random music track
                        const randomIndex = Math.floor(Math.random() * availableMusic.length);
                        musicIdToUse = availableMusic[randomIndex]._id;
                        console.log('Selected random music:', availableMusic[randomIndex].name);
                    }
                } catch (error) {
                    console.warn('Failed to fetch available music, proceeding without music:', error);
                }
            }
            
            // Generate video using workspace API
            generatePayload = {
                voiceoverUrl: onboardingVoiceoverUrl,
                cuts: onboardingCuts.filter(cut => !hiddenCuts.has(cut._id)),
                musicId: musicIdToUse,
                fontId: selectedFontId || '',
                hook: onboardingScript.hook || ''
            };
            if (forceFillWithTextScreens) generatePayload.fillWithTextScreens = true;
            const response = await api.generateWorkspaceVideo(currentWorkspace._id, onboardingScript._id, generatePayload);
            
            // Start polling for video generation status
            const pollVideoStatus = async () => {
                try {
                    const statusResponse = await api.getWorkspaceVideoStatus(currentWorkspace._id, onboardingScript._id);
                    
                    const { status, progress, videoUrl } = statusResponse.data;
                    console.log(`[ONBOARDING VIDEO] Status check: ${status}, progress: ${progress}%, videoUrl: ${videoUrl ? 'exists' : 'none'}`);
                    
                    setOnboardingVideoProgress(progress || 0);
                    
                    if ((status === 'draft' && videoUrl) || status === 'error') {
                        if (status === 'draft' && videoUrl) {
                            console.log(`[ONBOARDING VIDEO] Generation complete! Setting video URL: ${videoUrl}`);
                            setOnboardingVideoUrl(videoUrl);
                            setOnboardingVideoProgress(100);
                            trackEvent('onboarding_video_generated', { 
                                workspaceId: currentWorkspace._id,
                                scriptId: onboardingScript._id
                            });
                            if (typeof playCompletionSound === 'function') playCompletionSound('video_merra');
                            if (typeof triggerCompletionGlow === 'function') triggerCompletionGlow(['#f093fb', '#f5576c']);
                        } else if (status === 'error') {
                            console.log(`[ONBOARDING VIDEO] Generation failed with error status`);
                        }
                        setIsGeneratingVideoOnboarding(false);
                    } else {
                        // Continue polling
                        setTimeout(pollVideoStatus, 2000);
                    }
                } catch (error) {
                    console.error('Error polling video status:', error);
                    setIsGeneratingVideoOnboarding(false);
                    setOnboardingVideoProgress(0);
                }
            };
            
            // Start polling after a short delay
            setTimeout(pollVideoStatus, 1000);
            
        } catch (error) {
            console.error('Error generating video:', error);
            setIsGeneratingVideoOnboarding(false);
            setOnboardingVideoProgress(0);
        }
    };

    const confirmOnboardingInsufficientCutsOptimizeAndFill = () => {
        setShowInsufficientCutsModal(false);
        generateVideo(true, false);
    };
    const confirmOnboardingInsufficientCutsRepeatedCuts = () => {
        setShowInsufficientCutsModal(false);
        generateVideo(false, true);
    };

    // Auto-generate video when arriving at step 6 (Generate Video)
    useEffect(() => {
        console.log('Video generation useEffect triggered:', {
            step,
            hasVideoUrl: !!onboardingVideoUrl,
            isGenerating: isGeneratingVideoOnboarding,
            hasVoiceover: !!onboardingVoiceoverUrl,
            cutsCount: onboardingCuts.length,
            shouldGenerate: step === 7 && !onboardingVideoUrl && !isGeneratingVideoOnboarding && onboardingVoiceoverUrl && onboardingCuts.length > 0
        });
        
        if (step === 7 && !onboardingVideoUrl && !isGeneratingVideoOnboarding && onboardingVoiceoverUrl && onboardingCuts.length > 0) {
            console.log('Auto-generating video on step 6...');
            generateVideo();
        }
    }, [step, onboardingVideoUrl, isGeneratingVideoOnboarding, onboardingVoiceoverUrl, onboardingCuts.length, generateVideo]);
    

    const saveVideo = async () => {
        if (!onboardingScript || !onboardingVideoUrl) return;
        
        try {
            setIsDownloadingVideoOnboarding(true);
            // First download the video
            await handleOnboardingVideoDownload();
            setIsDownloadingVideoOnboarding(false);
            
            setIsSavingVideoOnboarding(true);
            // Then publish it to generated videos
            await api.publishWorkspaceVideo(currentWorkspace._id, onboardingScript._id, {
                videoUrl: onboardingVideoUrl
            });
            trackEvent('onboarding_video_saved');
            setIsSavingVideoOnboarding(false);
            
        } catch (error) {
            console.error('Error saving video:', error);
            setIsDownloadingVideoOnboarding(false);
            setIsSavingVideoOnboarding(false);
        }
    };

    const handleOnboardingVideoDownload = async () => {
        if (!onboardingVideoUrl) return;
        
        try {
            celebrateVideoDownload();
            const response = await fetch(onboardingVideoUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `onboarding-video-${Date.now()}.mp4`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error downloading video:', error);
        }
    };

    const handleImportInstagramVideosOnboarding = async () => {
        const username = instagramHandle.replace('@', '').trim();
        
        if (!username) {
            setOnboardingErrors(prev => [...prev, 'Please enter an Instagram handle first']);
            return;
        }
        
        if (!currentWorkspace?._id) {
            setOnboardingErrors(prev => [...prev, 'Workspace not found']);
            return;
        }
        
        const videoCount = 1;//10;
        setImportingInstagram(true);
        setInstagramImportProgress({ progress: 0, imported: 0, total: 0 });
        setInstagramImportSuccess(null);
        setOnboardingErrors([]);
        
        try {
            // Start the import (returns immediately)
            const response = await api.importInstagramVideos(currentWorkspace._id, username, videoCount);
            
            if (response.data.status !== 'started') {
                throw new Error('Failed to start Instagram import');
            }
            
            // Poll for status updates
            const pollInterval = setInterval(async () => {
                try {
                    const statusResponse = await api.getInstagramImportStatus(currentWorkspace._id);
                    const status = statusResponse.data;
                    
                    setInstagramImportProgress({
                        progress: status.progress || 0,
                        imported: status.imported || 0,
                        total: status.total || 0
                    });

                    if (status.status === 'none') {
                        clearInterval(pollInterval);
                        setImportingInstagram(false);
                        setOnboardingErrors(prev => [...prev, 'Import status not found']);
                        return;
                    }
                    
                    if (status.status === 'completed') {
                        clearInterval(pollInterval);
                        setImportingInstagram(false);
                        
                        const { imported = 0, total, errors: importErrors } = status;
                        
                        if (imported > 0) {
                            // Show success message immediately so it's visible even if refresh fails
                            setInstagramImportSuccess(`${imported} video${imported !== 1 ? 's' : ''} imported from @${username}.`);
                            trackEvent('onboarding_instagram_import_completed', {
                                imported,
                                total,
                                username
                            });
                            // Refresh videos list and start cuts polling (non-blocking for success message)
                            try {
                                const videosResponse = await api.getWorkspaceVideos(currentWorkspace._id);
                                const raw = videosResponse.data?.items ?? videosResponse.data;
                                const newVideos = Array.isArray(raw) ? raw : [];
                                setOnboardingVideos(newVideos);
                                if (newVideos.length > 0) {
                                    startCutsPolling(newVideos);
                                }
                            } catch (refreshErr) {
                                console.error('Error refreshing videos after Instagram import:', refreshErr);
                            }
                        } else {
                            setOnboardingErrors(prev => [...prev, status.error || 'No videos were imported']);
                        }
                    } else if (status.status === 'error') {
                        clearInterval(pollInterval);
                        setImportingInstagram(false);
                        setOnboardingErrors(prev => [...prev, `Instagram import failed: ${status.error || 'Unknown error'}`]);
                    }
                } catch (pollError) {
                    console.error('Error polling Instagram import status:', pollError);
                }
            }, 2000);
            
            // Timeout after 5 minutes
            setTimeout(() => {
                clearInterval(pollInterval);
                setImportingInstagram(prev => {
                    if (prev) {
                        setOnboardingErrors(errors => [...errors, 'Instagram import is taking longer than expected. Please check back later.']);
                        return false;
                    }
                    return prev;
                });
            }, 5 * 60 * 1000);
            
        } catch (error) {
            console.error('Error starting Instagram import:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Failed to start Instagram import';
            setOnboardingErrors(prev => [...prev, `Instagram import failed: ${errorMessage}`]);
            setImportingInstagram(false);
        }
    };

        
    // Helper function to get step name for tracking
    const getStepName = (stepNumber) => {
        const stepNames = [
            'referral_source',
            'welcome',
            'profile',
            'script_generation',
            'voice_choice',
            'video_upload',
            'filter_cuts',
            'video_generation',
            'completion'
        ];
        return stepNames[stepNumber] || 'unknown';
    };
    
    // Auto-generation triggers
    const handleNextWithAutoGeneration = async () => {
        // Track step navigation
        trackEvent('onboarding_step_next', {
            fromStep: step,
            toStep: step + 1,
            stepName: getStepName(step),
            workspaceId: currentWorkspace?._id
        });
        
        if (step === 3) {
            // Auto-generate voiceover with Brian when leaving script step
            if (onboardingScript && (!onboardingVoiceoverUrl || scriptModified)) {
                const tiffanyVoiceId = 'nPczCjzI2devNBz1zQrb'; // Brian
                setSelectedDefaultVoice(tiffanyVoiceId);
                setUseClonedVoice(false);
                
                // Start voiceover generation in background
                setGeneratingVoiceId(tiffanyVoiceId);
                setIsGeneratingVoiceoverOnboarding(true);
                (async () => {
                    try {
                        if (onboardingVoiceoverUrl) requestVoiceoverAutoplayRef.current = true;
                        // If script was modified, save the changes to database first
                        if (scriptModified) {
                            console.log('Saving script changes before voiceover generation...');
                            await api.updateWorkspaceScriptContent(
                                currentWorkspace._id, 
                                onboardingScript._id, 
                                'main', 
                                onboardingScript.content
                            );
                        }
                        
                        const response = await api.studioGenerateVoiceover(onboardingScript._id, null, tiffanyVoiceId);
                        setOnboardingVoiceoverUrl(response.data.voiceoverUrl);
                        setCurrentVoiceoverVoiceId(tiffanyVoiceId);
                        setCurrentVoiceoverVoiceType('default');
                        trackEvent('onboarding_voiceover_auto_generated', { 
                            voiceType: 'default',
                            voiceId: tiffanyVoiceId,
                            voiceName: 'Tiffany Natural'
                        });
                    } catch (error) {
                        console.error('Error auto-generating voiceover:', error);
                    } finally {
                        setIsGeneratingVoiceoverOnboarding(false);
                        setGeneratingVoiceId(null);
                        setScriptModified(false); // Reset the modified flag after generation
                        setIsEditingScript(false); // Return to highlighted view
                    }
                })();
            }
            onNext();
        } else if (step === 4) {
            // After voice choice, proceed to voiceover generation step
            onNext();
        } else {
            // For other steps, just proceed normally
            onNext();
        }
    };

    const renderStep = () => {
        switch (step) {
            case 0:
                return (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        maxWidth: '600px',
                        margin: '0 auto'
                    }}>
                        <h1 
                        className={darkMode ? "gradient-text-dark" : "gradient-text"}
                        style={{
                            fontSize: screenWidth <= 600 ? '20px' : '24px',
                            marginBottom: '20px',
                            marginTop: '30px',
                        }}>
                            How did you discover Merra AI?
                        </h1>
                        
                        {/* <p style={{
                            fontSize: screenWidth <= 600 ? '16px' : '18px',
                            lineHeight: '1.6',
                            marginBottom: '30px',
                            color: darkMode ? '#ccc' : '#666'
                        }}>
                            This helps us understand how you found Merra AI!
                        </p> */}

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: /*screenWidth <= 600 ? '1fr' : */'repeat(2, 1fr)',
                            gap: '12px',
                            marginBottom: '30px'
                        }}>
                            {referralOptions.map((option) => (
                                <button
                                    key={option}
                                    onClick={() => {
                                        setReferralSource(option);
                                        trackEvent('onboarding_referral_source_selected', {
                                            referralSource: option,
                                            workspaceId: currentWorkspace?._id
                                        });
                                    }}
                                    style={{
                                        padding: '16px 24px',
                                        backgroundColor: referralSource === option ? '#e1f679' : (darkMode ? '#2d3748' : '#f8f9fa'),
                                        color: referralSource === option ? 'black' : (darkMode ? '#fff' : '#333'),
                                        border: `2px solid ${referralSource === option ? '#e1f679' : (darkMode ? '#444' : '#ddd')}`,
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        textAlign: 'center',
                                        transition: 'all 0.2s ease',
                                        fontWeight: referralSource === option ? '600' : '400'
                                    }}
                                >
                                    {option}
                                </button>
                            ))}
                        </div>

                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center',
                            flexWrap: 'wrap'
                        }}>
                            <button
                                onClick={() => {
                                    trackEvent('onboarding_step_next', {
                                        fromStep: step,
                                        toStep: step + 1,
                                        stepName: getStepName(step),
                                        referralSource: referralSource || 'not_selected',
                                        workspaceId: currentWorkspace?._id
                                    });
                                    onNext();
                                }}
                                disabled={!referralSource}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: referralSource ? '#e1f679' : '#ccc',
                                    color: referralSource ? 'black' : 'white',
                                    fontWeight: 'bold',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: referralSource ? 'pointer' : 'not-allowed',
                                    fontSize: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                Next
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                );

            case 1:
                return (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        maxWidth: '800px',
                        margin: '0 auto'
                    }}>
                        <h1 
                        className={darkMode ? "gradient-text-dark" : "gradient-text"}
                        style={{
                            fontSize: screenWidth <= 600 ? '20px' : '24px',
                            marginBottom: '20px',
                            marginTop: '30px',
                        }}>
                            Welcome to Merra AI, {(userName)?.split(' ')[0]}! 
                        </h1>
                        
                        <p style={{
                            fontSize: screenWidth <= 600 ? '16px' : '18px',
                            lineHeight: '1.6',
                            marginBottom: '30px',
                            color: darkMode ? '#ccc' : '#666'
                        }}>
                            Our AI turns your videos into short-form marketing videos with voiceover and storytelling in 2 minutes. 
                            {/* <br/>
                            For that, you can give us information, record your voice, upload videos and get your edit in the next 2 minutes. */}
                        </p>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: screenWidth <= 600 ? '1fr' : '1fr 1fr',
                            gap: '20px',
                            marginBottom: '30px',
                            textAlign: 'left'
                        }}>
                            <div style={{
                                padding: '20px',
                                backgroundColor: darkMode ? '#2A2B2F' : '#f8f9fa',
                                borderRadius: '8px',
                                border: `1px solid ${darkMode ? '#444' : '#e9ecef'}`
                            }}>
                                <h3 style={{
                                    color: darkMode ? '#fff' : '#333',
                                    marginBottom: '10px',
                                    fontSize: '18px'
                                }}>
                                    What you provide:
                                </h3>
                                <ul style={{
                                    color: darkMode ? '#ccc' : '#666',
                                    paddingLeft: '20px',
                                    margin: 0
                                }}>
                                    <li>Profile information</li>
                                    <li>Voice recording (optional)</li>
                                    <li>Raw videos</li>
                                </ul>
                            </div>
                            
                            <div style={{
                                padding: '20px',
                                backgroundColor: darkMode ? '#2A2B2F' : '#f8f9fa',
                                borderRadius: '8px',
                                border: `1px solid ${darkMode ? '#444' : '#e9ecef'}`
                            }}>
                                <h3 style={{
                                    color: darkMode ? '#fff' : '#333',
                                    marginBottom: '10px',
                                    fontSize: '18px'
                                }}>
                                    What you get:
                                </h3>
                                <ul style={{
                                    color: darkMode ? '#ccc' : '#666',
                                    paddingLeft: '20px',
                                    margin: 0
                                }}>
                                    <li>Marketing script</li>
                                    <li>AI Voiceover for script</li>
                                    <li>Short form video</li>
                                </ul>
                            </div>
                        </div>


                        <div 
                            style={{ 
                                position: 'relative', 
                                display: 'inline-block',
                                cursor: 'pointer'
                            }}
                            onClick={() => setIsVideoModalOpen(true)}
                        >
                            <video
                                src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/Example-New-GenerateVideoWithVoiceover-Merra-2.mp4"
                                style={{
                                    // width: '100%',
                                    maxWidth: '600px',
                                    height: '300px',
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                    marginBottom: '30px',
                                    pointerEvents: 'none', // Disable video controls in small version
                                }}
                                playsInline
                                muted
                                loop
                                autoPlay
                            >
                                <source src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/Example-New-GenerateVideoWithVoiceover-Merra-2.mp4" type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                            <div
                                style={{
                                position: 'absolute',
                                top: '10px',
                                left: '15px',
                                color: 'white',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                                pointerEvents: 'none', // lets clicks pass through to video controls
                                }}
                            >
                                Example
                            </div>
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -75%)',
                                    width: '80px',
                                    height: '80px',
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    pointerEvents: 'none',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                <Play 
                                    size={40} 
                                    color="white" 
                                    fill="white"
                                    style={{
                                        marginLeft: '4px' // Slight offset to center the play icon
                                    }}
                                />
                            </div>
                        </div>

                        {isVideoModalOpen && (
                            <div
                                style={{
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(0, 0, 0, 0.85)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 10000,
                                    padding: '20px',
                                }}
                                onClick={() => setIsVideoModalOpen(false)}
                            >
                                <div
                                    style={{
                                        position: 'relative',
                                        maxWidth: '90vw',
                                        maxHeight: '85vh',
                                        width: 'auto',
                                        height: 'auto',
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <button
                                        onClick={() => setIsVideoModalOpen(false)}
                                        style={{
                                            position: 'absolute',
                                            top: '-55px',
                                            right: '0',
                                            background: 'rgba(255, 255, 255, 0.2)',
                                            border: 'none',
                                            borderRadius: '50%',
                                            width: '40px',
                                            height: '40px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            color: 'white',
                                            zIndex: 10001,
                                            transition: 'background 0.2s ease',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                                        }}
                                    >
                                        <X size={24} />
                                    </button>
                                    <video
                                        src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/Example-New-GenerateVideoWithVoiceover-Merra-2.mp4"
                                        controls
                                        autoPlay
                                        style={{
                                            maxWidth: '90vw',
                                            maxHeight: '90vh',
                                            width: 'auto',
                                            height: 'auto',
                                            borderRadius: '12px',
                                            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                        }}
                                        playsInline
                                    >
                                        <source src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/Example-New-GenerateVideoWithVoiceover-Merra-2.mp4" type="video/mp4" />
                                        Your browser does not support the video tag.
                                    </video>
                                </div>
                            </div>
                        )}

                        {showInsufficientCutsModal && (
                            <div
                                style={{
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 10000,
                                    padding: '20px',
                                }}
                                onClick={() => { setShowInsufficientCutsModal(false); }}
                            >
                                <div
                                    style={{
                                        backgroundColor: darkMode ? '#1e1e1e' : '#fff',
                                        borderRadius: '12px',
                                        padding: '24px',
                                        maxWidth: '440px',
                                        textAlign: 'center',
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <h3 style={{ marginBottom: '12px', color: darkMode ? '#fff' : '#333' }}>
                                        Not enough footage for this video
                                    </h3>
                                    <p style={{ marginBottom: '24px', color: '#666', fontSize: '16px', lineHeight: '1.5', textAlign: 'left' }}>
                                        How should Merra AI complete it?
                                    </p>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'stretch' }}>
                                        <button
                                            className="main-button"
                                            onClick={confirmOnboardingInsufficientCutsOptimizeAndFill}
                                            style={{ flex: 1, width: '100%' }}
                                        >
                                            Fill with text screens
                                        </button>
                                        <button
                                            className="tertiary-button"
                                            onClick={confirmOnboardingInsufficientCutsRepeatedCuts}
                                            style={{ flex: 1, justifyContent: 'center', textAlign: 'center', fontWeight: 'bold' }}
                                        >
                                            Use repeated cuts
                                        </button>
                                        <button
                                            onClick={() => setShowInsufficientCutsModal(false)}
                                            style={{ backgroundColor: 'transparent', color: '#666', border: '1px solid #ccc', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
                                        >
                                            Cancel generation
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center',
                            flexWrap: 'wrap'
                        }}>
                            <button
                                onClick={() => {
                                    trackEvent('onboarding_skipped', {
                                        step: step,
                                        stepName: getStepName(step),
                                        skipType: 'tutorial',
                                        workspaceId: currentWorkspace?._id
                                    });
                                    onSkip();
                                }}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: 'transparent',
                                    color: darkMode ? '#ccc' : '#666',
                                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <SkipForward size={16} />
                                Skip Tutorial
                            </button>
                            
                            <button
                                onClick={() => {
                                    trackEvent('onboarding_step_next', {
                                        fromStep: step,
                                        toStep: step + 1,
                                        stepName: getStepName(step),
                                        workspaceId: currentWorkspace?._id
                                    });
                                    onNext();
                                }}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: '#e1f679',
                                    color: 'black',
                                    fontWeight: 'bold',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                Get Started
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div style={{
                        padding: '40px 20px',
                        maxWidth: '600px',
                        margin: '0 auto',
                        position: 'relative'
                    }}>
                        {onPrevious && (
                            <button
                                onClick={onPrevious}
                                style={{
                                    position: 'absolute',
                                    top: '20px',
                                    left: '20px',
                                    padding: '8px 16px',
                                    backgroundColor: 'transparent',
                                    color: darkMode ? '#ccc' : '#666',
                                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    zIndex: 10
                                }}
                            >
                                <ChevronLeft size={14} />
                                Back
                            </button>
                        )}
                        
                        <h2
                        className={darkMode ? "gradient-text-dark" : "gradient-text"}
                        style={{
                            fontSize: screenWidth <= 600 ? '20px' : '24px',
                            marginBottom: '30px',
                            marginTop: '30px',
                            textAlign: 'center'
                        }}>
                            Let's make a video with this
                        </h2>

                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '30px',
                            marginBottom: '40px'
                        }}>
                            <div style={{
                                display: 'flex',
                                gap: '15px',
                                alignItems: 'flex-start'
                            }}>
                                <div style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    backgroundColor: '#e1f679',
                                    color: 'black',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    fontSize: '16px',
                                    flexShrink: 0
                                }}>
                                    1
                                </div>
                                <div style={{ flex: 1 }}>
                                    <p style={{
                                        color: darkMode ? '#e2e8f0' : '#333',
                                        fontSize: '16px',
                                        lineHeight: '1.6',
                                        margin: 0,
                                        marginBottom: '10px'
                                    }}>
                                        Short bio, company mission, target
                                    </p>
                                    <p style={{
                                        color: darkMode ? '#a0aec0' : '#666',
                                        fontSize: '14px',
                                        margin: '0 0 15px 0',
                                        fontStyle: 'italic'
                                    }}>
                                        Don't worry, we'll keep that private and only use it to generate your video.
                                    </p>
                                    <textarea
                                        value={onboardingProfile.companyPurpose || ''}
                                        onChange={(e) => setOnboardingProfile(prev => ({
                                            ...prev,
                                            companyPurpose: e.target.value
                                        }))}
                                        placeholder="Tell us about your company, mission, and target audience... (If you don’t have a company, tell us about yourself)"
                                        style={{
                                            width: '100%',
                                            minHeight: '100px',
                                            padding: '12px',
                                            border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                            borderRadius: '8px',
                                            backgroundColor: darkMode ? '#1a202c' : '#fff',
                                            color: darkMode ? '#e2e8f0' : '#333',
                                            fontFamily: 'inherit',
                                            boxSizing: 'border-box',
                                            resize: 'vertical'
                                        }}
                                    />
                                    <div style={{
                                        minHeight: '24px',
                                        marginTop: '8px'
                                    }}>
                                        {(onboardingProfile.companyPurpose || '').length > 10 && (
                                            <p style={{
                                                color: darkMode ? '#90ee90' : '#28a745',
                                                fontSize: '14px',
                                                margin: 0,
                                                fontStyle: 'italic'
                                            }}>
                                                Good start, keep going!
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div style={{
                                display: 'flex',
                                gap: '15px',
                                alignItems: 'flex-start'
                            }}>
                                <div style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    backgroundColor: '#e1f679',
                                    color: 'black',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    fontSize: '16px',
                                    flexShrink: 0
                                }}>
                                    2
                                </div>
                                <div style={{ flex: 1 }}>
                                    <p style={{
                                        color: darkMode ? '#e2e8f0' : '#333',
                                        fontSize: '16px',
                                        lineHeight: '1.6',
                                        margin: 0,
                                        marginBottom: '10px'
                                    }}>
                                        Drop 10 clips we can use, can be anything, don't overthink it
                                    </p>
                                    <p style={{
                                        color: darkMode ? '#a0aec0' : '#666',
                                        fontSize: '14px',
                                        margin: '0 0 15px 0',
                                        fontStyle: 'italic'
                                    }}>
                                        Works better with vertical format, raw content, no slow-motion, no text.
                                    </p>
                                    {showMobilePickerNotice && (
                                        <div
                                            role="dialog"
                                            aria-modal="true"
                                            style={{
                                                position: 'fixed',
                                                inset: 0,
                                                zIndex: 10000,
                                                backgroundColor: 'rgba(0,0,0,0.55)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: '16px'
                                            }}
                                            onClick={() => setShowMobilePickerNotice(false)}
                                        >
                                            <div
                                                style={{
                                                    width: 'min(520px, 100%)',
                                                    background: darkMode ? '#0b1220' : '#ffffff',
                                                    borderRadius: '14px',
                                                    padding: '16px',
                                                    border: darkMode ? '1px solid rgba(148, 163, 184, 0.22)' : '1px solid rgba(15, 23, 42, 0.12)',
                                                    boxShadow: darkMode
                                                        ? '0 18px 60px rgba(0,0,0,0.55)'
                                                        : '0 18px 60px rgba(15, 23, 42, 0.22)'
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <div style={{ fontSize: '15px', fontWeight: 700, color: darkMode ? '#fff' : '#0f172a', marginBottom: '8px' }}>
                                                    Mobile upload note
                                                </div>
                                                <div style={{ fontSize: '13px', lineHeight: 1.5, color: darkMode ? '#cbd5e1' : '#334155' }}>
                                                    Mobile browsers can take time to load/export videos from your gallery before they appear here.
                                                    If it looks stuck, give it a moment — or select fewer videos at once.
                                                </div>

                                                <label style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '12px', fontSize: '13px', color: darkMode ? '#cbd5e1' : '#334155' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={mobilePickerNoticeDismissed}
                                                        onChange={(e) => {
                                                            const next = !!e.target.checked;
                                                            setMobilePickerNoticeDismissed(next);
                                                            try {
                                                                localStorage.setItem('onboarding_mobile_picker_notice_dismissed', next ? '1' : '0');
                                                            } catch {
                                                                // ignore
                                                            }
                                                        }}
                                                    />
                                                    Don’t show again
                                                </label>

                                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '14px' }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowMobilePickerNotice(false)}
                                                        style={{
                                                            padding: '10px 12px',
                                                            borderRadius: '10px',
                                                            border: darkMode ? '1px solid rgba(148, 163, 184, 0.25)' : '1px solid rgba(15, 23, 42, 0.12)',
                                                            background: 'transparent',
                                                            color: darkMode ? '#e2e8f0' : '#0f172a',
                                                            cursor: 'pointer',
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setShowMobilePickerNotice(false);
                                                            onboardingFileInputRef.current?.click();
                                                        }}
                                                        style={{
                                                            padding: '10px 12px',
                                                            borderRadius: '10px',
                                                            border: 'none',
                                                            background: 'linear-gradient(90deg, #AB5898 0%, #5C4BD3 100%)',
                                                            color: '#fff',
                                                            cursor: 'pointer',
                                                            fontWeight: 700
                                                        }}
                                                    >
                                                        Continue
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div
                                        className={`upload-area ${isDragging ? 'dragging' : ''}`}
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            setIsDragging(true);
                                        }}
                                        onDragLeave={() => setIsDragging(false)}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            setIsDragging(false);
                                            handleFileUpload(e.dataTransfer.files);
                                        }}
                                        style={{
                                            border: `2px dashed ${isDragging ? '#0066cc' : (darkMode ? '#444' : '#ddd')}`,
                                            borderRadius: '12px',
                                            padding: '30px 20px',
                                            textAlign: 'center',
                                            backgroundColor: isDragging ? (darkMode ? '#2A2B2F' : '#f8f9fa') : 'transparent',
                                            transition: 'all 0.2s ease',
                                            cursor: 'pointer',
                                            marginBottom: '10px'
                                        }}
                                    >
                                        <input
                                            ref={onboardingFileInputRef}
                                            type="file"
                                            multiple
                                            accept="video/*"
                                            onChange={(e) => handleFileUpload(e.target.files)}
                                            style={{ display: 'none' }}
                                            id="onboarding-file-input-step2"
                                        />
                                        <label
                                            role="button"
                                            tabIndex={0}
                                            onClick={openOnboardingFilePicker}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    openOnboardingFilePicker(e);
                                                }
                                            }}
                                            style={{
                                                cursor: 'pointer',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: '12px'
                                            }}
                                        >
                                            <Upload size={32} style={{ color: darkMode ? '#ccc' : '#666' }} />
                                            <span style={{
                                                fontSize: '16px',
                                                color: darkMode ? '#fff' : '#333',
                                                fontWeight: '500'
                                            }}>
                                                Drag & drop videos here or click to upload
                                                <br />
                                                <span style={{ fontSize: '12px', opacity: 0.7 }}>Up to 50 files at once, maximum size of 2GB each</span>
                                            </span>
                                        </label>
                                    </div>

                                    {/* Instagram Import Section */}
                                    <div style={{
                                        marginBottom: '30px',
                                        padding: '24px',
                                        backgroundColor: darkMode ? '#2d3748' : '#f8f9fa',
                                        borderRadius: '12px',
                                        border: `2px dashed ${darkMode ? '#4a5568' : '#e2e8f0'}`,
                                        textAlign: 'center',
                                        boxSizing: 'border-box'
                                    }}>
                                        <p style={{ 
                                            fontSize: '16px',
                                            color: darkMode ? '#fff' : '#333',
                                            fontWeight: '500',
                                            marginBottom: '15px', 
                                        }}>
                                            <Instagram size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                                            Or Import from Instagram
                                        </p>
                                        <p style={{ 
                                            fontSize: '14px', 
                                            color: darkMode ? '#ccc' : '#666', 
                                            marginBottom: '20px' 
                                        }}>
                                            Enter a public Instagram handle to import the last 10 videos.
                                        </p>
                                        <div style={{ 
                                            display: 'flex', 
                                            flexDirection: screenWidth <= 600 ? 'column' : 'row',
                                            gap: '10px', 
                                            justifyContent: 'center',
                                            alignItems: screenWidth <= 600 ? 'stretch' : 'center',
                                            maxWidth: '400px',
                                            margin: '0 auto'
                                        }}>
                                            <input
                                                type="text"
                                                placeholder="@username"
                                                value={instagramHandle}
                                                onChange={(e) => setInstagramHandle(e.target.value)}
                                                style={{
                                                    flex: screenWidth <= 600 ? 'none' : 1,
                                                    minWidth: 0,
                                                    width: screenWidth <= 600 ? '100%' : undefined,
                                                    padding: '12px 16px',
                                                    borderRadius: '8px',
                                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                                    backgroundColor: darkMode ? '#1a202c' : '#fff',
                                                    color: darkMode ? '#fff' : '#333',
                                                    fontSize: '14px',
                                                    outline: 'none',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                            <button
                                                onClick={handleImportInstagramVideosOnboarding}
                                                disabled={importingInstagram || !instagramHandle.trim()}
                                                style={{
                                                    padding: '12px 20px',
                                                    borderRadius: '8px',
                                                    backgroundColor: importingInstagram || !instagramHandle.trim() ? '#ccc' : '#e1f679',
                                                    color: 'black',
                                                    border: 'none',
                                                    cursor: importingInstagram || !instagramHandle.trim() ? 'not-allowed' : 'pointer',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px',
                                                    transition: 'all 0.2s',
                                                    boxSizing: 'border-box',
                                                    flexShrink: 0
                                                }}
                                            >
                                    {importingInstagram ? (
                                        <>
                                            <RefreshCw size={16} className="spin" />
                                            {instagramImportProgress.progress > 0 ? `${instagramImportProgress.progress}%` : 'Importing...'}
                                        </>
                                    ) : (
                                        <>
                                            <Download size={16} />
                                            Import
                                        </>
                                    )}
                                </button>
                            </div>
                            {importingInstagram && instagramImportProgress.total > 0 && (
                                <div style={{ marginTop: '15px', width: '100%', maxWidth: '400px', margin: '15px auto 0' }}>
                                    <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        fontSize: '12px', 
                                        color: darkMode ? '#ccc' : '#666',
                                        marginBottom: '5px'
                                    }}>
                                        <span>Importing videos...</span>
                                        <span>{instagramImportProgress.imported} / {instagramImportProgress.total}</span>
                                    </div>
                                    <div style={{ 
                                        width: '100%', 
                                        height: '6px', 
                                        backgroundColor: darkMode ? '#1a202c' : '#e2e8f0', 
                                        borderRadius: '3px',
                                        overflow: 'hidden'
                                    }}>
                                        <div 
                                            className="progress-bar-shimmer-script"
                                            style={{ 
                                                width: `${instagramImportProgress.progress}%`, 
                                                height: '100%', 
                                                backgroundColor: '#8b5cf6',
                                                transition: 'width 0.3s ease'
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                            {instagramImportSuccess && (
                                <div style={{ 
                                    marginTop: '15px', 
                                    maxWidth: '400px',
                                    margin: '15px auto 0'
                                }}>
                                    <div style={{ 
                                        padding: '10px 16px', 
                                        backgroundColor: darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)', 
                                        borderRadius: '8px',
                                        border: `1px solid ${darkMode ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.2)'}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        justifyContent: 'center'
                                    }}>
                                        <CheckCircle size={16} style={{ color: '#4CAF50' }} />
                                        <span style={{ color: darkMode ? '#81C784' : '#2E7D32', fontSize: '14px', fontWeight: '500' }}>
                                            {instagramImportSuccess}
                                        </span>
                                    </div>
                                    
                                </div>
                            )}
                            {onboardingErrors.length > 0 && (
                                            <div style={{ marginTop: '15px' }}>
                                                {onboardingErrors.map((err, i) => (
                                                    <p key={i} style={{ color: '#f44336', fontSize: '13px', margin: '4px 0' }}>{err}</p>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {Object.keys(uploadProgress).length > 0 && (
                                        <div style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: '15px',
                                            marginTop: '20px',
                                            marginBottom: '10px'
                                        }}>
                                            {Object.entries(uploadProgress).map(([fileName, progress], index) => {
                                                const fileIndex = index + 1;
                                                
                                                return (
                                                    <div
                                                        key={`upload-${fileName}`}
                                                        onMouseEnter={() => setHoveredUploadFile(fileName)}
                                                        onMouseLeave={() => setHoveredUploadFile(null)}
                                                        style={{
                                                            position: 'relative',
                                                            width: '50px',
                                                            height: '50px',
                                                            cursor: hoveredUploadFile === fileName ? 'pointer' : 'default'
                                                        }}
                                                        onClick={() => {
                                                            if (hoveredUploadFile === fileName) {
                                                                cancelUpload(fileName);
                                                            }
                                                        }}
                                                    >
                                                        <svg
                                                            width="50"
                                                            height="50"
                                                            style={{
                                                                transform: 'rotate(-90deg)',
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0
                                                            }}
                                                        >
                                                            <circle
                                                                cx="25"
                                                                cy="25"
                                                                r="20"
                                                                fill="none"
                                                                stroke={darkMode ? '#444' : '#e2e8f0'}
                                                                strokeWidth="4"
                                                            />
                                                            <circle
                                                                cx="25"
                                                                cy="25"
                                                                r="20"
                                                                fill="none"
                                                                stroke={progress < 100 ? '#4ecdc4' : '#4CAF50'}
                                                                strokeWidth="4"
                                                                strokeDasharray={`${2 * Math.PI * 20}`}
                                                                strokeDashoffset={`${2 * Math.PI * 20 * (1 - progress / 100)}`}
                                                                strokeLinecap="round"
                                                                style={{
                                                                    transition: 'stroke-dashoffset 0.3s ease'
                                                                }}
                                                            />
                                                        </svg>
                                                        
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '50%',
                                                            left: '50%',
                                                            transform: 'translate(-50%, -50%)',
                                                            fontSize: '16px',
                                                            fontWeight: 'bold',
                                                            color: darkMode ? '#fff' : '#333',
                                                            zIndex: 2
                                                        }}>
                                                            {fileIndex}
                                                        </div>
                                                        
                                                        {hoveredUploadFile === fileName && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: '50%',
                                                                left: '50%',
                                                                transform: 'translate(-50%, -50%)',
                                                                width: '50px',
                                                                height: '50px',
                                                                borderRadius: '50%',
                                                                backgroundColor: 'rgba(220, 53, 69, 0.9)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                zIndex: 3,
                                                                cursor: 'pointer'
                                                            }}>
                                                                <X size={20} color="white" />
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    
                                    {onboardingVideos.length > 0 && Object.keys(uploadProgress).length === 0 && (
                                        <div style={{ margin: '10px 0 0 0' }}>
                                            <p style={{
                                                color: darkMode ? '#4CAF50' : '#28a745',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                marginBottom: '8px'
                                            }}>
                                                {onboardingVideos.length} video{onboardingVideos.length !== 1 ? 's' : ''} successfully imported.
                                            </p>
                                            <div style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: '6px'
                                            }}>
                                                {onboardingVideos.map((video, index) => (
                                                    <div
                                                        key={video._id || index}
                                                        style={{
                                                            width: '40px',
                                                            height: '40px',
                                                            borderRadius: '6px',
                                                            overflow: 'hidden',
                                                            flexShrink: 0,
                                                            backgroundColor: darkMode ? '#2d3748' : '#e9ecef'
                                                        }}
                                                        title={video.name || video.title || `Video ${index + 1}`}
                                                    >
                                                        <video
                                                            src={video.url}
                                                            muted
                                                            playsInline
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                                display: 'block'
                                                            }}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {onboardingVideos.length > 0 && (
                                        <div style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: '6px',
                                            marginTop: '10px',
                                            justifyContent: 'center'
                                        }}>
                                            {onboardingVideos.slice(0, 12).map((video, index) => (
                                                <div
                                                    key={video._id || index}
                                                    style={{
                                                        width: '40px',
                                                        height: '60px',
                                                        borderRadius: '6px',
                                                        overflow: 'hidden',
                                                        flexShrink: 0,
                                                        backgroundColor: darkMode ? '#2d3748' : '#e9ecef'
                                                    }}
                                                    title={video.name || video.title || `Video ${index + 1}`}
                                                >
                                                    <video
                                                        src={video.url}
                                                        muted
                                                        playsInline
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            display: 'block'
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                            {onboardingVideos.length > 12 && (
                                                <span style={{ fontSize: '12px', color: darkMode ? '#a0aec0' : '#666', alignSelf: 'center' }}>
                                                    +{onboardingVideos.length - 12} more
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={{
                                display: 'flex',
                                gap: '15px',
                                alignItems: 'flex-start',
                                width: '100%',
                                minWidth: 0
                            }}>
                                <div style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    backgroundColor: '#e1f679',
                                    color: 'black',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    fontSize: '16px',
                                    flexShrink: 0
                                }}>
                                    3
                                </div>
                                <div style={{ flex: 1, minWidth: 0, width: '100%', overflow: 'hidden' }}>
                                    <p style={{
                                        color: darkMode ? '#e2e8f0' : '#333',
                                        fontSize: '16px',
                                        lineHeight: '1.6',
                                        margin: 0,
                                        marginBottom: '10px'
                                    }}>
                                        Clone your voice <span style={{ color: darkMode ? '#888' : '#666', fontSize: '14px' }}>(optional)</span>
                                    </p>
                                    <p style={{
                                        color: darkMode ? '#a0aec0' : '#666',
                                        fontSize: '14px',
                                        margin: '0 0 20px 0',
                                        fontStyle: 'italic'
                                    }}>
                                        Record 20 seconds (or more), tell us about your day
                                    </p>
                                    <div style={{
                                        marginTop: '-10px',
                                        marginBottom: '20px',
                                        padding: '12px',
                                        backgroundColor: darkMode ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.15)',
                                        border: `1px solid ${darkMode ? 'rgba(255, 193, 7, 0.3)' : 'rgba(255, 193, 7, 0.4)'}`,
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        color: darkMode ? '#ffc107' : '#856404',
                                        lineHeight: '1.5'
                                    }}>
                                        <strong>💡 Tip:</strong> Use the tone you want your AI voice to have. Avoid pausing or long breaths, or the AI will learn that too.
                                    </div>

                                    
                                    {/* Recording controls */}
                                    {/* Only show recording buttons if there's no cloned voice */}
                                    {!onboardingVoiceId && (
                                        <div style={{ 
                                            display: 'flex', 
                                            flexDirection: screenWidth <= 480 ? 'column' : 'row',
                                            gap: '10px', 
                                            marginBottom: '20px',
                                            width: '100%'
                                        }}>
                                            <button
                                                onClick={isRecordingOnboarding ? stopRecording : startRecording}
                                                style={{ 
                                                    flex: screenWidth <= 480 ? 'none' : 1,
                                                    width: screenWidth <= 480 ? '100%' : 'auto',
                                                    padding: '12px',
                                                    backgroundColor: (isRecordingOnboarding ? (isSavingRecordingOnboarding ? '#ccc' : '#dc3545') : '#e1f679'),
                                                    color: (isRecordingOnboarding ? 'white' : 'black'),
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px',
                                                    fontWeight: 'bold',
                                                    fontSize: '14px',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {isRecordingOnboarding ? (
                                                    <>
                                                        {isSavingRecordingOnboarding ? <RefreshCw className="spin" size={16} /> : <Square size={16} />}
                                                        {isSavingRecordingOnboarding ? 'Saving...' : `Stop Recording (${formatTime(recordingDurationOnboarding)})`}
                                                    </>
                                                ) : (
                                                    <>
                                                        <Mic size={16} />
                                                        {onboardingRecordings.length > 0 ? 'Record Again' : 'Start Recording'}
                                                    </>
                                                )}
                                            </button>
                                            
                                            <input
                                                ref={audioImportRef}
                                                type="file"
                                                accept="audio/*,video/*"
                                                onChange={handleAudioImport}
                                                style={{ display: 'none' }}
                                            />
                                            <button
                                                onClick={() => audioImportRef.current?.click()}
                                                style={{ 
                                                    backgroundColor: isImportingAudio ? '#ccc' : '#e1f679',
                                                    color: isImportingAudio ? 'white' : 'black',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    cursor: isImportingAudio ? 'not-allowed' : 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px',
                                                    padding: '12px',
                                                    width: screenWidth <= 480 ? '100%' : 'auto',
                                                    minWidth: screenWidth <= 480 ? 'auto' : '120px',
                                                    fontWeight: 'bold',
                                                    fontSize: '14px',
                                                    whiteSpace: 'nowrap'
                                                }}
                                                disabled={isImportingAudio}
                                            >
                                                {isImportingAudio ? (
                                                    <>
                                                        <RefreshCw className="spin" size={16} />
                                                        Importing...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Upload size={16} />
                                                        {onboardingRecordings.length > 0 ? 'Import again' : 'Import voice file (audio or video)'}
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}

                                    {isGeneratingVoiceOnboarding && (
                                        <div style={{ marginBottom: '15px', textAlign: 'center' }}>
                                            <div style={{
                                                width: '30px',
                                                height: '30px',
                                                background: '#4ecdc4',
                                                borderRadius: '50%',
                                                animation: 'pulse 2s infinite',
                                                margin: '0 auto 10px'
                                            }} />
                                            <p style={{ 
                                                color: darkMode ? '#ccc' : '#666', 
                                                margin: 0,
                                                fontSize: '14px'
                                            }}>
                                                Creating Your AI Voice...
                                            </p>
                                        </div>
                                    )}

                                    {/* Recording list */}
                                    {onboardingRecordings.length > 0 && (
                                        <div style={{ marginBottom: '20px' }}>
                                            {onboardingRecordings.map((recording, index) => (
                                                <div
                                                    key={recording._id || index}
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '8px',
                                                        padding: '8px',
                                                        backgroundColor: darkMode ? '#2d3748' : '#f8f9fa',
                                                        borderRadius: '4px',
                                                        marginBottom: '12px',
                                                        border: `1px solid ${darkMode ? '#444' : '#dfe1e5'}`
                                                    }}
                                                >
                                                    <div style={{
                                                        display: 'flex',
                                                        flexDirection: screenWidth <= 480 ? 'column' : 'row',
                                                        alignItems: screenWidth <= 480 ? 'stretch' : 'center', 
                                                        gap: '12px',
                                                        width: '100%',
                                                        minWidth: 0
                                                    }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '12px',
                                                            flex: screenWidth <= 480 ? 'none' : 1,
                                                            minWidth: 0,
                                                            width: screenWidth <= 480 ? '100%' : 'auto'
                                                        }}>
                                                            <span style={{ 
                                                                color: darkMode ? '#ccc' : '#666',
                                                                fontSize: '14px',
                                                                fontWeight: '500',
                                                                whiteSpace: 'nowrap',
                                                                flexShrink: 0
                                                            }}>
                                                                Audio {index + 1}
                                                            </span>
                                                            {/* Simple audio player */}
                                                            <audio 
                                                                controls
                                                                style={{ 
                                                                    flex: 1,
                                                                    // minWidth: screenWidth <= 480 ? '200px' : '150px',
                                                                    width: screenWidth <= 480 ? '100%' : 'auto',
                                                                    height: '32px'
                                                                }}
                                                                controlsList="nodownload"
                                                                onError={(e) => {
                                                                    console.error('Audio playback error:', e.target.error);
                                                                    console.log('Audio URL:', recording.url);
                                                                }}
                                                                onLoadedMetadata={(e) => {
                                                                    console.log('Audio metadata loaded, duration:', e.target.duration);
                                                                    if (isFinite(e.target.duration)) {
                                                                        setRecordingLength(prev => ({
                                                                            ...prev,
                                                                            [recording._id]: e.target.duration
                                                                        }));
                                                                    }
                                                                }}
                                                            >
                                                                <source 
                                                                    src={recording.url} 
                                                                    type={recording.url.includes('.mp3') ? 'audio/mpeg' : 
                                                                          recording.url.includes('.wav') ? 'audio/wav' :
                                                                          recording.url.includes('.m4a') ? 'audio/mp4' :
                                                                          recording.url.includes('.ogg') ? 'audio/ogg' : 'audio/webm'}
                                                                />
                                                                Your browser does not support the audio element.
                                                            </audio>
                                                        </div>
                                                        <button
                                                            onClick={() => handleDeleteRecording(recording._id)}
                                                            style={{
                                                                padding: '8px 12px', 
                                                                backgroundColor: '#dc3545',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: '4px',
                                                                fontSize: '0.8em',
                                                                whiteSpace: 'nowrap',
                                                                flexShrink: 0,
                                                                width: screenWidth <= 480 ? '100%' : 'auto'
                                                            }}
                                                        >
                                                            <Trash2 size={14} />
                                                            Delete
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {onboardingVoiceId && !isGeneratingVoiceOnboarding && (
                                        <div style={{
                                            padding: '12px',
                                            backgroundColor: darkMode ? '#1a5f1a' : '#d4edda',
                                            border: `1px solid ${darkMode ? '#2d7a2d' : '#c3e6cb'}`,
                                            borderRadius: '8px',
                                            color: darkMode ? '#90ee90' : '#155724',
                                            fontSize: '14px',
                                            textAlign: 'center',
                                            marginBottom: '15px'
                                        }}>
                                            ✓ Your AI Voice is ready!
                                        </div>
                                    )}

                                    {/* Clone voice button - only show if there's a recording and no cloned voice, or if there's a cloned voice (to restart) */}
                                    {((onboardingRecordings.length >= 1 && !onboardingVoiceId) || onboardingVoiceId) && (
                                        <button
                                            onClick={() => {
                                                if (onboardingVoiceId) {
                                                    // Restart: clear voice and recordings
                                                    setOnboardingVoiceId('');
                                                    setOnboardingRecordings([]);
                                                    setVoiceCloningError(null);
                                                } else {
                                                    // Create voice clone
                                                    handleVoiceCloning();
                                                }
                                            }}
                                            disabled={isGeneratingVoiceOnboarding || (onboardingRecordings.length < 1 && !onboardingVoiceId)}
                                            style={{ 
                                                width: '100%',
                                                padding: '12px',
                                                backgroundColor: (isGeneratingVoiceOnboarding || (onboardingRecordings.length < 1 && !onboardingVoiceId)) ? '#ccc' : '#e1f679',
                                                color: (isGeneratingVoiceOnboarding || (onboardingRecordings.length < 1 && !onboardingVoiceId)) ? 'white' : 'black',
                                                border: 'none',
                                                borderRadius: '8px',
                                                cursor: (isGeneratingVoiceOnboarding || (onboardingRecordings.length < 1 && !onboardingVoiceId)) ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px',
                                                fontWeight: 'bold',
                                                fontSize: '14px',
                                                marginBottom: '15px'
                                            }}
                                        >
                                            {isGeneratingVoiceOnboarding ? (
                                                <>
                                                    <RefreshCw className="spin" size={16} />
                                                    Generating Voice Clone...
                                                </>
                                            ) : (
                                                <>
                                                    {onboardingVoiceId ? (
                                                        <>
                                                            <RefreshCw size={16} />
                                                            Restart Voice Cloning
                                                        </>
                                                    ) : (
                                                        'Clone Voice'
                                                    )}
                                                </>
                                            )}
                                        </button>
                                    )}

                                    {voiceCloningError && !isGeneratingVoiceOnboarding && (
                                        <div style={{
                                            padding: '12px',
                                            backgroundColor: darkMode ? '#2d2419' : '#fff3cd',
                                            border: `1px solid ${darkMode ? '#4a3a1a' : '#ffeaa7'}`,
                                            borderRadius: '8px',
                                            color: darkMode ? '#d4a853' : '#856404',
                                            fontSize: '14px',
                                            marginTop: '10px'
                                        }}>
                                            {voiceCloningError}
                                            {voiceCloneUpgradeActions}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center',
                            flexWrap: 'wrap'
                        }}>
                            <button
                                onClick={() => {
                                    trackEvent('onboarding_skipped', {
                                        step: step,
                                        stepName: getStepName(step),
                                        skipType: 'onboarding',
                                        workspaceId: currentWorkspace?._id
                                    });
                                    onSkip();
                                }}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: 'transparent',
                                    color: darkMode ? '#ccc' : '#666',
                                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <SkipForward size={16} />
                                Skip Tutorial
                            </button>
                            
                            <button
                                onClick={() => {
                                    trackEvent('onboarding_step_next', {
                                        fromStep: step,
                                        toStep: step + 1,
                                        stepName: getStepName(step),
                                        hasCompanyPurpose: !!onboardingProfile.companyPurpose,
                                        videosCount: onboardingVideos.length,
                                        hasVoice: !!onboardingVoiceId,
                                        workspaceId: currentWorkspace?._id
                                    });
                                    if (onboardingProfile.companyPurpose) {
                                        onProfileSave();
                                    }
                                    onNext();
                                }}
                                disabled={!onboardingProfile.companyPurpose || onboardingVideos.length === 0}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: (!onboardingProfile.companyPurpose || onboardingVideos.length === 0) ? '#ccc' : '#e1f679',
                                    color: 'black',
                                    fontWeight: 'bold',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: (!onboardingProfile.companyPurpose || onboardingVideos.length === 0) ? 'not-allowed' : 'pointer',
                                    fontSize: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                Next <span style={{ fontSize: '12px', color: 'gray' }}>(Generate Script)</span>
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                );

            /* Original case 2 - commented out
            case 2:
                return (
                    <div style={{
                        padding: '40px 20px',
                        maxWidth: '600px',
                        margin: '0 auto',
                        position: 'relative'
                    }}>
                        {onPrevious && (
                            <button
                                onClick={onPrevious}
                                style={{
                                    position: 'absolute',
                                    top: '20px',
                                    left: '20px',
                                    padding: '8px 16px',
                                    backgroundColor: 'transparent',
                                    color: darkMode ? '#ccc' : '#666',
                                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    zIndex: 10
                                }}
                            >
                                <ChevronLeft size={14} />
                                Back
                            </button>
                        )}
                        
                        <h2
                        className={darkMode ? "gradient-text-dark" : "gradient-text"}
                        style={{
                            fontSize: screenWidth <= 600 ? '20px' : '24px',
                            marginBottom: '20px',
                            marginTop: '30px',
                            textAlign: 'center'
                        }}>
                            Tell us about your business
                        </h2>
                        
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '8px',
                                fontWeight: '600',
                                color: darkMode ? '#fff' : '#333'
                            }}>
                                Company Purpose <span style={{ color: '#AB5898' }}>*</span>
                            </label>
                            <textarea
                                value={onboardingProfile.companyPurpose}
                                onChange={(e) => setOnboardingProfile(prev => ({
                                    ...prev,
                                    companyPurpose: e.target.value
                                }))}
                                placeholder="Describe what your company does and its main purpose..."
                                style={{
                                    width: '100%',
                                    minHeight: '100px',
                                    padding: '12px',
                                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                    borderRadius: '8px',
                                    backgroundColor: darkMode ? '#1a202c' : '#fff',
                                    color: darkMode ? '#e2e8f0' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box',
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '8px',
                                fontWeight: '600',
                                color: darkMode ? '#fff' : '#333'
                            }}>
                                Target Audience
                            </label>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                <textarea
                                    value={onboardingProfile.targetAudience}
                                    onChange={(e) => setOnboardingProfile(prev => ({
                                        ...prev,
                                        targetAudience: e.target.value
                                    }))}
                                    placeholder="Who is your ideal customer? Include demographics, psychographics, pain points..."
                                    style={{
                                        flex: 1,
                                        minHeight: '80px',
                                        padding: '12px',
                                        border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                        borderRadius: '8px',
                                        backgroundColor: darkMode ? '#1a202c' : '#fff',
                                        color: darkMode ? '#e2e8f0' : '#333',
                                        fontFamily: 'inherit',
                                        boxSizing: 'border-box',
                                        position: 'relative'
                                    }}
                                />
                                <button
                                    onClick={() => !aiSuggestionLoading?.targetAudience && onAISuggestion('targetAudience')}
                                    disabled={aiSuggestionLoading?.targetAudience}
                                    style={{
                                        padding: '8px',
                                        backgroundColor: aiSuggestionLoading?.targetAudience ? '#ccc' : '#e1f679',
                                        color: 'black',
                                        fontWeight: 'bold',
                                        border: aiSuggestionLoading?.targetAudience ? '2px solid #e1f679' : 'none',
                                        borderRadius: '8px',
                                        cursor: aiSuggestionLoading?.targetAudience ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        fontSize: '12px',
                                        whiteSpace: 'nowrap',
                                        opacity: aiSuggestionLoading?.targetAudience ? 0.7 : 1,
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {aiSuggestionLoading?.targetAudience ? (
                                        <>
                                            <span className="spinner-border" style={{marginRight:'5px'}}/>
                                            Loading...
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles size={14} />
                                            AI suggestion
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '8px',
                                fontWeight: '600',
                                color: darkMode ? '#fff' : '#333'
                            }}>
                                Main Challenges & Solutions
                            </label>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                <textarea
                                    value={onboardingProfile.targetChallenge}
                                    onChange={(e) => setOnboardingProfile(prev => ({
                                        ...prev,
                                        targetChallenge: e.target.value
                                    }))}
                                    placeholder="What challenges does your target audience face and how do you solve them?"
                                    style={{
                                        flex: 1,
                                        minHeight: '80px',
                                        padding: '12px',
                                        border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                        borderRadius: '8px',
                                        backgroundColor: darkMode ? '#1a202c' : '#fff',
                                        color: darkMode ? '#e2e8f0' : '#333',
                                        fontFamily: 'inherit',
                                        boxSizing: 'border-box',
                                        position: 'relative'
                                    }}
                                />
                                <button
                                    onClick={() => !aiSuggestionLoading?.targetChallenge && onAISuggestion('targetChallenge')}
                                    disabled={aiSuggestionLoading?.targetChallenge}
                                    style={{
                                        padding: '8px',
                                        backgroundColor: aiSuggestionLoading?.targetChallenge ? '#ccc' : '#e1f679',
                                        color: 'black',
                                        fontWeight: 'bold',
                                        border: aiSuggestionLoading?.targetChallenge ? '2px solid #e1f679' : 'none',
                                        borderRadius: '8px',
                                        cursor: aiSuggestionLoading?.targetChallenge ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        fontSize: '12px',
                                        whiteSpace: 'nowrap',
                                        opacity: aiSuggestionLoading?.targetChallenge ? 0.7 : 1,
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {aiSuggestionLoading?.targetChallenge ? (
                                        <>
                                            <span className="spinner-border" style={{marginRight:'5px'}}/>
                                            Loading...
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles size={14} />
                                            AI suggestion
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div style={{ marginBottom: '30px' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '8px',
                                fontWeight: '600',
                                color: darkMode ? '#fff' : '#333'
                            }}>
                                Competitive Advantage
                            </label>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                <textarea
                                    value={onboardingProfile.competitors}
                                    onChange={(e) => setOnboardingProfile(prev => ({
                                        ...prev,
                                        competitors: e.target.value
                                    }))}
                                    placeholder="How are you different from competitors? What makes you unique?"
                                    style={{
                                        flex: 1,
                                        minHeight: '80px',
                                        padding: '12px',
                                        border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                        borderRadius: '8px',
                                        backgroundColor: darkMode ? '#1a202c' : '#fff',
                                        color: darkMode ? '#e2e8f0' : '#333',
                                        fontFamily: 'inherit',
                                        boxSizing: 'border-box',
                                        position: 'relative'
                                    }}
                                />
                                <button
                                    onClick={() => !aiSuggestionLoading?.competitors && onAISuggestion('competitors')}
                                    disabled={aiSuggestionLoading?.competitors}
                                    style={{
                                        padding: '8px',
                                        backgroundColor: aiSuggestionLoading?.competitors ? '#ccc' : '#e1f679',
                                        color: 'black',
                                        fontWeight: 'bold',
                                        border: aiSuggestionLoading?.competitors ? '2px solid #e1f679' : 'none',
                                        borderRadius: '8px',
                                        cursor: aiSuggestionLoading?.competitors ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        fontSize: '12px',
                                        whiteSpace: 'nowrap',
                                        opacity: aiSuggestionLoading?.competitors ? 0.7 : 1,
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {aiSuggestionLoading?.competitors ? (
                                        <>
                                            <span className="spinner-border" style={{marginRight:'5px'}}/>
                                            Loading...
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles size={14} />
                                            AI suggestion
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center',
                            flexWrap: 'wrap'
                        }}>
                            <button
                                onClick={() => {
                                    trackEvent('onboarding_skipped', {
                                        step: step,
                                        stepName: getStepName(step),
                                        skipType: 'onboarding',
                                        workspaceId: currentWorkspace?._id
                                    });
                                    onSkip();
                                }}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: 'transparent',
                                    color: darkMode ? '#ccc' : '#666',
                                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <SkipForward size={16} />
                                Skip Tutorial
                            </button>
                            
                            <button
                                onClick={async () => {
                                    await onProfileSave();
                                    trackEvent('onboarding_profile_completed', {
                                        hasCompanyPurpose: !!onboardingProfile.companyPurpose,
                                        hasTargetAudience: !!onboardingProfile.targetAudience,
                                        hasTargetChallenge: !!onboardingProfile.targetChallenge,
                                        hasCompetitors: !!onboardingProfile.competitors,
                                        workspaceId: currentWorkspace?._id
                                    });
                                    onNext(); // Just go to script generation step, don't auto-generate
                                }}
                                disabled={!onboardingProfile.companyPurpose}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: onboardingProfile.companyPurpose ? '#e1f679' : '#ccc',
                                    color: 'black',
                                    fontWeight: 'bold',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: onboardingProfile.companyPurpose ? 'pointer' : 'not-allowed',
                                    fontSize: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                Generate Video Script
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                );
            */

            case 3:
                return (
                    <div style={{
                        padding: '40px 20px',
                        maxWidth: '600px',
                        margin: '0 auto',
                        position: 'relative'
                    }}>
                        {onPrevious && (
                            <button
                                onClick={onPrevious}
                                style={{
                                    position: 'absolute',
                                    top: '20px',
                                    left: '20px',
                                    padding: '8px 16px',
                                    backgroundColor: 'transparent',
                                    color: darkMode ? '#ccc' : '#666',
                                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    zIndex: 10
                                }}
                            >
                                <ChevronLeft size={14} />
                                Back
                            </button>
                        )}
                        
                        <h2
                        className={darkMode ? "gradient-text-dark" : "gradient-text"}
                        style={{
                            fontSize: screenWidth <= 600 ? '20px' : '24px',
                            marginBottom: '30px',
                            marginTop: '30px',
                            textAlign: 'center'
                        }}>
                            Generate your video's voiceover:
                        </h2>

                        {/* Script Generation Animation */}
                        {isGeneratingScriptOnboarding ? (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px 20px',
                                position: 'relative',
                                minHeight: '300px'
                            }}>
                                {/* Animated mouse cursors */}
                                {isGeneratingScriptOnboarding && mousePositions.map((mouse, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            position: 'absolute',
                                            left: `${mouse.x}px`,
                                            top: `${mouse.y}px`,
                                            transition: 'left 0.1s linear, top 0.1s linear',
                                            zIndex: 10,
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        {/* Speech bubble */}
                                        <div style={{
                                            position: 'absolute',
                                            top: '20px',
                                            left: '20px',
                                            backgroundColor: mouse.color,
                                            padding: '6px 10px',
                                            borderRadius: '8px',
                                            fontSize: '11px',
                                            color: '#fff',
                                            whiteSpace: 'nowrap',
                                            boxShadow: `0 2px 8px ${mouse.color}80`,
                                            border: `1px solid ${mouse.color}`,
                                            fontWeight: '500'
                                        }}>
                                            {mouse.label}
                                            {/* Arrow pointing to top-left (towards cursor) */}
                                            {/* <div style={{
                                                position: 'absolute',
                                                top: '-6px',
                                                left: '10px',
                                                width: 0,
                                                height: 0,
                                                borderLeft: '6px solid transparent',
                                                borderRight: '6px solid transparent',
                                                borderBottom: `6px solid ${mouse.color}`
                                            }} /> */}
                                        </div>
                                        {/* Mouse cursor */}
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill={mouse.color}
                                            stroke={mouse.color}
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            style={{ 
                                                filter: `drop-shadow(0 2px 4px ${mouse.color}80)`,
                                                // transform: 'rotate(-45deg)'
                                            }}
                                        >
                                            <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
                                        </svg>
                                    </div>
                                ))}
                                
                                <div style={{
                                    width: '50px',
                                    height: '50px',
                                    background: '#4ecdc4',
                                    borderRadius: '50%',
                                    animation: 'pulse 2s infinite',
                                    margin: '0 auto 20px'
                                }} />
                                <p style={{ display: 'inline-block', color: darkMode ? '#ccc' : '#666', padding: '10px', borderRadius: '8px', marginBottom: '10px', marginTop: '0px', zIndex: 10, position: 'relative', backgroundColor: darkMode ? '#1a202c90' : '#ffffff90', /*boxShadow: darkMode ? '0 2px 8px #1a202c' : '0 2px 8px #fff',*/ /*textShadow: darkMode ? '0 0px 10px #1a202c' : '0 1px 2px #fff',*/ minWidth: '100px' }}>
                                    {progressMessage || 'Creating your personalized script...'}
                                </p>
                                <div style={{
                                    width: '100%',
                                    height: '8px',
                                    backgroundColor: darkMode ? '#444' : '#ddd',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    marginBottom: '10px'
                                }}>
                                    <div 
                                    className="progress-bar-shimmer-script"
                                    style={{
                                        width: `${progress}%`,
                                        height: '100%',
                                        background: 'linear-gradient(90deg, #AB5898 0%, #5C4BD3 100%)',
                                        borderRadius: '4px',
                                        transition: 'width 0.3s ease',
                                        boxShadow: '0 0 8px rgba(171, 88, 152, 0.5), 0 0 16px rgba(92, 75, 211, 0.5)'
                                    }} />
                                </div>
                                <p style={{ 
                                    color: darkMode ? '#ccc' : '#666', 
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}>
                                    {progress}%
                                </p>
                            </div>
                        ) : onboardingScript ? (
                            <>
                                {/* Script Section */}
                                {scriptGenerationDuration && (
                                    <p style={{
                                        fontSize: '14px',
                                        color: darkMode ? '#888' : '#666',
                                        marginBottom: '10px',
                                        fontStyle: 'italic',
                                        textAlign: 'left'
                                    }}>
                                        Thought for {scriptGenerationDuration} s {/*second{scriptGenerationDuration !== 1 ? 's' : ''}*/}
                                    </p>
                                )}
                                <div style={{
                                    backgroundColor: darkMode ? '#2d3748' : '#f8f9fa',
                                    borderRadius: '8px',
                                    padding: '20px',
                                    marginBottom: '30px',
                                    position: 'relative'
                                }}>
                                    <h3 style={{
                                        fontSize: '18px',
                                        marginBottom: '15px',
                                        color: darkMode ? '#fff' : '#333'
                                    }}>
                                        Your Script <span style={{ fontSize: '14px', color: '#666' }}>(editable)</span> 
                                    </h3>
                                    {onboardingScript.content ? (
                                        !isEditingScript ? (
                                            // Show highlighted version (always correct based on position: first, middle, last)
                                            <div
                                                onClick={() => setIsEditingScript(true)}
                                                style={{
                                                    width: '100%',
                                                    // minHeight: '100px',
                                                    padding: '12px',
                                                    lineHeight: '1.6',
                                                    color: darkMode ? '#e2e8f0' : '#333',
                                                    backgroundColor: darkMode ? '#1a202c' : '#fff',
                                                    border: '2px solid #8b5cf6',
                                                    borderRadius: '6px',
                                                    fontFamily: 'inherit',
                                                    boxSizing: 'border-box',
                                                    cursor: 'text',
                                                    whiteSpace: 'pre-wrap',
                                                    wordWrap: 'break-word',
                                                    fontSize: '12px',
                                                }}
                                            >
                                                {(() => {
                                                    const text = onboardingScript.content;
                                                    // Split into sentences (simple approach: split by period, exclamation, question mark)
                                                    const sentences = text.split(/([.!?]+[\s\n]*)/).filter(s => s.trim());
                                                    const sentenceGroups = [];
                                                    let currentGroup = [];
                                                    
                                                    for (let i = 0; i < sentences.length; i++) {
                                                        if (sentences[i].match(/[.!?]/)) {
                                                            currentGroup.push(sentences[i]);
                                                            sentenceGroups.push([...currentGroup]);
                                                            currentGroup = [];
                                                        } else {
                                                            currentGroup.push(sentences[i]);
                                                        }
                                                    }
                                                    if (currentGroup.length > 0) {
                                                        sentenceGroups.push(currentGroup);
                                                    }
                                                    
                                                    if (sentenceGroups.length === 0) {
                                                        return <span>{text}</span>;
                                                    }
                                                    
                                                    return sentenceGroups.map((group, index) => {
                                                        const sentence = group.join('');
                                                        const isFirst = index === 0;
                                                        const isSecond = index === 1;
                                                        const isLast = index === sentenceGroups.length - 1;
                                                        
                                                        let bgColor, glowColor;
                                                        if (isFirst) {
                                                            bgColor = darkMode ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.15)';
                                                            glowColor = 'rgba(239, 68, 68, 0.4)';
                                                        } else if (isSecond) {
                                                            bgColor = darkMode ? 'rgba(234, 179, 8, 0.2)' : 'rgba(234, 179, 8, 0.15)';
                                                            glowColor = 'rgba(234, 179, 8, 0.4)';
                                                        } else if (isLast) {
                                                            bgColor = darkMode ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.15)';
                                                            glowColor = 'rgba(34, 197, 94, 0.4)';
                                                        } else {
                                                            bgColor = darkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.15)';
                                                            glowColor = 'rgba(59, 130, 246, 0.4)';
                                                        }
                                                        
                                                        return (
                                                            <span
                                                                key={index}
                                                                style={{
                                                                    backgroundColor: bgColor,
                                                                    padding: '2px 4px',
                                                                    borderRadius: '4px',
                                                                    boxShadow: `0 0 8px ${glowColor}`,
                                                                    display: 'inline'
                                                                }}
                                                            >
                                                                {sentence}
                                                            </span>
                                                        );
                                                    });
                                                })()}
                                            </div>
                                        ) : (
                                            // Show editable textarea when in edit mode
                                            <TextareaAutosize
                                                ref={scriptTextareaRef}
                                                value={onboardingScript.content}
                                                onChange={(e) => {
                                                    const newContent = e.target.value;
                                                    setOnboardingScript(prev => ({
                                                        ...prev,
                                                        content: newContent
                                                    }));
                                                    // Only mark as modified if content actually changed
                                                    if (newContent !== onboardingScript.content) {
                                                        setScriptModified(true);
                                                    }
                                                }}
                                                onBlur={() => {
                                                    // Optionally exit edit mode on blur, or keep it editable
                                                    setIsEditingScript(false);
                                                }}
                                                style={{
                                                    width: '100%',
                                                    padding: '12px',
                                                    lineHeight: '1.6',
                                                    color: darkMode ? '#e2e8f0' : '#333',
                                                    backgroundColor: darkMode ? '#1a202c' : '#fff',
                                                    border: '2px solid #8b5cf6',
                                                    borderRadius: '6px',
                                                    resize: 'vertical',
                                                    fontFamily: 'inherit',
                                                    outline: 'none',
                                                    boxSizing: 'border-box',
                                                    fontSize: '12px',
                                                }}
                                                placeholder="Your script will appear here..."
                                            />
                                        )
                                    ) : (
                                        <TextareaAutosize
                                            value={onboardingScript.content || ''}
                                            onChange={(e) => {
                                                setOnboardingScript(prev => ({
                                                    ...prev,
                                                    content: e.target.value
                                                }));
                                                setScriptModified(true);
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                lineHeight: '1.6',
                                                color: darkMode ? '#e2e8f0' : '#333',
                                                backgroundColor: darkMode ? '#1a202c' : '#fff',
                                                border: '2px solid #8b5cf6',
                                                borderRadius: '6px',
                                                resize: 'vertical',
                                                fontFamily: 'inherit',
                                                outline: 'none',
                                                boxSizing: 'border-box',
                                            }}
                                            placeholder="Your script will appear here..."
                                        />
                                    )}
                                    <div style={{
                                        display: 'flex',
                                        gap: '20px',
                                        marginTop: `${isEditingScript ? '6.5px' : '10px'}`,
                                        fontSize: '12px',
                                        alignItems: 'center',
                                        flexWrap: 'wrap'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span style={{
                                                display: 'inline-block',
                                                width: '12px',
                                                height: '12px',
                                                borderRadius: '4px',
                                                backgroundColor: darkMode ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.15)',
                                                boxShadow: `0 0 8px ${darkMode ? 'rgba(239, 68, 68, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`
                                            }}></span>
                                            <span style={{ color: darkMode ? '#ccc' : '#666' }}>hook</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span style={{
                                                display: 'inline-block',
                                                width: '12px',
                                                height: '12px',
                                                borderRadius: '4px',
                                                backgroundColor: darkMode ? 'rgba(234, 179, 8, 0.2)' : 'rgba(234, 179, 8, 0.15)',
                                                boxShadow: `0 0 8px ${darkMode ? 'rgba(234, 179, 8, 0.4)' : 'rgba(234, 179, 8, 0.4)'}`
                                            }}></span>
                                            <span style={{ color: darkMode ? '#ccc' : '#666' }}>setup</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span style={{
                                                display: 'inline-block',
                                                width: '12px',
                                                height: '12px',
                                                borderRadius: '4px',
                                                backgroundColor: darkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.15)',
                                                boxShadow: `0 0 8px ${darkMode ? 'rgba(59, 130, 246, 0.4)' : 'rgba(59, 130, 246, 0.4)'}`
                                            }}></span>
                                            <span style={{ color: darkMode ? '#ccc' : '#666' }}>body</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span style={{
                                                display: 'inline-block',
                                                width: '12px',
                                                height: '12px',
                                                borderRadius: '4px',
                                                backgroundColor: darkMode ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.15)',
                                                boxShadow: `0 0 8px ${darkMode ? 'rgba(34, 197, 94, 0.4)' : 'rgba(34, 197, 94, 0.4)'}`
                                            }}></span>
                                            <span style={{ color: darkMode ? '#ccc' : '#666' }}>closing</span>
                                        </div>
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        marginTop: '15px'
                                    }}>
                                        <button
                                            onClick={generateScript}
                                            disabled={isGeneratingScriptOnboarding || isTranslating}
                                            style={{
                                                padding: '6px 12px',
                                                backgroundColor: (isGeneratingScriptOnboarding || isTranslating) ? '#ccc' : 'transparent',
                                                color: darkMode ? '#e2e8f0' : '#333',
                                                border: '1px solid #ccc',
                                                borderRadius: '8px',
                                                cursor: (isGeneratingScriptOnboarding || isTranslating) ? 'not-allowed' : 'pointer',
                                                fontWeight: 'bold',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                opacity: (isGeneratingScriptOnboarding || isTranslating) ? 0.6 : 1
                                            }}
                                        >
                                            <RefreshCw size={16} />
                                            Regenerate Script
                                        </button>
                                    </div>
                                </div>

                                {/* Voiceover Section */}
                                <div style={{
                                    backgroundColor: darkMode ? '#2d3748' : '#f8f9fa',
                                    borderRadius: '8px',
                                    padding: '20px',
                                    marginBottom: '30px'
                                }}>
                                    <h3 style={{
                                        fontSize: '18px',
                                        marginBottom: '20px',
                                        color: darkMode ? '#fff' : '#333'
                                    }}>
                                        Your Voiceover
                                    </h3>

                            {/* Voice Dropdown */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: '600',
                                    color: darkMode ? '#fff' : '#333',
                                    fontSize: '14px'
                                }}>
                                    Select a voice
                                </label>
                                <div style={{ position: 'relative' }} data-voice-dropdown>
                                    <button
                                        type="button"
                                        onClick={() => setShowVoiceDropdown(!showVoiceDropdown)}
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            borderRadius: '6px',
                                            border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                            background: darkMode ? '#1a202c' : '#fff',
                                            color: darkMode ? '#e2e8f0' : '#333',
                                            fontSize: '14px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            textAlign: 'left'
                                        }}
                                    >
                                        <span>
                                            {(() => {
                                                // Check if workspace voice is selected
                                                if (onboardingVoiceId && selectedDefaultVoice === onboardingVoiceId) {
                                                    return 'My Cloned Voice';
                                                }
                                                // Otherwise show default voice name
                                                return selectedDefaultVoice ? 
                                                    DEFAULT_VOICES.find(v => v.id === selectedDefaultVoice)?.name || 'Select a voice...' 
                                                    : (onboardingVoiceId ? 'My Cloned Voice' : 'Select a voice...');
                                            })()}
                                        </span>
                                        <span style={{ fontSize: '12px', opacity: 0.7 }}>
                                            {showVoiceDropdown ? '▲' : '▼'}
                                        </span>
                                    </button>
                                    
                                    {showVoiceDropdown && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            backgroundColor: darkMode ? '#1a202c' : '#fff',
                                            border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                            borderRadius: '6px',
                                            marginTop: '2px',
                                            zIndex: 1000,
                                            maxHeight: '200px',
                                            overflowY: 'auto',
                                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.5)'
                                        }}>
                                            {/* Workspace voice first if it exists */}
                                            {onboardingVoiceId && (
                                                <div
                                                    style={{
                                                        padding: '8px 12px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        borderBottom: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                                        backgroundColor: selectedDefaultVoice === onboardingVoiceId ? (darkMode ? '#4a5568' : '#e9ecef') : 'transparent',
                                                        color: darkMode ? '#e2e8f0' : '#333',
                                                        fontWeight: selectedDefaultVoice === onboardingVoiceId ? '600' : 'normal'
                                                    }}
                                                    onClick={() => {
                                                        setSelectedDefaultVoice(onboardingVoiceId);
                                                        setShowVoiceDropdown(false);
                                                        // Automatically generate voiceover when workspace voice is selected
                                                        if (onboardingScript) {
                                                            generateVoiceover(onboardingVoiceId);
                                                        }
                                                    }}
                                                >
                                                    <span style={{ fontSize: '14px' }}>My Cloned Voice</span>
                                                </div>
                                            )}
                                            {/* Default voices */}
                                            {DEFAULT_VOICES.map(voice => (
                                                <div
                                                    key={voice.id}
                                                    style={{
                                                        padding: '8px 12px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        borderBottom: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                                        backgroundColor: selectedDefaultVoice === voice.id ? (darkMode ? '#4a5568' : '#e9ecef') : 'transparent',
                                                        color: darkMode ? '#e2e8f0' : '#333'
                                                    }}
                                                    onClick={() => {
                                                        setSelectedDefaultVoice(voice.id);
                                                        setShowVoiceDropdown(false);
                                                        // Automatically generate voiceover when a voice is selected
                                                        if (onboardingScript) {
                                                            generateVoiceover(voice.id);
                                                        }
                                                    }}
                                                >
                                                    <span style={{ fontSize: '14px' }}>{voice.name}</span>
                                                    {voice.previewUrl && (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleVoicePreview(voice.id, voice.previewUrl);
                                                            }}
                                                            style={{
                                                                padding: '4px 6px',
                                                                backgroundColor: playingVoiceId === voice.id ? '#ff4444' : '#AB5898',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: '4px',
                                                                height: '22px',
                                                                width: '22px'
                                                            }}
                                                        >
                                                            {playingVoiceId === voice.id ? '⏹' : '▶'}
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Voice Generation Mode */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '14px' }}>
                                    Voice Generation Mode
                                    <button
                                        type="button"
                                        onClick={() => setShowVoiceGenerationModeInfo(!showVoiceGenerationModeInfo)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '2px',
                                            borderRadius: '50%',
                                            width: '18px',
                                            height: '18px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            color: darkMode ? '#ccc' : '#666',
                                            backgroundColor: darkMode ? '#4a5568' : '#e9ecef',
                                            transition: 'all 0.2s ease',
                                        }}
                                        onMouseOver={(e) => {
                                            e.target.style.backgroundColor = darkMode ? '#4a5568' : '#d1d5db';
                                            e.target.style.color = darkMode ? '#fff' : '#333';
                                        }}
                                        onMouseOut={(e) => {
                                            e.target.style.backgroundColor = darkMode ? '#2d3748' : '#e9ecef';
                                            e.target.style.color = darkMode ? '#ccc' : '#666';
                                        }}
                                    >
                                        i
                                    </button>
                                </label>
                                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '10px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                                        <input
                                            type="radio"
                                            name="voiceGenerationMode"
                                            value="stable"
                                            checked={voiceGenerationMode === 'stable'}
                                            onChange={async (e) => {
                                                setVoiceGenerationMode(e.target.value);
                                                localStorage.setItem('voiceover_voice_generation_mode', e.target.value);
                                                // Auto-regenerate if voiceover already exists
                                                if (onboardingVoiceoverUrl) {
                                                    requestVoiceoverAutoplayRef.current = true;
                                                    // Trigger regeneration
                                                    const isWorkspaceVoice = onboardingVoiceId && selectedDefaultVoice === onboardingVoiceId;
                                                    setIsGeneratingVoiceoverOnboarding(true);
                                                    try {
                                                        // If editing, save current textarea content first
                                                        if (isEditingScript && scriptTextareaRef.current) {
                                                            const currentContent = scriptTextareaRef.current.value;
                                                            if (currentContent !== onboardingScript.content) {
                                                                await api.updateWorkspaceScriptContent(
                                                                    currentWorkspace._id, 
                                                                    onboardingScript._id, 
                                                                    'main', 
                                                                    currentContent
                                                                );
                                                                setOnboardingScript(prev => ({
                                                                    ...prev,
                                                                    content: currentContent
                                                                }));
                                                                setScriptModified(true);
                                                            }
                                                        } else if (scriptModified) {
                                                            await api.updateWorkspaceScriptContent(
                                                                currentWorkspace._id, 
                                                                onboardingScript._id, 
                                                                'main', 
                                                                onboardingScript.content
                                                            );
                                                        }
                                                        
                                                        const response = isWorkspaceVoice ? 
                                                            await api.studioGenerateVoiceover(onboardingScript._id, { voiceGenerationMode: e.target.value }, onboardingVoiceId) :
                                                            await api.studioGenerateVoiceover(onboardingScript._id, { voiceGenerationMode: e.target.value }, selectedDefaultVoice);
                                                        setOnboardingVoiceoverUrl(response.data.voiceoverUrl);
                                                    } catch (error) {
                                                        console.error('Error regenerating voiceover:', error);
                                                    } finally {
                                                        setIsGeneratingVoiceoverOnboarding(false);
                                                    }
                                                }
                                            }}
                                            style={{
                                                cursor: 'pointer',
                                                accentColor: '#AB5898'
                                            }}
                                        />
                                        <span style={{ color: darkMode ? '#e2e8f0' : '#333' }}>Stable</span>
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                                        <input
                                            type="radio"
                                            name="voiceGenerationMode"
                                            value="expressive"
                                            checked={voiceGenerationMode === 'expressive'}
                                            onChange={async (e) => {
                                                setVoiceGenerationMode(e.target.value);
                                                localStorage.setItem('voiceover_voice_generation_mode', e.target.value);
                                                // Auto-regenerate if voiceover already exists
                                                if (onboardingVoiceoverUrl) {
                                                    requestVoiceoverAutoplayRef.current = true;
                                                    // Trigger regeneration
                                                    const isWorkspaceVoice = onboardingVoiceId && selectedDefaultVoice === onboardingVoiceId;
                                                    setIsGeneratingVoiceoverOnboarding(true);
                                                    try {
                                                        // If editing, save current textarea content first
                                                        if (isEditingScript && scriptTextareaRef.current) {
                                                            const currentContent = scriptTextareaRef.current.value;
                                                            if (currentContent !== onboardingScript.content) {
                                                                await api.updateWorkspaceScriptContent(
                                                                    currentWorkspace._id, 
                                                                    onboardingScript._id, 
                                                                    'main', 
                                                                    currentContent
                                                                );
                                                                setOnboardingScript(prev => ({
                                                                    ...prev,
                                                                    content: currentContent
                                                                }));
                                                                setScriptModified(true);
                                                            }
                                                        } else if (scriptModified) {
                                                            await api.updateWorkspaceScriptContent(
                                                                currentWorkspace._id, 
                                                                onboardingScript._id, 
                                                                'main', 
                                                                onboardingScript.content
                                                            );
                                                        }
                                                        
                                                        const response = isWorkspaceVoice ? 
                                                            await api.studioGenerateVoiceover(onboardingScript._id, { voiceGenerationMode: e.target.value }, onboardingVoiceId) :
                                                            await api.studioGenerateVoiceover(onboardingScript._id, { voiceGenerationMode: e.target.value }, selectedDefaultVoice);
                                                        setOnboardingVoiceoverUrl(response.data.voiceoverUrl);
                                                    } catch (error) {
                                                        console.error('Error regenerating voiceover:', error);
                                                    } finally {
                                                        setIsGeneratingVoiceoverOnboarding(false);
                                                    }
                                                }
                                            }}
                                            style={{
                                                cursor: 'pointer',
                                                accentColor: '#AB5898'
                                            }}
                                        />
                                        <span style={{ color: darkMode ? '#e2e8f0' : '#333' }}>Expressive</span>
                                    </label>
                                </div>
                                {showVoiceGenerationModeInfo && (
                                    <div style={{
                                        backgroundColor: darkMode ? '#2d3748' : '#f8f9fa',
                                        border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                        borderRadius: '6px',
                                        padding: '12px',
                                        marginTop: '8px',
                                        fontSize: '13px',
                                        color: darkMode ? '#ccc' : '#666',
                                        lineHeight: '1.5'
                                    }}>
                                        <strong style={{ color: darkMode ? '#fff' : '#333' }}>Stable:</strong> Consistent, predictable voice output. Best for professional content.<br />
                                        <strong style={{ color: darkMode ? '#fff' : '#333' }}>Expressive:</strong> More dynamic and emotional variations. Best for engaging storytelling.
                                    </div>
                                )}
                            </div>

                            {/* Generate/Regenerate Button */}
                            <button
                                onClick={async () => {
                                    if (!onboardingScript || !selectedDefaultVoice) return;
                                    
                                    const isWorkspaceVoice = onboardingVoiceId && selectedDefaultVoice === onboardingVoiceId;
                                    
                                    setIsGeneratingVoiceoverOnboarding(true);
                                    if (onboardingVoiceoverUrl) requestVoiceoverAutoplayRef.current = true;
                                    try {
                                        // If editing, save current textarea content first
                                        if (isEditingScript && scriptTextareaRef.current) {
                                            const currentContent = scriptTextareaRef.current.value;
                                            if (currentContent !== onboardingScript.content) {
                                                console.log('Saving script changes from textarea before voiceover generation...');
                                                await api.updateWorkspaceScriptContent(
                                                    currentWorkspace._id, 
                                                    onboardingScript._id, 
                                                    'main', 
                                                    currentContent
                                                );
                                                setOnboardingScript(prev => ({
                                                    ...prev,
                                                    content: currentContent
                                                }));
                                                setScriptModified(true);
                                            }
                                        } else if (scriptModified) {
                                            console.log('Saving script changes before voiceover generation...');
                                            await api.updateWorkspaceScriptContent(
                                                currentWorkspace._id, 
                                                onboardingScript._id, 
                                                'main', 
                                                onboardingScript.content
                                            );
                                        }
                                        
                                        const voiceSettings = { voiceGenerationMode: voiceGenerationMode };
                                        const response = isWorkspaceVoice ? 
                                            await api.studioGenerateVoiceover(onboardingScript._id, voiceSettings, onboardingVoiceId) :
                                            await api.studioGenerateVoiceover(onboardingScript._id, voiceSettings, selectedDefaultVoice);
                                        
                                        // Increment regeneration count if this is a regeneration (not first generation)
                                        if (onboardingVoiceoverUrl) {
                                            setVoiceoverRegenerationCount(prev => prev + 1);
                                        }
                                        
                                        setOnboardingVoiceoverUrl(response.data.voiceoverUrl);
                                        setCurrentVoiceoverVoiceId(selectedDefaultVoice);
                                        setCurrentVoiceoverVoiceType(isWorkspaceVoice ? 'cloned' : 'default');
                                        
                                        // Get voiceover duration for warning message
                                        const audio = new Audio(response.data.voiceoverUrl);
                                        audio.addEventListener('loadedmetadata', () => {
                                            setVoiceoverDuration(audio.duration);
                                        });
                                        
                                        trackEvent('onboarding_voiceover_generated', { 
                                            voiceType: isWorkspaceVoice ? 'cloned' : 'default',
                                            voiceId: selectedDefaultVoice,
                                            voiceGenerationMode: voiceGenerationMode
                                        });
                                    } catch (error) {
                                        console.error('Error generating voiceover:', error);
                                    } finally {
                                        setIsGeneratingVoiceoverOnboarding(false);
                                        setScriptModified(false);
                                        setIsEditingScript(false); // Return to highlighted view
                                    }
                                }}
                                disabled={!onboardingScript || !selectedDefaultVoice || isGeneratingVoiceoverOnboarding}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    backgroundColor: (!onboardingScript || !selectedDefaultVoice || isGeneratingVoiceoverOnboarding) ? '#ccc' : '#AB5898',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: (!onboardingScript || !selectedDefaultVoice || isGeneratingVoiceoverOnboarding) ? 'not-allowed' : 'pointer',
                                    fontSize: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    fontWeight: 'bold',
                                    marginBottom: '20px'
                                }}
                            >
                                {isGeneratingVoiceoverOnboarding ? (
                                    <>
                                        <RefreshCw className="spin" size={16} />
                                        Generating...
                                    </>
                                ) : (
                                    <>
                                        {onboardingVoiceoverUrl ? (
                                            <>
                                                <RefreshCw size={16} />
                                                {scriptModified ? 'Regenerate' : 'Try new voice tone'}
                                            </>
                                        ) : (
                                            <>
                                                Generate Voiceover
                                            </>
                                        )}
                                    </>
                                )}
                            </button>

                            {/* Tip after 5 regenerations */}
                            {voiceoverRegenerationCount >= 5 && (
                                <div style={{
                                    marginTop: '-10px',
                                    marginBottom: '20px',
                                    padding: '12px',
                                    backgroundColor: darkMode ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.15)',
                                    border: `1px solid ${darkMode ? 'rgba(255, 193, 7, 0.3)' : 'rgba(255, 193, 7, 0.4)'}`,
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    color: darkMode ? '#ffc107' : '#856404',
                                    lineHeight: '1.5'
                                }}>
                                    <strong>💡 Tip:</strong> If you don't get the tone that you want, try changing the script, adding and removing commas, question marks, exclamation marks.
                                </div>
                            )}

                            {/* Voiceover Player */}
                            {onboardingVoiceoverUrl && !isGeneratingVoiceoverOnboarding && (
                                <div style={{
                                    marginTop: '20px',
                                    padding: '15px',
                                    backgroundColor: darkMode ? '#1a202c' : '#fff',
                                    borderRadius: '8px',
                                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`
                                }}>
                                    <audio
                                        ref={voiceoverAudioRef}
                                        controls
                                        style={{ 
                                            width: '100%', 
                                            height: '40px'
                                        }}
                                    >
                                        <source src={onboardingVoiceoverUrl} type="audio/mpeg" />
                                    </audio>
                                </div>
                            )}
                                </div>
                            </>
                        ) : null}

                        {onboardingVoiceoverUrl && isGeneratingVoiceoverOnboarding && (
                            <div style={{ height: '75px' }} />
                        )}
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <button
                                onClick={() => {
                                    stopAllAudio();
                                    trackEvent('onboarding_step_next', {
                                        fromStep: step,
                                        toStep: step + 1,
                                        stepName: getStepName(step),
                                        hasVoiceover: !!onboardingVoiceoverUrl,
                                        workspaceId: currentWorkspace?._id
                                    });
                                    onNext();
                                }}
                                disabled={!onboardingVoiceoverUrl}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: onboardingVoiceoverUrl ? '#e1f679' : '#ccc',
                                    fontWeight: 'bold',
                                    color: 'black',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: onboardingVoiceoverUrl ? 'pointer' : 'not-allowed',
                                    fontSize: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                Next <span style={{ fontSize: '12px', color: 'gray' }}>(Choose Cuts)</span>
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                );

            /* Original case 3 - commented out
            case 3:
                return (
                    <div style={{
                        padding: '40px 20px',
                        maxWidth: '600px',
                        margin: '0 auto',
                        position: 'relative'
                    }}>
                        {onPrevious && (
                            <button
                                onClick={onPrevious}
                                style={{
                                    position: 'absolute',
                                    top: '20px',
                                    left: '20px',
                                    padding: '8px 16px',
                                    backgroundColor: 'transparent',
                                    color: darkMode ? '#ccc' : '#666',
                                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    zIndex: 10
                                }}
                            >
                                <ChevronLeft size={14} />
                                Back
                            </button>
                        )}
                        
                        <h2
                        className={darkMode ? "gradient-text-dark" : "gradient-text"}
                        style={{
                            fontSize: screenWidth <= 600 ? '20px' : '24px',
                            marginBottom: '20px',
                            textAlign: 'center'
                        }}>
                            Generate your video script
                        </h2>
                        
                        <p style={{
                            fontSize: '16px',
                            lineHeight: '1.6',
                            marginBottom: '30px',
                            color: darkMode ? '#ccc' : '#666',
                            textAlign: 'center'
                        }}>
                            {isGeneratingScriptOnboarding ? '' : 'Your script has been generated based on your profile information.'}
                        </p>

                        {!isGeneratingScriptOnboarding && onboardingScript && (
                            <div style={{
                                backgroundColor: darkMode ? '#1a2332' : '#e7f3ff',
                                border: `1px solid ${darkMode ? '#2d4a6b' : '#b3d9ff'}`,
                                borderRadius: '8px',
                                padding: '12px',
                                marginBottom: '30px',
                                color: darkMode ? '#7bb3f0' : '#0066cc'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <span style={{ fontSize: '16px' }}>💡</span>
                                    <strong style={{ fontSize: '14px' }}>
                                        Hey, I'm your AI script writer, I made a script for you, it is good enough for your first video with Merra AI, but next time, it could be so much better with more profile information ! I hope you like it, feel free to edit it !
                                    </strong>
                                </div>
                            </div>
                        )}

                        {isGeneratingScriptOnboarding ? (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px 20px'
                            }}>
                                <div style={{
                                    width: '50px',
                                    height: '50px',
                                    background: '#4ecdc4',
                                    borderRadius: '50%',
                                    animation: 'pulse 2s infinite',
                                    margin: '0 auto 20px'
                                }} />
                                <p style={{ color: darkMode ? '#ccc' : '#666', marginBottom: '10px' }}>
                                    {progressMessage || 'Creating your personalized script...'}
                                </p>
                                <div style={{
                                    width: '100%',
                                    height: '8px',
                                    backgroundColor: darkMode ? '#444' : '#ddd',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    marginBottom: '10px'
                                }}>
                                    <div 
                                    className="progress-bar-shimmer-script"
                                    style={{
                                        width: `${progress}%`,
                                        height: '100%',
                                        background: 'linear-gradient(90deg, #AB5898 0%, #5C4BD3 100%)',
                                        borderRadius: '4px',
                                        transition: 'width 0.3s ease',
                                        boxShadow: '0 0 8px rgba(171, 88, 152, 0.5), 0 0 16px rgba(92, 75, 211, 0.5)'
                                    }} />
                                </div>
                                <p style={{ 
                                    color: darkMode ? '#ccc' : '#666', 
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}>
                                    {progress}%
                                </p>
                            </div>
                        ) : onboardingScript ? (
                            <div style={{
                                backgroundColor: darkMode ? '#2d3748' : '#f8f9fa',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px',
                                position: 'relative'
                            }}>
                                {isTranslating && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        zIndex: 1000,
                                        gap: '15px'
                                    }}>
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            background: '#4ecdc4',
                                            borderRadius: '50%',
                                            animation: 'pulse 2s infinite'
                                        }} />
                                        <p style={{
                                            color: 'white',
                                            fontSize: '16px',
                                            fontWeight: '600',
                                            margin: 0,
                                            textAlign: 'center'
                                        }}>
                                            Translating to {languageOptions.find(l => l.code === translationLanguage)?.name || 'Spanish'}...
                                        </p>
                                        <p style={{
                                            color: '#ccc',
                                            fontSize: '14px',
                                            margin: 0,
                                            textAlign: 'center'
                                        }}>
                                            Please wait while we translate your script
                                        </p>
                                    </div>
                                )}
                                
                                <h3 style={{
                                    fontSize: '18px',
                                    marginBottom: '15px',
                                    color: darkMode ? '#fff' : '#333'
                                }}>
                                    Your Script <span style={{ fontSize: '14px', color: '#666' }}>(editable)</span> 
                                </h3>
                                <TextareaAutosize
                                    value={onboardingScript.content}
                                    onChange={(e) => {
                                        setOnboardingScript(prev => ({
                                            ...prev,
                                            content: e.target.value
                                        }));
                                        setScriptModified(true); // Mark script as modified
                                    }}
                                    disabled={isTranslating}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        // fontSize: '16px',
                                        lineHeight: '1.6',
                                        color: darkMode ? '#e2e8f0' : '#333',
                                        backgroundColor: darkMode ? '#1a202c' : '#fff',
                                        border: '2px solid #8b5cf6',
                                        borderRadius: '6px',
                                        resize: 'vertical',
                                        fontFamily: 'inherit',
                                        outline: 'none',
                                        boxSizing: 'border-box',
                                        opacity: isTranslating ? 0.5 : 1,
                                        cursor: isTranslating ? 'not-allowed' : 'text',
                                    }}
                                    placeholder="Your script will appear here..."
                                />
                                
                                <div style={{ 
                                    display: 'flex', 
                                    gap: '8px', 
                                    alignItems: 'center',
                                    marginTop: '15px',
                                    justifyContent: 'flex-end',
                                    flexDirection: window.innerWidth <= 600 ? 'column' : 'row'
                                }}>

                                    <button
                                        onClick={generateScript}
                                        disabled={isGeneratingScriptOnboarding || isTranslating}
                                        style={{
                                            padding: '6px 12px',
                                            backgroundColor: (isGeneratingScriptOnboarding || isTranslating) ? '#ccc' : 'transparent',
                                            color: darkMode ? '#e2e8f0' : '#333',
                                            border: '1px solid #ccc',
                                            borderRadius: '8px',
                                            cursor: (isGeneratingScriptOnboarding || isTranslating) ? 'not-allowed' : 'pointer',
                                            // fontSize: '16px',
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            opacity: (isGeneratingScriptOnboarding || isTranslating) ? 0.6 : 1
                                        }}
                                    >
                                        <RefreshCw size={16} />
                                        Regenerate Script
                                    </button>

                                    <button 
                                        onClick={translateScript} 
                                        disabled={isTranslating}
                                        style={{ 
                                            padding: '6px 12px', 
                                            borderRadius: '6px',
                                            backgroundColor: darkMode ? '#2d5a9e' : '#2d5a9e',
                                            color: darkMode ? '#e2e8f0' : '#e2e8f0',
                                            border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                            opacity: isTranslating ? 0.6 : 1,
                                            cursor: isTranslating ? 'not-allowed' : 'pointer',
                                            // fontSize: '14px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                    > 
                                        {isTranslating ? (
                                            <>
                                                <div style={{
                                                    width: '12px',
                                                    height: '12px',
                                                    border: `2px solid ${darkMode ? '#444' : '#ddd'}`,
                                                    borderTop: `2px solid #0066cc`,
                                                    borderRadius: '50%',
                                                    animation: 'spin 1s linear infinite'
                                                }} />
                                                Translating...
                                            </>
                                            ) : (
                                            <>
                                                Translate
                                            </>
                                        )}
                                    </button>

                                    <div style={{ position: 'relative' }}>
                                        <button
                                            type="button"
                                            onClick={() => setShowTranslationLanguageDropdown(!showTranslationLanguageDropdown)}
                                            disabled={isTranslating}
                                            style={{
                                                padding: '6px 12px',
                                                borderRadius: '6px',
                                                border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                                background: darkMode ? '#2d3748' : '#f8f9fa',
                                                color: darkMode ? '#e2e8f0' : '#333',
                                                cursor: isTranslating ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                minWidth: '100px',
                                                opacity: isTranslating ? 0.6 : 1
                                                // fontSize: '14px'
                                            }}
                                        >
                                            <span>🌐</span>
                                            <span>{languageOptions.find(l => l.code === translationLanguage)?.name || 'Spanish'}</span>
                                            <span style={{ fontSize: '10px', opacity: 0.7 }}>
                                                {showTranslationLanguageDropdown ? '▲' : '▼'}
                                            </span>
                                        </button>
                                        
                                        {showTranslationLanguageDropdown && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '100%',
                                                right: 0,
                                                backgroundColor: darkMode ? '#2d3748' : '#f8f9fa',
                                                border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                                borderRadius: '6px',
                                                marginTop: '2px',
                                                zIndex: 1000,
                                                maxHeight: '200px',
                                                overflowY: 'auto',
                                                minWidth: '120px',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                            }}>
                                                {languageOptions.map(lang => (
                                                    <div
                                                        key={lang.code}
                                                        style={{
                                                            padding: '8px 12px',
                                                            cursor: 'pointer',
                                                            borderRadius: '4px',
                                                            backgroundColor: translationLanguage === lang.code ? (darkMode ? '#4a5568' : '#e9ecef') : 'transparent',
                                                            color: translationLanguage === lang.code ? (darkMode ? '#fff' : '#333') : (darkMode ? '#e2e8f0' : '#333'),
                                                            fontSize: '14px',
                                                            transition: 'background-color 0.2s ease'
                                                        }}
                                                        onClick={() => {
                                                            setTranslationLanguage(lang.code);
                                                            setShowTranslationLanguageDropdown(false);
                                                        }}
                                                        onMouseOver={(e) => {
                                                            if (translationLanguage !== lang.code) {
                                                                e.target.style.backgroundColor = darkMode ? '#4a5568' : '#e9ecef';
                                                            }
                                                        }}
                                                        onMouseOut={(e) => {
                                                            if (translationLanguage !== lang.code) {
                                                                e.target.style.backgroundColor = 'transparent';
                                                            }
                                                        }}
                                                    >
                                                        {lang.name}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            
                            <button
                                onClick={handleNextWithAutoGeneration}
                                disabled={!onboardingScript}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: onboardingScript ? '#e1f679' : '#ccc',
                                    fontWeight: 'bold',
                                    color: 'black',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: onboardingScript ? 'pointer' : 'not-allowed',
                                    fontSize: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                Next
                                <ChevronRight size={16} />
                            </button>

                        </div>
                    </div>
                );
            */

            /* Case 4 - Commented out to skip directly to step 7
            case 4:
                return (
                    <div style={{
                        padding: '40px 20px',
                        maxWidth: '600px',
                        margin: '0 auto',
                        position: 'relative'
                    }}>
                        {onPrevious && (
                            <button
                                onClick={() => {
                                    stopAllAudio();
                                    onPrevious();
                                }}
                                style={{
                                    position: 'absolute',
                                    top: '20px',
                                    left: '20px',
                                    padding: '8px 16px',
                                    backgroundColor: 'transparent',
                                    color: darkMode ? '#ccc' : '#666',
                                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    zIndex: 10
                                }}
                            >
                                <ChevronLeft size={14} />
                                Back
                            </button>
                        )}
                        
                        <h2
                        className={darkMode ? "gradient-text-dark" : "gradient-text"}
                        style={{
                            fontSize: screenWidth <= 600 ? '20px' : '24px',
                            marginBottom: '20px',
                            marginTop: '30px',
                            textAlign: 'center'
                        }}>
                            Generate voiceover for your script
                        </h2>
                        
                        <p style={{
                            fontSize: '16px',
                            lineHeight: '1.6',
                            marginBottom: '30px',
                            color: darkMode ? '#ccc' : '#666',
                            textAlign: 'center'
                        }}>
                            Choose between default voices or use your cloned voice for the voiceover.
                        </p>

                        {isGeneratingVoiceoverOnboarding ? (
                            <div style={{
                                backgroundColor: darkMode ? '#2d3748' : '#f8f9fa',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '30px',
                                textAlign: 'center'
                            }}>
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    background: '#4ecdc4',
                                    borderRadius: '50%',
                                    animation: 'pulse 2s infinite',
                                    margin: '0 auto 15px'
                                }} />
                                <h4 style={{ 
                                    margin: '0 0 10px 0', 
                                    color: darkMode ? '#fff' : '#333',
                                    fontSize: '18px'
                                }}>
                                    Generating Voiceover...
                                </h4>
                                <p style={{ 
                                    color: darkMode ? '#ccc' : '#666', 
                                    marginBottom: '0',
                                    fontSize: '14px'
                                }}>
                                    Creating your voiceover with {generatingVoiceId && DEFAULT_VOICES.find(v => v.id === generatingVoiceId)?.name || 
                                        (generatingVoiceId === onboardingVoiceId ? 'your cloned' : 'Tiffany Natural')} voice
                                </p>
                            </div>
                        ) : onboardingVoiceoverUrl ? (
                            <div style={{
                                backgroundColor: darkMode ? '#2d3748' : '#f8f9fa',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '30px',
                                textAlign: 'center'
                            }}>
                                <h4 style={{ 
                                    margin: '0 0 15px 0', 
                                    color: darkMode ? '#fff' : '#333',
                                    fontSize: '18px'
                                }}>
                                    Generated Voiceover Preview
                                </h4>
                                <p style={{ 
                                    color: darkMode ? '#ccc' : '#666', 
                                    marginBottom: '15px',
                                    fontSize: '14px'
                                }}>
                                    Listen to your script with {currentVoiceoverVoiceType === 'cloned' ? 'your cloned voice' : 
                                        (currentVoiceoverVoiceId ? DEFAULT_VOICES.find(v => v.id === currentVoiceoverVoiceId)?.name || 'the selected voice' : 'Brian voice')}:
                                </p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', justifyContent: 'center' }}>
                                    <audio
                                        ref={voiceoverAudioRef}
                                        controls
                                        style={{ 
                                            width: '100%', 
                                            maxWidth: '300px',
                                            height: '40px'
                                        }}
                                    >
                                        <source src={onboardingVoiceoverUrl} type="audio/mpeg" />
                                    </audio>
                                    {(useClonedVoice || selectedDefaultVoice) && (
                                        <button
                                        onClick={async () => {
                                            if (onboardingScript) {
                                                const voiceId = useClonedVoice ? onboardingVoiceId : selectedDefaultVoice;
                                                const voiceType = useClonedVoice ? 'cloned' : 'default';
                                                
                                                setGeneratingVoiceId(voiceId);
                                                setIsGeneratingVoiceoverOnboarding(true);
                                                requestVoiceoverAutoplayRef.current = true;
                                                try {
                                                    // If script was modified, save the changes to database first
                                                    if (scriptModified) {
                                                        console.log('Saving script changes before voiceover regeneration...');
                                                        await api.updateWorkspaceScriptContent(
                                                            currentWorkspace._id, 
                                                            onboardingScript._id, 
                                                            'main', 
                                                            onboardingScript.content
                                                        );
                                                    }
                                                    
                                                    const response = useClonedVoice ? 
                                                        await api.studioGenerateVoiceover(onboardingScript._id, null, onboardingVoiceId) :
                                                        await api.studioGenerateVoiceover(onboardingScript._id, selectedDefaultVoice, null);
                                                    
                                                    setOnboardingVoiceoverUrl(response.data.voiceoverUrl);
                                                    setCurrentVoiceoverVoiceId(voiceId);
                                                    setCurrentVoiceoverVoiceType(voiceType);
                                                    trackEvent('onboarding_voiceover_regenerated', { 
                                                        voiceType: voiceType,
                                                        voiceId: voiceId
                                                    });
                                                } catch (error) {
                                                    console.error('Error regenerating voiceover:', error);
                                                } finally {
                                                    setIsGeneratingVoiceoverOnboarding(false);
                                                    setGeneratingVoiceId(null);
                                                    setScriptModified(false);
                                                    setIsEditingScript(false); // Return to highlighted view
                                                }
                                            }
                                        }}
                                        disabled={!useClonedVoice && !selectedDefaultVoice} // Allow overriding ongoing generation
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: ((!useClonedVoice && !selectedDefaultVoice)) ? '#ccc' : '#e1f679',
                                            color: 'black',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: ((!useClonedVoice && !selectedDefaultVoice)) ? 'not-allowed' : 'pointer',
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {isGeneratingVoiceoverOnboarding ? (
                                            <>
                                                <RefreshCw className="spin" size={14} />
                                                Regenerating...
                                            </>
                                        ) : (
                                            <>
                                                <RefreshCw size={14} />
                                                Regenerate
                                            </>
                                        )}
                                    </button>)}
                                </div>
                            </div>
                        ) : null}

                        <div style={{ marginBottom: '30px' }}>
                            <div style={{
                                display: 'flex',
                                gap: '10px',
                                marginBottom: '20px',
                                justifyContent: 'center'
                            }}>
                                <button
                                    onClick={() => {
                                        setVoiceCloningError(null); // Clear error when switching modes
                                        setUseClonedVoice(false);
                                    }}
                                    style={{
                                        padding: '12px 24px',
                                        backgroundColor: !useClonedVoice ? '#0066cc' : 'transparent',
                                        color: !useClonedVoice ? 'white' : (darkMode ? '#ccc' : '#666'),
                                        border: `1px solid ${!useClonedVoice ? '#0066cc' : (darkMode ? '#444' : '#ddd')}`,
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '16px'
                                    }}
                                >
                                    Default Voices
                                </button>
                                <span style={{ fontSize: '16px', color: darkMode ? '#ccc' : '#666', alignSelf: 'center' }}>or</span>
                                <button
                                    onClick={() => {
                                        setVoiceCloningError(null); // Clear error when switching modes
                                        setUseClonedVoice(true);
                                    }}
                                    style={{
                                        padding: '12px 24px',
                                        backgroundColor: useClonedVoice ? '#0066cc' : 'transparent',
                                        color: useClonedVoice ? 'white' : (darkMode ? '#ccc' : '#666'),
                                        border: `1px solid ${useClonedVoice ? '#0066cc' : (darkMode ? '#444' : '#ddd')}`,
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                    }}
                                >
                                    My Cloned Voice
                                </button>
                            </div>

                            {!useClonedVoice && (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: '15px'
                                }}>
                                    {DEFAULT_VOICES.map((voice) => (
                                        <div
                                            key={voice.id}
                                            // onClick={() => setSelectedDefaultVoice(voice.id)}
                                            style={{
                                                padding: '15px',
                                                backgroundColor: selectedDefaultVoice === voice.id ? '#0066cc' : (darkMode ? '#2d3748' : '#f8f9fa'),
                                                color: selectedDefaultVoice === voice.id ? 'white' : (darkMode ? '#fff' : '#333'),
                                                borderRadius: '8px',
                                                // cursor: 'pointer',
                                                border: `2px solid ${selectedDefaultVoice === voice.id ? '#0066cc' : 'transparent'}`,
                                                textAlign: 'center'
                                            }}
                                        >
                                            <div style={{ margin: '0 0 10px 0', display: 'flex', flex: '0 0 auto', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                                <span style={{ fontWeight: 'bold'}}>{voice.name}</span>
                                                {voice.previewUrl && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleVoicePreview(voice.id, voice.previewUrl);
                                                        }}
                                                        style={{
                                                            padding: '4px 6px',
                                                            backgroundColor: playingVoiceId === voice.id ? '#ff4444' : '#0066cc',
                                                            color: 'white',
                                                            border: '1px solid var(--input-border)',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '12px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '4px',
                                                            height: '22px',
                                                            width: '22px',
                                                        }}
                                                    >
                                                        {playingVoiceId === voice.id ? '⏹' : '▶'}
                                                    </button>
                                                )}
                                            </div>
                                            
                                            
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedDefaultVoice(voice.id);
                                                    setUseClonedVoice(false);
                                                    // Generate voiceover with this voice
                                                    if (onboardingScript) {
                                                        // Increment generation ID to track this request
                                                        currentGenerationIdRef.current++;
                                                        const generationId = currentGenerationIdRef.current;
                                                        
                                                        setGeneratingVoiceId(voice.id);
                                                        setIsGeneratingVoiceoverOnboarding(true);
                                                        (async () => {
                                                            try {
                                                                if (onboardingVoiceoverUrl) requestVoiceoverAutoplayRef.current = true;
                                                                // If script was modified, save the changes to database first
                                                                if (scriptModified) {
                                                                    console.log('Saving script changes before voiceover generation...');
                                                                    await api.updateWorkspaceScriptContent(
                                                                        currentWorkspace._id, 
                                                                        onboardingScript._id, 
                                                                        'main', 
                                                                        onboardingScript.content
                                                                    );
                                                                }
                                                                
                                                                const response = await api.studioGenerateVoiceover(onboardingScript._id, null, voice.id);
                                                                
                                                                // Only update state if this is still the latest request
                                                                if (generationId === currentGenerationIdRef.current) {
                                                                    setOnboardingVoiceoverUrl(response.data.voiceoverUrl);
                                                                    setCurrentVoiceoverVoiceId(voice.id);
                                                                    setCurrentVoiceoverVoiceType('default');
                                                                    trackEvent('onboarding_voiceover_generated', { 
                                                                        voiceType: 'default',
                                                                        voiceId: voice.id,
                                                                        voiceName: voice.name
                                                                    });
                                                                }
                                                            } catch (error) {
                                                                console.error('Error generating voiceover:', error);
                                                            } finally {
                                                                // Only update loading state if this is still the latest request
                                                                if (generationId === currentGenerationIdRef.current) {
                                                                    setIsGeneratingVoiceoverOnboarding(false);
                                                                    setGeneratingVoiceId(null);
                                                                    setScriptModified(false); // Reset the modified flag after generation
                                                                    setIsEditingScript(false); // Return to highlighted view
                                                                }
                                                            }
                                                        })();
                                                    }
                                                }}
                                                disabled={false} // Allow overriding ongoing generation
                                                style={{
                                                    padding: '6px 12px',
                                                    backgroundColor: generatingVoiceId === voice.id ? '#ccc' : 'transparent',
                                                    color: generatingVoiceId === voice.id 
                                                        ? (darkMode ? '#333' : '#666')  // Dark text for generating state
                                                        : (darkMode ? '#e2e8f0' : selectedDefaultVoice === voice.id ? 'white' : '#333'), // Normal text color
                                                    border: '1px solid #ccc',
                                                    borderRadius: '8px',
                                                    cursor: generatingVoiceId ? 'not-allowed' : 'pointer',
                                                    fontSize: '12px',
                                                    fontWeight: 'bold',
                                                    marginTop: '10px',
                                                    width: '100%',
                                                    opacity: generatingVoiceId ? (generatingVoiceId === voice.id ? 0.6 : 0.4) : 1
                                                }}
                                            >
                                                {generatingVoiceId === voice.id ? <div><span className="spinner-border" /> Generating... </div> : 
                                                 (onboardingVoiceoverUrl && selectedDefaultVoice === voice.id) ? 'Try new voice tone' : 
                                                 'Select & Generate Voiceover'}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {useClonedVoice && (
                                <div style={{
                                    padding: '20px',
                                    backgroundColor: darkMode ? '#2d3748' : '#f8f9fa',
                                    borderRadius: '8px'
                                }}>
                                    {!onboardingVoiceId && (
                                        <>
                                            <h4 style={{ margin: '0 0 15px 0', color: darkMode ? '#fff' : '#333', textAlign: 'center' }}>
                                                Record Your Voice
                                            </h4>
                                            <p style={{ 
                                                color: darkMode ? '#ccc' : '#666', 
                                                marginBottom: '30px', 
                                                textAlign: 'center',
                                                fontSize: '14px',
                                                lineHeight: '1.5'
                                            }}>
                                                Record a sample of your voice or import an audio file. We'll automatically create your AI voice.
                                            </p>

                                            <div style={{ display: 'flex', gap: '10px', marginBottom: '30px' }}>
                                                <button
                                                    onClick={isRecordingOnboarding ? stopRecording : startRecording}
                                                    disabled={onboardingRecordings.length > 0 && !isRecordingOnboarding}
                                                    style={{
                                                        flex: 1,
                                                        padding: '12px',
                                                        backgroundColor: onboardingRecordings.length > 0 ? '#ccc' : (isRecordingOnboarding ? '#dc3545' : '#0066cc'),
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        cursor: onboardingRecordings.length > 0 ? 'not-allowed' : 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '8px',
                                                        fontWeight: 'bold',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    {isRecordingOnboarding ? (
                                                        <>
                                                            {isSavingRecordingOnboarding ? <RefreshCw className="spin" size={16} /> : <Square size={16} />}
                                                            {isSavingRecordingOnboarding ? 'Saving...' : `Stop Recording (${formatTime(recordingDurationOnboarding)})`}
                                                        </>
                                                    ) : (
                                                        <>
                                                            {onboardingRecordings.length > 0 ? <Check size={16} /> : <Mic size={16} />}
                                                            {onboardingRecordings.length > 0 ? 'Voice Recorded' : 'Start Recording'}
                                                        </>
                                                    )}
                                                </button>
                                                
                                                <input
                                                    ref={audioImportRef}
                                                    type="file"
                                                    accept="audio/*,video/*"
                                                    onChange={handleAudioImport}
                                                    style={{ display: 'none' }}
                                                />
                                                <button
                                                    onClick={() => audioImportRef.current?.click()}
                                                    disabled={onboardingRecordings.length > 0 || isImportingAudio}
                                                    style={{
                                                        padding: '12px',
                                                        backgroundColor: (onboardingRecordings.length > 0 || isImportingAudio) ? '#ccc' : '#e1f679',
                                                        color: (onboardingRecordings.length > 0 || isImportingAudio) ? 'white' : 'black',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        cursor: (onboardingRecordings.length > 0 || isImportingAudio) ? 'not-allowed' : 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        minWidth: '120px',
                                                        fontWeight: 'bold',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    {isImportingAudio ? (
                                                        <>
                                                            <RefreshCw className="spin" size={16} />
                                                            Importing...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Upload size={16} />
                                                            Import voice file (audio or video)
                                                        </>
                                                    )}
                                                </button>
                                            </div>

                                            {onboardingRecordings.length > 0 && (
                                                <div style={{ marginBottom: '30px' }}>
                                                    <h4 style={{ 
                                                        margin: '0 0 15px 0', 
                                                        color: darkMode ? '#fff' : '#333', 
                                                        fontSize: '16px',
                                                        textAlign: 'center'
                                                    }}>
                                                        Your Recording
                                                    </h4>
                                                    {onboardingRecordings.map((recording, index) => (
                                                        <div
                                                            key={recording._id || index}
                                                            style={{
                                                                padding: '12px',
                                                                backgroundColor: darkMode ? '#2d3748' : '#f8f9fa',
                                                                borderRadius: '8px',
                                                                marginBottom: '10px',
                                                                border: `1px solid ${darkMode ? '#444' : '#e9ecef'}`
                                                            }}
                                                        >
                                                            <div style={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                justifyContent: 'space-between',
                                                                marginBottom: '8px'
                                                            }}>
                                                                <span style={{ 
                                                                    color: darkMode ? '#ccc' : '#666',
                                                                    fontSize: '14px',
                                                                    fontWeight: '500'
                                                                }}>
                                                                    {recording.filename || `Recording ${index + 1}`}
                                                                    {recording.duration && (
                                                                        <span style={{ 
                                                                            color: darkMode ? '#888' : '#999',
                                                                            marginLeft: '8px',
                                                                            fontSize: '12px'
                                                                        }}>
                                                                            ({formatTime(recording.duration)})
                                                                        </span>
                                                                    )}
                                                                </span>
                                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                                    <button
                                                                        onClick={() => handlePlayRecording(recording)}
                                                                        style={{
                                                                            padding: '6px 12px',
                                                                            backgroundColor: playingRecordingId === recording._id ? '#dc3545' : '#0066cc',
                                                                            color: 'white',
                                                                            border: 'none',
                                                                            borderRadius: '6px',
                                                                            cursor: 'pointer',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: '6px',
                                                                            fontSize: '12px',
                                                                            fontWeight: 'bold'
                                                                        }}
                                                                    >
                                                                        {playingRecordingId === recording._id ? (
                                                                            <>
                                                                                <Pause size={14} />
                                                                                Pause
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <Play size={14} />
                                                                                Play
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDeleteRecording(recording._id)}
                                                                        style={{
                                                                            padding: '6px 12px',
                                                                            backgroundColor: '#dc3545',
                                                                            color: 'white',
                                                                            border: 'none',
                                                                            borderRadius: '6px',
                                                                            cursor: 'pointer',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: '6px',
                                                                            fontSize: '12px',
                                                                            fontWeight: 'bold'
                                                                        }}
                                                                    >
                                                                        <Trash2 size={14} />
                                                                        Delete
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {isGeneratingVoiceOnboarding && (
                                                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                                                    <div style={{
                                                        width: '40px',
                                                        height: '40px',
                                                        background: '#4ecdc4',
                                                        borderRadius: '50%',
                                                        animation: 'pulse 2s infinite',
                                                        margin: '0 auto 15px'
                                                    }} />
                                                    <h4 style={{ 
                                                        margin: '0 0 10px 0', 
                                                        color: darkMode ? '#fff' : '#333',
                                                        fontSize: '18px'
                                                    }}>
                                                        Creating Your AI Voice...
                                                    </h4>
                                                    <p style={{ 
                                                        color: darkMode ? '#ccc' : '#666', 
                                                        marginBottom: '0',
                                                        fontSize: '14px'
                                                    }}>
                                                        This may take a few moments
                                                    </p>
                                                </div>
                                            )}

                                            {voiceCloningError && !isGeneratingVoiceOnboarding && (
                                                <div style={{ 
                                                    marginBottom: '20px', 
                                                    textAlign: 'center',
                                                    backgroundColor: darkMode ? '#2d2419' : '#fff3cd',
                                                    border: `1px solid ${darkMode ? '#4a3a1a' : '#ffeaa7'}`,
                                                    borderRadius: '8px',
                                                    padding: '12px',
                                                    color: darkMode ? '#d4a853' : '#856404'
                                                }}>
                                                    {voiceCloningError !== VOICE_CLONE_LIMIT_MESSAGE && (
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
                                                            <AlertCircle size={16} />
                                                            <strong>Voice cloning failed</strong>
                                                        </div>
                                                    )}
                                                    <p style={{ fontSize: '14px', marginBottom: '12px' }}>
                                                        {voiceCloningError}
                                                    </p>
                                                    {voiceCloneUpgradeActions}
                                                    {voiceCloningError !== VOICE_CLONE_LIMIT_MESSAGE && (
                                                    <button
                                                        onClick={handleVoiceCloning}
                                                        style={{
                                                            padding: '8px 16px',
                                                            backgroundColor: '#e1f679',
                                                            color: 'black',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer',
                                                            fontSize: '14px',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        Try Again
                                                    </button>
                                                    )}
                                                </div>
                                            )}

                                            {onboardingRecordings.length > 0 && !onboardingVoiceId && !isGeneratingVoiceOnboarding && !voiceCloningError && (
                                                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                                                    <button
                                                        onClick={handleVoiceCloning}
                                                        style={{
                                                            padding: '12px 24px',
                                                            backgroundColor: '#0066cc',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            cursor: 'pointer',
                                                            fontSize: '16px',
                                                            fontWeight: 'bold',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '8px',
                                                            margin: '0 auto'
                                                        }}
                                                    >
                                                        Create AI Voice
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {onboardingVoiceId && !isGeneratingVoiceOnboarding && (
                                        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                                            <div style={{
                                                width: '40px',
                                                height: '40px',
                                                background: '#28a745',
                                                borderRadius: '50%',
                                                margin: '0 auto 15px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <Check size={20} color="white" />
                                            </div>
                                            <h4 style={{ 
                                                margin: '0 0 10px 0', 
                                                color: darkMode ? '#fff' : '#333',
                                                fontSize: '18px'
                                            }}>
                                                Your AI Voice is Ready to use!
                                            </h4>
                                            <p style={{ 
                                                color: darkMode ? '#ccc' : '#666', 
                                                marginBottom: '0',
                                                fontSize: '14px'
                                            }}>
                                                You can now generate voiceovers with your cloned voice
                                            </p>
                                        </div>
                                    )}

                                    {onboardingVoiceId && (
                                        <div style={{ marginBottom: '20px' }}>
                                            <button
                                                onClick={async () => {
                                                    if (onboardingScript) {
                                                        // Increment generation ID to track this request
                                                        currentGenerationIdRef.current++;
                                                        const generationId = currentGenerationIdRef.current;
                                                        
                                                        setGeneratingVoiceId(onboardingVoiceId);
                                                        setIsGeneratingVoiceoverOnboarding(true);
                                                        try {
                                                            if (onboardingVoiceoverUrl) requestVoiceoverAutoplayRef.current = true;
                                                            // If script was modified, save the changes to database first
                                                            if (scriptModified) {
                                                                console.log('Saving script changes before voiceover generation...');
                                                                await api.updateWorkspaceScriptContent(
                                                                    currentWorkspace._id, 
                                                                    onboardingScript._id, 
                                                                    'main', 
                                                                    onboardingScript.content
                                                                );
                                                            }
                                                            
                                                            const response = await api.studioGenerateVoiceover(onboardingScript._id, null, onboardingVoiceId);
                                                            
                                                            // Only update state if this is still the latest request
                                                            if (generationId === currentGenerationIdRef.current) {
                                                                setOnboardingVoiceoverUrl(response.data.voiceoverUrl);
                                                                setCurrentVoiceoverVoiceId(onboardingVoiceId);
                                                                setCurrentVoiceoverVoiceType('cloned');
                                                                trackEvent('onboarding_voiceover_generated', { 
                                                                    voiceType: 'cloned',
                                                                    voiceId: onboardingVoiceId
                                                                });
                                                            }
                                                        } catch (error) {
                                                            console.error('Error generating voiceover:', error);
                                                        } finally {
                                                            // Only update loading state if this is still the latest request
                                                            if (generationId === currentGenerationIdRef.current) {
                                                                setIsGeneratingVoiceoverOnboarding(false);
                                                                setGeneratingVoiceId(null);
                                                                setScriptModified(false);
                                                                setIsEditingScript(false); // Return to highlighted view
                                                            }
                                                        }
                                                    }
                                                }}
                                                disabled={false} // Allow overriding ongoing generation
                                                style={{
                                                    width: '100%',
                                                    padding: '12px',
                                                    backgroundColor: generatingVoiceId === onboardingVoiceId ? '#ccc' : '#0066cc',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    fontSize: '16px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {generatingVoiceId === onboardingVoiceId ? (
                                                    <>
                                                        <RefreshCw className="spin" size={16} />
                                                        Generating Voiceover...
                                                    </>
                                                ) : (
                                                    currentVoiceoverVoiceType === 'cloned' && currentVoiceoverVoiceId === onboardingVoiceId ? 
                                                        'Try new voice tone' : 
                                                        'Generate Voiceover with My Voice'
                                                )}
                                            </button>
                                            {currentVoiceoverVoiceType === 'cloned' && currentVoiceoverVoiceId === onboardingVoiceId && (
                                                <p style={{
                                                    color: darkMode ? '#ccc' : '#666',
                                                    fontSize: '14px',
                                                    textAlign: 'center',
                                                    marginTop: '10px',
                                                    fontStyle: 'italic'
                                                }}>
                                                    Try a new voice tone to change pacing or emphasis
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            
                            <button
                                onClick={async () => {
                                    stopAllAudio();
                                    await handleNextWithAutoGeneration();
                                }}
                                disabled={!onboardingVoiceoverUrl && (!useClonedVoice && !selectedDefaultVoice)}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: (!onboardingVoiceoverUrl && (!useClonedVoice && !selectedDefaultVoice)) ? '#ccc' : '#e1f679',
                                    color: 'black',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: (!onboardingVoiceoverUrl && (!useClonedVoice && !selectedDefaultVoice)) ? 'not-allowed' : 'pointer',
                                    fontSize: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    fontWeight: 'bold'
                                }}
                            >
                                Next
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                );
            */

            /* Case 5 - Commented out to skip directly to step 7
            case 5:
                return (
                    <div style={{
                        padding: '40px 20px',
                        maxWidth: '600px',
                        margin: '0 auto',
                        position: 'relative'
                    }}>
                        {onPrevious && (
                            <button
                                onClick={onPrevious}
                                style={{
                                    position: 'absolute',
                                    top: '20px',
                                    left: '20px',
                                    padding: '8px 16px',
                                    backgroundColor: 'transparent',
                                    color: darkMode ? '#ccc' : '#666',
                                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    zIndex: 10
                                }}
                            >
                                <ChevronLeft size={14} />
                                Back
                            </button>
                        )}
                        
                        <h2
                            className={darkMode ? "gradient-text-dark" : "gradient-text"}
                            style={{
                                fontSize: screenWidth <= 600 ? '20px' : '24px',
                                marginBottom: '20px',
                                marginTop: '30px',
                                textAlign: 'center'
                        }}>
                            Upload your videos
                        </h2>
                        
                        <p style={{
                            fontSize: '16px',
                            lineHeight: '1.6',
                            marginBottom: '20px',
                            color: darkMode ? '#ccc' : '#666',
                            textAlign: 'center'
                        }}>
                            The AI will cut and find the key moments of these videos, and in the next step, you'll be able to filter the scenes to keep only the best ones. Upload at least one video to continue.
                            <br/>
                            <br/>
                            <strong>💡 Tip: Don't overthink it, it can be anything, lifestyle, activity, people, office, landscape, event, etc.</strong>
                            <br/>
                            <br/>
                            What is key will be the message, the videos are just filler content to keep the viewer engaged.
                        </p>
                        
                        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                            <a
                                href="/b-roll-ideas"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'inline-block',
                                    padding: '12px 24px',
                                    backgroundColor: darkMode ? '#333' : '#f0f0f0',
                                    color: darkMode ? '#fff' : '#333',
                                    textDecoration: 'none',
                                    borderRadius: '8px',
                                    border: `1px solid ${darkMode ? '#555' : '#ddd'}`,
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    transition: 'all 0.2s ease',
                                    cursor: 'pointer'
                                }}
                                onMouseOver={(e) => {
                                    e.target.style.backgroundColor = darkMode ? '#444' : '#e0e0e0';
                                }}
                                onMouseOut={(e) => {
                                    e.target.style.backgroundColor = darkMode ? '#333' : '#f0f0f0';
                                }}
                            >
                                Check some ideas →
                            </a>
                        </div>

                        <div
                            className={`upload-area ${isDragging ? 'dragging' : ''}`}
                            onDragOver={(e) => {
                                e.preventDefault();
                                setIsDragging(true);
                            }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setIsDragging(false);
                                handleFileUpload(e.dataTransfer.files);
                            }}
                            style={{
                                border: `2px dashed ${isDragging ? '#0066cc' : (darkMode ? '#444' : '#ddd')}`,
                                borderRadius: '12px',
                                padding: '40px 20px',
                                textAlign: 'center',
                                backgroundColor: isDragging ? (darkMode ? '#2A2B2F' : '#f8f9fa') : 'transparent',
                                transition: 'all 0.2s ease',
                                cursor: 'pointer',
                                marginBottom: '20px'
                            }}
                        >
                            <input
                                type="file"
                                multiple
                                accept="video/*"
                                onChange={(e) => handleFileUpload(e.target.files)}
                                style={{ display: 'none' }}
                                id="onboarding-file-input"
                            />
                            <label
                                htmlFor="onboarding-file-input"
                                style={{
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '12px'
                                }}
                            >
                                <Upload size={32} style={{ color: darkMode ? '#ccc' : '#666' }} />
                                <span style={{
                                    fontSize: '18px',
                                    color: darkMode ? '#fff' : '#333',
                                    fontWeight: '500'
                                }}>
                                    Drag & drop videos here or click to upload
                                </span>
                                <span style={{
                                    fontSize: '14px',
                                    color: darkMode ? '#999' : '#666'
                                }}>
                                    Up to 50 files at once, maximum size of 2GB each
                                </span>
                            </label>
                        </div>

                        {Object.keys(uploadProgress).length > 0 && (
                            <div style={{
                                marginBottom: '20px',
                                padding: '16px',
                                backgroundColor: darkMode ? '#2d3748' : '#f8f9fa',
                                borderRadius: '8px',
                                border: `1px solid ${darkMode ? '#4a5568' : '#e2e8f0'}`
                            }}>
                                {uploadStats.totalFiles > 0 && (
                                    <div style={{
                                        padding: '12px',
                                        marginBottom: '16px',
                                        backgroundColor: darkMode ? '#1a202c' : '#fff',
                                        borderRadius: '8px',
                                        border: `1px solid ${darkMode ? '#4a5568' : '#e2e8f0'}`,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        gap: '8px'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ fontWeight: 'bold', color: darkMode ? '#fff' : '#333' }}>
                                                Uploading: {uploadStats.currentFileIndex} of {uploadStats.totalFiles}
                                            </span>
                                            {uploadStats.currentFileName && (
                                                <span style={{ 
                                                    color: darkMode ? '#a0aec0' : '#666',
                                                    fontSize: '14px',
                                                    maxWidth: '200px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {uploadStats.currentFileName}
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', gap: '16px', fontSize: '14px' }}>
                                            <span style={{ color: '#4CAF50' }}>
                                                ✓ {uploadStats.completedFiles} completed
                                            </span>
                                            {uploadStats.failedFiles > 0 && (
                                                <span style={{ color: '#f44336' }}>
                                                    ✗ {uploadStats.failedFiles} failed
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}
                                
                                {Object.entries(uploadProgress).map(([fileName, progress]) => (
                                    <div key={`progress-${fileName}`} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        marginBottom: '8px',
                                        padding: '8px',
                                        backgroundColor: darkMode ? '#1a202c' : '#fff',
                                        borderRadius: '6px',
                                        border: `1px solid ${darkMode ? '#4a5568' : '#e2e8f0'}`
                                    }}>
                                        <span style={{
                                            minWidth: '120px',
                                            fontSize: '14px',
                                            color: darkMode ? '#e2e8f0' : '#333',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {fileName}
                                        </span>
                                        <div style={{
                                            flex: 1,
                                            height: '8px',
                                            backgroundColor: darkMode ? '#4a5568' : '#e2e8f0',
                                            borderRadius: '4px',
                                            overflow: 'hidden'
                                        }}>
                                            <div 
                                                style={{
                                                    width: `${progress}%`,
                                                    height: '100%',
                                                    background: progress < 100 
                                                        ? 'linear-gradient(90deg, #4ecdc4 0%, #44a08d 100%)'
                                                        : '#4CAF50',
                                                    borderRadius: '4px',
                                                    transition: 'width 0.3s ease',
                                                    animation: progress < 100 ? 'pulse 2s infinite' : 'none'
                                                }}
                                            />
                                        </div>
                                        <span style={{
                                            minWidth: '40px',
                                            textAlign: 'right',
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            color: darkMode ? '#e2e8f0' : '#333'
                                        }}>
                                            {progress}%
                                        </span>
                                        <button
                                            onClick={() => cancelUpload(fileName)}
                                            style={{
                                                width: '24px',
                                                height: '24px',
                                                backgroundColor: 'transparent',
                                                color: darkMode ? '#fff' : '#333',
                                                border: 'none',
                                                borderRadius: '50%',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '12px',
                                                flexShrink: 0
                                            }}
                                            title="Cancel upload"
                                        >
                                            <X size={12} />
                                        </button>
                                    </div>
                                    )
                                )}
                            </div>
                        )}

                        {onboardingVideos.length > 0 && (
                            <div style={{ marginBottom: '20px' }}>
                                <p style={{
                                    color: darkMode ? '#4CAF50' : '#28a745',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    marginBottom: '12px'
                                }}>
                                    {onboardingVideos.length} video{onboardingVideos.length !== 1 ? 's' : ''} successfully imported.
                                </p>
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                    marginBottom: '16px'
                                }}>
                                    {onboardingVideos.map((video, index) => (
                                        <div
                                            key={video._id || index}
                                            style={{
                                                width: '56px',
                                                height: '56px',
                                                borderRadius: '8px',
                                                overflow: 'hidden',
                                                flexShrink: 0,
                                                backgroundColor: darkMode ? '#2d3748' : '#e9ecef',
                                                boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                                            }}
                                            title={video.name || video.title || `Video ${index + 1}`}
                                        >
                                            <video
                                                src={video.url}
                                                muted
                                                playsInline
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    display: 'block'
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center',
                            flexWrap: 'wrap'
                        }}>
                            
                            <button
                                onClick={() => {
                                    trackEvent('onboarding_step_next', {
                                        fromStep: step,
                                        toStep: step + 1,
                                        stepName: getStepName(step),
                                        workspaceId: currentWorkspace?._id
                                    });
                                    onNext();
                                }}
                                disabled={onboardingVideos.length === 0}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: onboardingVideos.length > 0 ? '#e1f679' : '#ccc',
                                    color: onboardingVideos.length > 0 ? 'black' : 'white',
                                    fontWeight: 'bold',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: onboardingVideos.length > 0 ? 'pointer' : 'not-allowed',
                                    fontSize: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                Next
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                );
            */

            case 6:
                return (
                    <div style={{
                        padding: '40px 20px',
                        maxWidth: '600px',
                        margin: '0 auto',
                        position: 'relative'
                    }}>
                        {onPrevious && (
                            <button
                                onClick={onPrevious}
                                style={{
                                    position: 'absolute',
                                    top: '20px',
                                    left: '20px',
                                    padding: '8px 16px',
                                    backgroundColor: 'transparent',
                                    color: darkMode ? '#ccc' : '#666',
                                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    zIndex: 10
                                }}
                            >
                                <ChevronLeft size={14} />
                                Back
                            </button>
                        )}
                        
                        <h2 className={darkMode ? "gradient-text-dark" : "gradient-text"}
                        style={{
                            fontSize: screenWidth <= 600 ? '20px' : '24px',
                            marginBottom: '20px',
                            marginTop: '30px',
                            textAlign: 'center'
                        }}>
                            Filter cuts
                        </h2>
                        
                        <p style={{
                            fontSize: '16px',
                            lineHeight: '1.6',
                            marginBottom: '30px',
                            color: darkMode ? '#ccc' : '#666',
                            textAlign: 'center'
                        }}>
                            Review and filter the cuts generated from your videos. The AI will pick some of the cuts to generate the video. Hide any cuts you don't want to use in the generated video.
                        </p>

                        {onboardingCuts.length > 0 ? (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                                gap: '15px',
                                marginBottom: '30px'
                            }}>
                                {onboardingCuts.map((cut) => (
                                    <div
                                        key={cut._id}
                                        style={{
                                            backgroundColor: darkMode ? '#2d3748' : '#f8f9fa',
                                            borderRadius: '8px',
                                            padding: '15px',
                                            border: hiddenCuts.has(cut._id) ? `2px solid #dc3545` : `2px solid transparent`,
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        <video
                                            src={cut.url}
                                            style={{
                                                width: '100%',
                                                objectFit: 'contain',
                                                borderRadius: '4px',
                                                marginBottom: '10px',
                                                opacity: hiddenCuts.has(cut._id) ? '0.5' : '1',
                                                boxSizing: 'border-box',
                                                maxWidth: '600px',
                                                maxHeight: '300px',
                                                objectFit: 'contain'
                                            }}
                                            playsInline
                                            muted
                                            autoPlay
                                            loop
                                            onLoadedMetadata={(e) => {
                                                e.target.currentTime = e.target.duration / 2;
                                            }}
                                        />
                                            <button
                                                onClick={() => handleHideCut(cut._id)}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px',
                                                    backgroundColor: hiddenCuts.has(cut._id) ? '#28a745' : '#dc3545',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '14px'
                                                }}
                                            >
                                                {hiddenCuts.has(cut._id) ? 'Show' : 'Hide'}
                                            </button>
                                    </div>
                                ))}
                                
                                {cutsGenerationActive && (
                                    <div style={{
                                        backgroundColor: darkMode ? '#2d3748' : '#f8f9fa',
                                        borderRadius: '8px',
                                        padding: '15px',
                                        border: '2px dashed #ccc',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: '200px'
                                    }}>
                                        <div style={{
                                            width: '30px',
                                            height: '30px',
                                            background: '#4ecdc4',
                                            borderRadius: '50%',
                                            animation: 'pulse 2s infinite',
                                            marginBottom: '10px'
                                        }} />
                                        <p style={{
                                            color: darkMode ? '#ccc' : '#666',
                                            fontSize: '14px',
                                            textAlign: 'center',
                                            margin: 0
                                        }}>
                                            Generating cuts...
                                        </p>
                                    </div>
                                )}
                            </div>
                        ) : onboardingVideos.length > 0 ? (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px 20px',
                                color: darkMode ? '#ccc' : '#666'
                            }}>
                                <div style={{
                                    width: '50px',
                                    height: '50px',
                                    background: '#4ecdc4',
                                    borderRadius: '50%',
                                    animation: 'pulse 2s infinite',
                                    margin: '0 auto 20px'
                                }} />
                                <p style={{ marginBottom: '10px', fontSize: '16px' }}>
                                    {cutsGenerationActive ? 'Generating cuts from your videos...' : 'Loading cuts...'}
                                </p>
                                <p style={{ fontSize: '14px', opacity: 0.8 }}>
                                    This may take a few moments
                                </p>
                            </div>
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px 20px',
                                color: darkMode ? '#ccc' : '#666'
                            }}>
                                <p>No cuts available yet. Please upload videos first.</p>
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            
                            <button
                                onClick={() => {
                                    trackEvent('onboarding_step_next', {
                                        fromStep: step,
                                        toStep: step + 1,
                                        stepName: getStepName(step),
                                        cutsCount: onboardingCuts.length,
                                        workspaceId: currentWorkspace?._id
                                    });
                                    onNext();
                                }}
                                disabled={onboardingCuts.length === 0}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: onboardingCuts.length === 0 ? '#ccc' : '#e1f679',
                                    color: onboardingCuts.length === 0 ? 'white' : 'black',
                                    fontWeight: 'bold',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: onboardingCuts.length === 0 ? 'not-allowed' : 'pointer',
                                    fontSize: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                Next <span style={{ fontSize: '12px', color: 'gray' }}>(Generate Video)</span>
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                );

            case 7:
                return (
                    <div style={{
                        padding: '40px 20px',
                        maxWidth: '600px',
                        margin: '0 auto',
                        position: 'relative'
                    }}>
                        {onPrevious && (
                            <button
                                onClick={onPrevious}
                                style={{
                                    position: 'absolute',
                                    top: '20px',
                                    left: '20px',
                                    padding: '8px 16px',
                                    backgroundColor: 'transparent',
                                    color: darkMode ? '#ccc' : '#666',
                                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    zIndex: 10
                                }}
                            >
                                <ChevronLeft size={14} />
                                Back
                            </button>
                        )}
                        
                        <h2 className={darkMode ? "gradient-text-dark" : "gradient-text"}
                        style={{
                            fontSize: screenWidth <= 600 ? '20px' : '24px',
                            marginBottom: '20px',
                            marginTop: '30px',
                            textAlign: 'center'
                        }}>
                            Generate your video
                        </h2>
                        
                        <p style={{
                            fontSize: '16px',
                            lineHeight: '1.6',
                            marginBottom: '30px',
                            color: darkMode ? '#ccc' : '#666',
                            textAlign: 'center'
                        }}>
                            {isGeneratingVideoOnboarding ? 'Generating your video...' : 
                             onboardingVideoUrl ? 'Your video has been generated! 🥳 You can regenerate it or save it to complete the onboarding.' :
                             onboardingVideos.length > 0 && onboardingCuts.length === 0 ? 'Loading cuts from your videos... This may take a moment.' :
                             'Click the button below to generate your video.'}
                        </p>

                        {onboardingVideos.length > 0 && onboardingCuts.length === 0 && !isGeneratingVideoOnboarding && !onboardingVideoUrl && (
                            <div style={{
                                textAlign: 'center',
                                padding: '30px 20px',
                                marginBottom: '20px',
                                color: darkMode ? '#ccc' : '#666'
                            }}>
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    background: '#4ecdc4',
                                    borderRadius: '50%',
                                    animation: 'pulse 2s infinite',
                                    margin: '0 auto 15px'
                                }} />
                                <p style={{ fontSize: '14px', margin: 0 }}>Preparing your clips...</p>
                            </div>
                        )}

                        {onboardingVoiceoverUrl && onboardingCuts.length > 0 && (() => {
                            const totalCutsDuration = onboardingCuts.length;
                            
                            return voiceoverDuration > 0 && voiceoverDuration > totalCutsDuration && (
                                <div style={{
                                    backgroundColor: '#fff3cd',
                                    border: '1px solid #ffeaa7',
                                    borderRadius: '6px',
                                    padding: '12px',
                                    marginBottom: '20px',
                                    fontSize: '14px',
                                    color: '#856404',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '8px'
                                }}>
                                    <AlertCircle size={16} style={{flexShrink: 0, marginTop: '2px'}} /> 
                                    <div>
                                        Hey there, I'm your AI video-editor!
                                        <br /><br />
                                        I can generate the video but your <strong>voiceover is {Math.ceil(voiceoverDuration)} seconds long</strong> , and for now, we only have <strong>{Math.round(totalCutsDuration)} seconds of cuts</strong>. The video will reuse cuts to fill the duration. 
                                        <br /><br />
                                        That's enough to make a demo video, but later, let's <strong>upload more videos to get more cuts</strong> for better variety.
                                    </div>
                                </div>
                            );
                        })()}

                        {isGeneratingVideoOnboarding && (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px 20px',
                                marginBottom: '20px'
                            }}>
                                <div style={{
                                    width: '50px',
                                    height: '50px',
                                    background: '#4ecdc4',
                                    borderRadius: '50%',
                                    animation: 'pulse 2s infinite',
                                    margin: '0 auto 20px'
                                }} />
                                <p style={{ color: darkMode ? '#ccc' : '#666', marginBottom: '15px' }}>
                                    Creating your video...
                                </p>

                                <VideoGenerationSteps
                                    progress={onboardingVideoProgress}
                                    perStepProgress
                                    showProgressBar
                                    shimmer
                                />

                                {onboardingCuts.length > 0 && (
                                    <div style={{ marginTop: '30px' }}>
                                        <SmoothScaleCarousel items={onboardingCuts} />
                                    </div>
                                )}
                            </div>
                        )}

                        {onboardingVideoUrl && !isGeneratingVideoOnboarding && (
                            <div style={{
                                backgroundColor: darkMode ? '#2d3748' : '#f8f9fa',
                                borderRadius: '8px',
                                paddingBottom: '20px',
                                paddingTop: '10px',
                                marginBottom: '20px',
                                textAlign: 'center'
                            }}>
                                <h3 style={{
                                    fontSize: '18px',
                                    marginBottom: '15px',
                                    color: darkMode ? '#fff' : '#333'
                                }}>
                                    Your Video
                                </h3>
                                <video
                                    src={onboardingVideoUrl}
                                    controls
                                    style={{
                                        width: '100%',
                                        minHeight: '400px',
                                        maxWidth: '400px',
                                        width:'auto',
                                        maxHeight: '400px',
                                        objectFit: 'contain',
                                        borderRadius: '8px'
                                    }}
                                    playsInline
                                />

                                <div style={{ display: 'flex', marginTop: '10px', gap: '10px', justifyContent: 'center' }}>
                                    <button
                                        onClick={generateVideo}
                                        disabled={isGeneratingVideoOnboarding || !onboardingVoiceoverUrl}
                                        style={{
                                            padding: '6px 12px',
                                            backgroundColor: (isGeneratingVideoOnboarding || !onboardingVoiceoverUrl) ? '#ccc' : 'transparent',
                                            color: darkMode ? '#e2e8f0' : '#333',
                                            border: '1px solid #ccc',
                                            borderRadius: '8px',
                                            cursor: (isGeneratingVideoOnboarding || !onboardingVoiceoverUrl) ? 'not-allowed' : 'pointer',
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            opacity: (isGeneratingVideoOnboarding || !onboardingVoiceoverUrl) ? 0.6 : 1
                                        }}
                                    >
                                        {isGeneratingVideoOnboarding ? (
                                            <>
                                                <div style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    background: '#4ecdc4',
                                                    borderRadius: '50%',
                                                    animation: 'pulse 2s infinite'
                                                }} />
                                                Generating...
                                            </>
                                        ) : (
                                            <>
                                                <RefreshCw size={16} />
                                                {onboardingVideoUrl ? 'Generate New Version' : 'Generate Video'}
                                            </>
                                        )}
                                    </button>
                                    
                                     <button
                                         onClick={saveVideo}
                                         disabled={!onboardingVideoUrl || isDownloadingVideoOnboarding || isSavingVideoOnboarding}
                                         style={{
                                             padding: '12px 24px',
                                             backgroundColor: (!onboardingVideoUrl || isDownloadingVideoOnboarding || isSavingVideoOnboarding) ? '#ccc' : '#AB5898',
                                             color: 'white',
                                             border: 'none',
                                             borderRadius: '8px',
                                             cursor: (!onboardingVideoUrl || isDownloadingVideoOnboarding || isSavingVideoOnboarding) ? 'not-allowed' : 'pointer',
                                             display: 'flex',
                                             alignItems: 'center',
                                             fontWeight: 'bold',
                                             gap: '8px'
                                         }}
                                     >
                                         {(isDownloadingVideoOnboarding || isSavingVideoOnboarding) && (
                                             <span className="spinner-border" style={{marginRight:'5px'}} />
                                         )}
                                         {isDownloadingVideoOnboarding ? 'Downloading...' : 
                                          isSavingVideoOnboarding ? 'Saving...' : 
                                          'Download Video'}
                                     </button>
                                </div>
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>

                            <button
                                onClick={() => {
                                    trackEvent('onboarding_step_next', {
                                        fromStep: step,
                                        toStep: step + 1,
                                        stepName: getStepName(step),
                                        hasVideo: !!onboardingVideoUrl,
                                        workspaceId: currentWorkspace?._id
                                    });
                                    onNext();
                                }}
                                disabled={!onboardingVideoUrl}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: onboardingVideoUrl ? '#e1f679' : '#ccc',
                                    color: onboardingVideoUrl ? 'black' : 'white',
                                    fontWeight: 'bold',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: onboardingVideoUrl ? 'pointer' : 'not-allowed',
                                    fontSize: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                Next <span style={{ fontSize: '12px', color: 'gray' }}>(Complete Onboarding)</span>
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                );

            case 8:
                return (
                    <div style={{
                        padding: '40px 20px',
                        maxWidth: '600px',
                        margin: '0 auto',
                        textAlign: 'center',
                        position: 'relative'
                    }}>
                        {onPrevious && (
                            <button
                                onClick={onPrevious}
                                style={{
                                    position: 'absolute',
                                    top: '20px',
                                    left: '20px',
                                    padding: '8px 16px',
                                    backgroundColor: 'transparent',
                                    color: darkMode ? '#ccc' : '#666',
                                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    zIndex: 10
                                }}
                            >
                                <ChevronLeft size={14} />
                                Back
                            </button>
                        )}
                        
                        <div style={{
                            fontSize: '64px',
                            marginBottom: '20px'
                        }}>
                            🎉
                        </div>
                        
                        <h2 className={darkMode ? "gradient-text-dark" : "gradient-text"}
                        style={{
                            fontSize: screenWidth <= 600 ? '20px' : '24px',
                            marginBottom: '20px',
                            textAlign: 'center'
                        }}>
                        </h2>
                        
                        <p style={{
                            fontSize: '18px',
                            lineHeight: '1.6',
                            marginBottom: '30px',
                            color: darkMode ? '#ccc' : '#666',
                            textAlign: 'center'
                        }}>
                            And that's how you create videos super fast!
                        </p>

                        <div style={{
                            backgroundColor: darkMode ? '#2d3748' : '#f8f9fa',
                            borderRadius: '12px',
                            padding: '30px',
                            marginBottom: '30px',
                            textAlign: 'left'
                        }}>
                            <h3 style={{
                                fontSize: '20px',
                                marginBottom: '15px',
                                color: darkMode ? '#fff' : '#333',
                                textAlign: 'center'
                            }}>
                                What's next?
                            </h3>
                            
                            <div style={{
                                display: 'grid',
                                gap: '15px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px'
                                }}>
                                    <div style={{
                                        width: '24px',
                                        height: '24px',
                                        backgroundColor: '#e1f679',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'black',
                                        fontSize: '14px',
                                        fontWeight: 'bold'
                                    }}>
                                        1
                                    </div>
                                    <span style={{ color: darkMode ? '#e2e8f0' : '#333' }}>
                                        Upload more videos to expand your content library
                                    </span>
                                </div>
                                
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px'
                                }}>
                                    <div style={{
                                        width: '24px',
                                        height: '24px',
                                        backgroundColor: '#e1f679',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'black',
                                        fontSize: '14px',
                                        fontWeight: 'bold'
                                    }}>
                                        2
                                    </div>
                                    <span style={{ color: darkMode ? '#e2e8f0' : '#333' }}>
                                        Create more content
                                    </span>
                                </div>

                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px'
                                }}>
                                    <div style={{
                                        width: '24px',
                                        height: '24px',
                                        backgroundColor: '#e1f679',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'black',
                                        fontSize: '14px',
                                        fontWeight: 'bold'
                                    }}>
                                        3
                                    </div>
                                    <span style={{ color: darkMode ? '#e2e8f0' : '#333' }}>
                                        Add collaborators to your workspace
                                    </span>
                                </div>

                                <h2 className={darkMode ? "gradient-text-dark" : "gradient-text"}
                                style={{
                                    fontSize: screenWidth <= 600 ? '20px' : '24px',
                                    marginBottom: '20px',
                                    textAlign: 'center'
                                }}>
                                    Happy creating!
                                </h2>
                            </div>
                        </div>

                        <button
                            onClick={async () => {
                                try {
                                    // If there's a generated video that hasn't been saved, save it now
                                    if (onboardingVideoUrl && onboardingScript) {
                                        console.log('Saving generated video before completing onboarding...');
                                        await api.publishWorkspaceVideo(currentWorkspace._id, onboardingScript._id, {
                                            videoUrl: onboardingVideoUrl
                                        });
                                    }
                                } catch (error) {
                                    console.error('Error saving generated video:', error);
                                    // Continue even if save fails
                                }
                                
                                trackEvent('onboarding_completed', {
                                    hasScript: !!onboardingScript,
                                    hasVoiceover: !!onboardingVoiceoverUrl,
                                    hasVideo: !!onboardingVideoUrl,
                                    hasClonedVoice: !!onboardingVoiceId,
                                    videosCount: onboardingVideos.length,
                                    cutsCount: onboardingCuts.length,
                                    workspaceId: currentWorkspace?._id
                                });
                                
                                // Wait a bit for confetti animation, then complete
                                setTimeout(() => {
                                    onComplete();
                                }, 1500);
                            }}
                            style={{
                                padding: '16px 32px',
                                backgroundColor: '#e1f679',
                                color: 'black',
                                border: 'none',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                fontSize: '18px',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                margin: '0 auto',
                                boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                                // Apply hover effect to the button and update CheckCircle background
                                e.currentTarget.style.backgroundColor = '#c4e55c';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                // Find and update the CheckCircle icon background
                                const checkCircle = e.currentTarget.querySelector('svg');
                                if (checkCircle) {
                                    checkCircle.style.backgroundColor = '#c4e55c';
                                    checkCircle.style.transition = 'background-color 0.2s ease';
                                }
                            }}
                            onMouseOut={(e) => {
                                // Reset hover effect for the button and CheckCircle
                                e.currentTarget.style.backgroundColor = '#e1f679';
                                e.currentTarget.style.transform = 'translateY(0)';
                                // Reset CheckCircle icon background
                                const checkCircle = e.currentTarget.querySelector('svg');
                                if (checkCircle) {
                                    checkCircle.style.backgroundColor = '#e1f679';
                                }
                            }}
                        >
                            <CheckCircle size={20} />
                            Go to Workspace
                        </button>
                    </div>
                );
            default:
        return null;
        }
    };

    // Trigger confetti when reaching the final step
    useEffect(() => {
        if (step === 8) {
            setShowConfetti(true);
        }
    }, [step]);

    // Animate mouse cursors randomly during script generation
    useEffect(() => {
        if (!isGeneratingScriptOnboarding) return;

        const intervalIds = [];
        const timeoutIds = [];

        // Create individual intervals for each mouse with different timings
        mousePositions.forEach((mouse, index) => {
            // Each mouse has a different initial delay: 0s, 0.5s, 1s, 1.5s
            const initialDelay = index * 500;
            // Each mouse updates at different intervals: 2-4s, 2.5-4.5s, 3-5s, 3.5-5.5s
            const baseInterval = 2000 + (index * 500);
            
            const timeoutId = setTimeout(() => {
                // First movement happens immediately after delay
                setMousePositions(prev => {
                    const newPositions = [...prev];
                    const newTargetX = Math.random() * 300 + 50;
                    const newTargetY = Math.random() * 200 + 50;
                    newPositions[index] = {
                        ...newPositions[index],
                        targetX: newTargetX,
                        targetY: newTargetY
                    };
                    return newPositions;
                });

                // Then set up recurring interval for this mouse
                const intervalId = setInterval(() => {
                    setMousePositions(prev => {
                        const newPositions = [...prev];
                        const newTargetX = Math.random() * 300 + 50;
                        const newTargetY = Math.random() * 200 + 50;
                        newPositions[index] = {
                            ...newPositions[index],
                            targetX: newTargetX,
                            targetY: newTargetY
                        };
                        return newPositions;
                    });
                }, baseInterval + Math.random() * 2000);
                
                intervalIds.push(intervalId);
            }, initialDelay);
            
            timeoutIds.push(timeoutId);
        });

        // Smooth animation of mouse positions
        const animate = () => {
            setMousePositions(prev => prev.map(mouse => {
                const dx = mouse.targetX - mouse.x;
                const dy = mouse.targetY - mouse.y;
                const speed = 0.05;
                
                return {
                    ...mouse,
                    x: mouse.x + dx * speed,
                    y: mouse.y + dy * speed
                };
            }));
        };

        const animationId = setInterval(animate, 16); // ~60fps

        return () => {
            timeoutIds.forEach(id => clearTimeout(id));
            intervalIds.forEach(id => clearInterval(id));
            clearInterval(animationId);
        };
    }, [isGeneratingScriptOnboarding]);

    // Confetti animation effect
    useEffect(() => {
        if (!showConfetti || !confettiCanvasRef.current) return;

        const canvas = confettiCanvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        const confetti = [];
        const colors = ['#e1f679', '#0066cc', '#28a745', '#ffc107', '#dc3545', '#8b5cf6'];
        const confettiCount = 150;

        // Create confetti particles
        for (let i = 0; i < confettiCount; i++) {
            confetti.push({
                x: Math.random() * canvas.width,
                y: -10,
                width: Math.random() * 10 + 5,
                height: Math.random() * 10 + 5,
                speed: Math.random() * 2 + 5, // Much faster: was 2-5, now 6-14
                rotation: Math.random() * 360,
                rotationSpeed: Math.random() * 10 - 20, // Faster rotation: was 5-10, now 10-20
                color: colors[Math.floor(Math.random() * colors.length)],
                opacity: Math.random() * 0.5 + 0.5
            });
        }

        let animationId;
        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            confetti.forEach((particle, index) => {
                ctx.save();
                ctx.globalAlpha = particle.opacity;
                ctx.fillStyle = particle.color;
                ctx.translate(particle.x, particle.y);
                ctx.rotate((particle.rotation * Math.PI) / 180);
                ctx.fillRect(-particle.width / 2, -particle.height / 2, particle.width, particle.height);
                ctx.restore();

                particle.y += particle.speed;
                particle.x += Math.sin(particle.y * 0.01) * 4; // Faster horizontal movement: was 2, now 4
                particle.rotation += particle.rotationSpeed;

                // Remove particles that are off screen
                if (particle.y > canvas.height + 20) {
                    confetti.splice(index, 1);
                }
            });

            if (confetti.length > 0) {
                animationId = requestAnimationFrame(animate);
            } else {
                setShowConfetti(false);
            }
        };

        animate();

        return () => {
            if (animationId) {
                cancelAnimationFrame(animationId);
            }
        };
    }, [showConfetti]);

    return (
        <div style={{
            backgroundColor: darkMode ? '#1a202c' : '#fff',
            borderRadius: '12px',
            boxShadow: darkMode ? '0 4px 20px rgba(0,0,0,0.0)' : '0 4px 20px rgba(0,0,0,0.1)',
            border: darkMode ? '1px solid #4a5568' : '1px solid #ddd',
            margin: window.innerWidth <= 600 ? '0px' : '20px',
            minHeight: '500px',
            position: 'relative'
        }}>
            {showConfetti && (
                <canvas
                    ref={confettiCanvasRef}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        zIndex: 9999
                    }}
                />
            )}
            <button 
                onClick={toggleDarkMode}
                style={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '20px',
                    padding: '8px',
                    backgroundColor: darkMode ? '#2d3748' : '#f8f9fa',
                    color: darkMode ? '#fff' : '#333',
                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'all 0.2s ease',
                    zIndex: 10
                }}
                onMouseOver={(e) => {
                    // Only apply hover effect to the button itself, not child elements
                    if (e.target === e.currentTarget) {
                        e.target.style.backgroundColor = darkMode ? '#4a5568' : '#e9ecef';
                    }
                }}
                onMouseOut={(e) => {
                    // Only apply hover effect to the button itself, not child elements
                    if (e.target === e.currentTarget) {
                        e.target.style.backgroundColor = darkMode ? '#2d3748' : '#f8f9fa';
                    }
                }}
                title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
                    {darkMode ? <Sun size={16} /> : <Moon size={16} />}
                    {darkMode ? "Light" : "Dark"}
            </button>

            <div style={{
                padding: '20px 20px 0 20px'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px'
                }}>
                    <span style={{
                        fontSize: '14px',
                        color: darkMode ? '#ccc' : '#666'
                    }}>
                        {(() => {
                            // Map actual steps (0, 1, 2, 3, 7, 8) to display steps (1, 2, 3, 4, 5, 6 of 6)
                            const stepMap = { 0: 1, 1: 2, 2: 3, 3: 4, 6:5, 7: 6, 8: 7  };
                            const displayStep = stepMap[step] || step;
                            const totalSteps = 7;
                            return `Step ${displayStep} of ${totalSteps}`;
                        })()}
                    </span>
                    <span style={{
                        fontSize: '14px',
                        color: darkMode ? '#ccc' : '#666'
                    }}>
                        {(() => {
                            // Map actual steps (0, 1, 2, 3, 7, 8) to progress (1-6)
                            const stepMap = { 0: 1, 1: 2, 2: 3, 3: 4, 6:5, 7: 6, 8: 7 };
                            const progressStep = stepMap[step] || step;
                            const totalSteps = 7;
                            return `${Math.round((progressStep / totalSteps) * 100)}%`;
                        })()}
                    </span>
                </div>
                <div style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: darkMode ? '#2d3748' : '#e2e8f0',
                    borderRadius: '6px',
                    overflow: 'visible',
                    padding: '2px'
                }}>
                    <div style={{
                        width: `${(() => {
                            // Map actual steps (0, 1, 2, 3, 7, 8) to progress (1-6)
                            const stepMap = { 0: 1, 1: 2, 2: 3, 3: 4, 6:5, 7: 6, 8: 7 };
                            const progressStep = stepMap[step] || step;
                            const totalSteps = 7;
                            return (progressStep / totalSteps) * 100;
                        })()}%`,
                        height: '100%',
                        background: 'linear-gradient(90deg, #AB5898 0%, #5C4BD3 100%)',
                        borderRadius: '4px',
                        transition: 'width 0.3s ease',
                        outline: '1px solid #393271',
                        outlineOffset: '-1px',
                        boxShadow: '0 0 8px rgba(171, 88, 152, 0.5), 0 0 16px rgba(92, 75, 211, 0.5)'
                    }} />
                </div>
            </div>

            <div style={{
                padding: '20px'
            }}>
                {renderStep()}
            </div>
        </div>
    );
};

export default OnboardingFlow;